<template>
  <v-card flat class="board-pp" ref="fullscreenRoot">
    <!-- ===================== TOOLBAR ===================== -->
    <div class="toolbar">
      <!-- ===================== FILTROS (en la misma fila de la toolbar, label dentro del input) ===================== -->
      <div v-if="!esFullscreen" class="filtros">
        <!-- Un solo selector de semana o los dos pickers, según la configuración. La semana se
             calcula con el mismo criterio que el rango por defecto (horarios de la empresa). -->
        <div v-if="filtroPorSemana" class="f-input f-sel f-sel--semana">
          <span class="f-sel-lbl">{{ $t('Reports.ProductionPlan.filters.week') }}:</span>
          <v-select v-model="semanaSeleccionada" :items="opcionesSemana" item-text="text" item-value="value" dense
            outlined hide-details :disabled="cargando" class="f-sel-input">
            <!-- Al llegar al final de la lista se cargan más semanas hacia atrás. -->
            <template #append-item>
              <div v-intersect="cargarMasSemanas" class="pp-semanas-mas" />
            </template>
          </v-select>
        </div>
        <template v-else>
          <VueCtkDateTimePicker v-model="filtros.inicio" :disabled="cargando" :max-date="filtros.fin || undefined"
            format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
            :locale="currentLocale" :label="$t('Reports.ProductionPlan.filters.start')"
            :button-now-translation="$t('Reports.ProductionPlan.chart.now')" class="f-input" />
          <VueCtkDateTimePicker v-model="filtros.fin" :disabled="cargando" :min-date="filtros.inicio || undefined"
            format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
            :locale="currentLocale" :label="$t('Reports.ProductionPlan.filters.end')"
            :button-now-translation="$t('Reports.ProductionPlan.chart.now')" class="f-input" />
        </template>
        <v-autocomplete v-model="filtros.idsArea" :items="catAreas" multiple chips small-chips deletable-chips clearable
          dense outlined hide-details :disabled="cargando" :label="$t('Reports.ProductionPlan.filters.areas')"
          class="f-input f-input--areas" />
        <div class="f-input f-sel">
          <span class="f-sel-lbl">{{ $t('Reports.ProductionPlan.filters.view') }}:</span>
          <v-select v-model="vista" :items="vistaOpciones" item-text="text" item-value="value" dense outlined
            hide-details :disabled="cargando" class="f-sel-input" />
        </div>
        <v-autocomplete v-if="gamemode" v-model="filtros.idsInstancia" :items="catInstancias" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.instances')" class="f-input f-input--areas" />
        <v-autocomplete v-if="gamemode" v-model="filtros.idsTrabajoOrden" :items="catOrdenes" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.workOrders')" class="f-input f-input--areas" />
        <v-autocomplete v-if="gamemode" v-model="filtros.idsArticulo" :items="catArticulos" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.articles')" class="f-input f-input--areas" />
        <v-autocomplete v-if="gamemode" v-model="filtros.idsMolde" :items="catMoldes" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.molds')" class="f-input f-input--areas" />
        <v-btn small color="primary" class="f-aplicar" :loading="cargando" @click="cargar">
          <v-icon left small>mdi-magnify</v-icon> {{ $t('Reports.ProductionPlan.filters.search') }}
        </v-btn>
      </div>

      <!-- Acciones a la derecha: timer de recarga, config (gamemode) y pantalla completa. -->
      <div class="tb-actions">
        <span v-if="!esFullscreen" class="auto-refresh" :class="{ 'is-loading': cargando }"
          :title="$t('Reports.ProductionPlan.toolbar.autoRefreshTitle')">
          <v-icon small :class="{ 'spin': cargando }">mdi-autorenew</v-icon>
          {{ restanteTexto }}
        </span>
        <v-btn v-if="gamemode" small outlined color="orange darken-2" class="pp-fs-btn" @click="abrirConfig"
          :title="$t('Reports.ProductionPlan.settings.button')">
          <v-icon left small>mdi-cog-outline</v-icon>
          {{ $t('Reports.ProductionPlan.settings.button') }}
        </v-btn>

        <v-btn small outlined color="primary" class="pp-fs-btn" @click="toggleFullscreen"
          :title="esFullscreen ? $t('Reports.ProductionPlan.toolbar.exitFullscreen') : $t('Reports.ProductionPlan.toolbar.fullscreen')">
          <v-icon left small>{{ esFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          {{ esFullscreen ? $t('Reports.ProductionPlan.toolbar.exit') : $t('Reports.ProductionPlan.toolbar.fullscreen') }}
        </v-btn>

        <!-- Solo aparece si hay barras más allá del fin de la jornada: activarlo estira el eje
             hasta la última barra en vez de cortar en el horario de la empresa. -->
        <v-checkbox v-if="mostrarCheckTodo" v-model="mostrarTodo" dense hide-details class="pp-todo-check"
          label="Mostrar todo"
          title="Hay órdenes que se pasan del fin de la jornada: actívalo para verlas completas" />
      </div>
    </div>

    <!-- ===================== TARJETAS RESUMEN: (Planta + Máquinas) | (Avance + Programadas), mismo renglón ===================== -->
    <div class="pp-toprow">
      <!-- Card 1: Planta + Máquinas activas -->
      <div class="pp-topcard pp-topcard--maq">
        <!-- Planta: total de máquinas activas de la planta + nombre de la instancia. -->
        <div class="pp-sec pp-sec--planta" @click="abrirActivas(null)"
          :title="$t('Reports.ProductionPlan.activeDialog.title')">
          <div class="pp-sec-title">{{ $t('Reports.ProductionPlan.kpi.plant') }}</div>
          <v-progress-circular v-if="maquinasActivasCargando" indeterminate color="#2d84da" :size="20" :width="3"
            class="pp-sec-loader" />
          <template v-else>
            <div class="pp-ma-val">{{ maquinasActivas.activas }}/{{ maquinasActivas.total }}</div>
            <div class="pp-ma-area pp-ma-area--planta">{{ nombrePlanta }}</div>
          </template>
        </div>

        <!-- Máquinas activas: una columna por nave/área (activas/esperadas). Clic en el título abre
             el detalle de todo el tablero; clic en una columna, solo el de esa área. -->
        <div class="pp-sec pp-sec--maq" @click="abrirActivas(null)"
          :title="$t('Reports.ProductionPlan.activeDialog.title')">
          <div class="pp-sec-title pp-sec-title--center">{{ $t('Reports.ProductionPlan.kpi.activeMachines') }}</div>
          <v-progress-circular v-if="maquinasActivasCargando" indeterminate color="#2d84da" :size="20" :width="3"
            class="pp-sec-loader" />
          <div v-else class="pp-ma-cols">
            <div v-for="a in resumenPorArea" :key="a.id" class="pp-ma-col" :title="a.nombre"
              @click.stop="abrirActivas(a)">
              <span class="pp-ma-val">{{ a.activas }}/{{ a.esperadas }}</span>
              <span class="pp-ma-area">{{ a.nombre }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 2: Avance de piezas + Programadas al momento -->
      <div class="pp-topcard pp-topcard--avance">
        <!-- Avance de piezas: piezas · barra · porcentaje en una línea. -->
        <div class="pp-sec pp-sec--avance">
          <div class="pp-sec-title pp-sec-title--center">{{ $t('Reports.ProductionPlan.summary.totalProgress') }}</div>
          <div class="pp-avance-row">
            <span class="rs-piezas">{{ fmtNum(avance.piezasOk) }} / {{ fmtNum(avance.piezasObjetivo) }}</span>
            <div class="rs-bar">
              <div class="rs-fill" :style="{ width: Math.min(100, porcentajeAvance) + '%', background: colorAvance }" />
            </div>
            <span class="rs-pct" :style="{ color: colorAvance }">{{ porcentajeAvance }}%</span>
          </div>
        </div>

        <!-- Programadas al momento: etiqueta normal, número en negritas y, a la derecha,
             el % de lo producido contra lo que el plan esperaba hasta ahora. -->
        <div class="pp-sec pp-sec--prog">
          <div class="pp-sec-title pp-sec-title--normal">{{ $t('Reports.ProductionPlan.summary.scheduledSoFar') }}</div>
          <div class="rs-now-row">
            <span class="rs-now-val">{{ fmtNum(avance.piezasOk) }} / {{ fmtNum(piezasProgramadasHastaAhora) }}</span>
            <span class="rs-now-pct" :style="{ color: colorAvanceHastaAhora }">{{ porcentajeHastaAhora }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== LEYENDA (fija al hacer scroll) ===================== -->
    <div class="pp-sticky-top" ref="stickyTop">
      <div v-if="mostrarLeyenda" class="legend">
        <span class="lg"><i class="i-plan" /> {{ $t('Reports.ProductionPlan.legend.plan') }}</span>
        <span v-for="(b, i) in bandasLeyenda" :key="i" class="lg">
          <i class="sw" :style="{ background: b.color }" /> {{ b.etiqueta || rangoBandaLeyenda(i) }}
        </span>
        <span class="lg"><i class="i-finalizada" /> {{ $t('Reports.ProductionPlan.legend.finished') }}</span>
        <!-- Nombre de instancia en rojo: paro no programado que el contador no reporta activa. -->
        <span class="lg"><i class="i-paro-np">Aa</i> {{ $t('Reports.ProductionPlan.legend.unplannedStop') }}</span>
        <!-- Nombre de instancia en amarillo: paro programado que el contador no reporta activa. -->
        <span class="lg"><i class="i-paro-p">Aa</i> {{ $t('Reports.ProductionPlan.legend.plannedStop') }}</span>
        <!-- Nombre de instancia del color configurado (rojo por defecto): orden programada ahora sin
             arrancar y, según el criterio configurado, también las atrasadas sin arrancar. -->
        <span class="lg"><i class="i-sin-orden" :style="{ color: colorSinOrdenArrancada }">Aa</i>
          {{ $t(modoSinOrdenArrancada === 'planAtrasado'
            ? 'Reports.ProductionPlan.legend.noStartedOrderLate'
            : 'Reports.ProductionPlan.legend.noStartedOrder') }}</span>
      </div>
    </div>

    <!-- ===================== GRÁFICA ===================== -->
    <div class="grafica-box">
      <!-- Cabecera de tiempo fija (sticky) y sincronizada con el gráfico principal: arriba las
           fechas (una celda por día de jornada) y debajo el eje con las horas. -->
      <div v-show="maquinasFiltradas.length > 0" class="pp-eje-wrap" :style="{ top: stickyTopH + 'px' }">
        <div class="pp-eje-dias" :style="{ paddingLeft: anchoColMaquina + 'px' }">
          <div class="pp-dias-track">
            <div v-for="d in marcasDia" :key="d.key" class="pp-dia" :style="{ left: d.izq }">
              {{ d.texto }}
            </div>
          </div>
        </div>
        <div ref="ejeFecha" class="pp-eje-fecha" />
      </div>
      <div v-if="cargando && !tablero" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.loadingBoard') }}</div>
      <div v-else-if="error" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.boardError') }}</div>
      <div v-else-if="maquinasFiltradas.length === 0" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.noMachines')
        }}</div>
      <div v-show="maquinasFiltradas.length > 0" ref="grafica" class="grafica-tablero"
        :style="{ height: altoGrafica + 'px' }" />
    </div>

    <!-- Dialog de detalle de fragmento -->
    <v-dialog v-model="dialogFragmento" max-width="560">
      <v-card class="frag-card">
        <v-card-title class="frag-title" :class="{ 'frag-title--fin': fragmento.finalizada }">
          <i v-if="fragmento.esReal" class="frag-dot" :class="{ 'frag-dot--fin': fragmento.finalizada }"
            :style="{ background: fragmento.colorHex }" />
          <span>{{ tipoLabel }}<template v-if="fragmento.area"> · {{ fragmento.area }}</template></span>
          <v-chip v-if="fragmento.esReal && fragmento.enCurso" color="primary" x-small class="ml-2">
            {{ $t('Reports.ProductionPlan.detail.inProgress') }}
          </v-chip>
          <v-spacer />
          <span v-if="fragmento.esReal" class="frag-banda" :style="{ color: fragmento.colorHex }">
            {{ bandaLabel }}
          </span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <!-- Identificación -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.identification') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.machine') }}</span><b>{{
                fragmento.maquina }}</b></div>
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.mold') }}</span><b>{{ fragmento.molde
                }}</b></div>
            <div class="frag-row frag-row--wide"><span>{{ $t('Reports.ProductionPlan.detail.orderAbbr') }}</span><b>{{ fragmento.clave
                }}</b></div>
          </div>

          <!-- Tiempos -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.times') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.start') }}</span><b>{{ fragmento.inicio
                }}</b></div>
            <!-- Fragmento teórico recortado (la OT terminó antes): el fin que se muestra es el programado. -->
            <div class="frag-row"><span>{{ fragmento.termino ? $t('Reports.ProductionPlan.detail.scheduledEnd')
                : $t('Reports.ProductionPlan.detail.end') }}{{ fragmento.enCurso ? `
                (${$t('Reports.ProductionPlan.detail.projected')})` : '' }}</span><b>{{ fragmento.fin
                }}</b>
            </div>
            <!-- OT terminada antes de su fin programado: hora en que terminó. -->
            <div v-if="fragmento.termino" class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.finishedAt')
                }}</span><b>{{ fragmento.termino }}</b></div>
            <!-- Duración: real = tiempo real; plan al 100% = tiempo programado base. -->
            <div v-if="fragmento.esReal || fragmento.holguraSeg <= 0" class="frag-row">
              <span>{{ fragmento.esReal ? $t('Reports.ProductionPlan.detail.realDuration') :
                $t('Reports.ProductionPlan.detail.scheduledDuration') }}</span><b>{{ fragmento.duracion }}</b>
            </div>
            <div v-if="fragmento.esReal && fragmento.duracionTeorica" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.theoreticalDuration') }}</span><b>{{ fragmento.duracionTeorica
                }}</b>
            </div>
            <!-- Plan ajustado: 100% = duración base; N% = duración total según la eficiencia. -->
            <div v-if="!fragmento.esReal && fragmento.holguraSeg > 0" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.efficiency') }} 100%</span><b>{{ fragmento.duracionBase }}</b>
            </div>
            <div v-if="!fragmento.esReal && fragmento.holguraSeg > 0" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.efficiency') }} {{ eficienciaPct }}%</span><b>{{ fragmento.duracion }}</b>
            </div>
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.scheduledTc') }}</span><b>{{ fragmento.tc ?
              fragmento.tc + ' seg' : '—' }}</b>
            </div>
          </div>

          <!-- Producción -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.production') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.target') }}</span><b>{{
              fmtNum(fragmento.objetivo) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
            <template v-if="fragmento.esReal">
              <div v-if="fragmento.programadasAhora != null" class="frag-row">
                <span>{{ $t('Reports.ProductionPlan.detail.scheduledSoFar') }}</span>
                <b>{{ fmtNum(fragmento.programadasAhora) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b>
              </div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.producedOk') }}</span><b>{{
                fmtNum(fragmento.ok) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.notOk') }}</span><b>{{
                fmtNum(fragmento.noOk) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.missing') }}</span><b>{{
                fmtNum(fragmento.faltantes) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row">
                <span>{{ $t('Reports.ProductionPlan.detail.compliance') }}</span>
                <b :style="{ color: fragmento.colorHex }">{{ fragmento.cumplimiento }}%</b>
              </div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.timeGap') }}</span><b>{{ desfaseTiempo
                  }}</b></div>
              <div v-if="fragmento.programadasAhora != null" class="frag-row">
                <span>{{ $t('Reports.ProductionPlan.detail.pieceGap') }}</span><b>{{ desfasePiezasTexto }}</b>
              </div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.percentGap') }}</span><b>{{
                  desfasePctTexto }}</b></div>
              <div class="frag-row frag-row--wide">
                <span>{{ $t('Reports.ProductionPlan.detail.range') }}</span>
                <b class="frag-rango" :style="{ color: fragmento.colorHex }">
                  <i class="frag-dot" :class="{ 'frag-dot--fin': fragmento.finalizada }"
                    :style="{ background: fragmento.colorHex, marginRight: '6px' }" />
                  {{ bandaLabel }}
                </b>
              </div>
            </template>
          </div>

          <!-- Barra de cumplimiento -->
          <div v-if="fragmento.esReal" class="frag-progress">
            <div class="frag-progress-bar" :class="{ 'frag-progress-bar--fin': fragmento.finalizada }">
              <div class="frag-progress-fill"
                :style="{ width: fragmento.cumplimientoBar + '%', background: fragmento.colorHex }" />
            </div>
            <span class="frag-progress-txt">{{ fmtNum(fragmento.ok) }} / {{ fmtNum(fragmento.objetivo) }}</span>
          </div>

          <!-- Detalle por operación (cada operación = una cavidad; corren en paralelo) -->
          <template v-if="fragmento.operaciones && fragmento.operaciones.length">
            <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.operations') }} · {{ fragmento.operaciones.length
              }} {{ $t('Reports.ProductionPlan.detail.cavities') }}</div>
            <table class="frag-ops">
              <thead>
                <tr>
                  <th>{{ $t('Reports.ProductionPlan.detail.articleCode') }}</th>
                  <th class="num">TC</th>
                  <th class="num">{{ $t('Reports.ProductionPlan.detail.scheduled') }}</th>
                  <template v-if="fragmento.esReal">
                    <th class="num">{{ $t('Reports.ProductionPlan.detail.done') }}</th>
                    <th class="num">OK</th>
                    <th class="num">{{ $t('Reports.ProductionPlan.detail.notOk') }}</th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(op, i) in fragmento.operaciones" :key="op.idOperacion || i">
                  <td>{{ op.codigo || '—' }}</td>
                  <td class="num">{{ op.tc ? op.tc + ' s' : '—' }}</td>
                  <td class="num">{{ fmtNum(op.esperadas) }}</td>
                  <template v-if="fragmento.esReal">
                    <td class="num">{{ fmtNum(op.totales) }}</td>
                    <td class="num">{{ fmtNum(op.ok) }}</td>
                    <td class="num">{{ fmtNum(op.noOk) }}</td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </v-card-text>
        <v-card-actions class="frag-actions">
          <v-btn v-if="fragmento.noIniciada" text color="error" @click="abrirCambioInstancia">
            <v-icon left small>mdi-swap-horizontal</v-icon>{{ $t('Reports.ProductionPlan.detail.changeInstance') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.esVigente && fragmento.idTrabajoOrden && fragmento.operaciones && fragmento.operaciones.length"
            text color="primary" @click="abrirActualizarTc">
            <v-icon left small>mdi-timer-cog-outline</v-icon>{{ $t('Reports.ProductionPlan.detail.updateTc') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.esVigente && fragmento.idTrabajoOrden && fragmento.operaciones && fragmento.operaciones.length"
            text color="primary" @click="abrirActualizarPiezas">
            <v-icon left small>mdi-counter</v-icon>{{ $t('Reports.ProductionPlan.detail.updatePieces') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.esVigente && fragmento.idTrabajoOrden && !fragmento.finalizada" text color="error"
            @click="abrirFinalizarOdt">
            <v-icon left small>mdi-flag-checkered</v-icon>{{ $t('Reports.ProductionPlan.detail.finishOdt') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.esVigente && fragmento.idTrabajoOrden && fragmento.finalizada" text color="success"
            @click="abrirReabrirOdt">
            <v-icon left small>mdi-lock-open-variant-outline</v-icon>{{ $t('Reports.ProductionPlan.detail.reopenOdt') }}
          </v-btn>
          <!-- Forzar finalización: cierra como OPS (Moldes V2) tenga o no bloque abierto y no deja
               nada abierto a nombre de la OT. Se ofrece también en OT ya finalizadas para barrer
               residuos (p. ej. finalizada desde OPS sin fecha de finalización). -->
          <v-btn v-if="gamemode && fragmento.esVigente && fragmento.idTrabajoOrden" text color="warning"
            @click="abrirForzarFinalizacion">
            <v-icon left small>mdi-flag-variant</v-icon>{{ $t('Reports.ProductionPlan.detail.forceFinishOdt') }}
          </v-btn>
          <!-- Eliminar fragmento: manda a condición 0 la fase (versión) completa de la OT que
               representa el fragmento real. Para pedacitos abiertos por error. -->
          <v-btn v-if="gamemode && fragmento.esReal && fragmento.idTrabajoOrden" text color="error"
            @click="abrirEliminarFragmento">
            <v-icon left small>mdi-delete-outline</v-icon>{{ $t('Reports.ProductionPlan.detail.deleteFragment') }}
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="dialogFragmento = false">{{ $t('Reports.ProductionPlan.detail.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: cambiar instancia (molde + instancia destino) de una ODT no iniciada -->
    <v-dialog v-model="dialogCambio" max-width="520" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-swap-horizontal</v-icon>
          <span>{{ $t('Reports.ProductionPlan.changeInstance.title') }} · {{
            $t('Reports.ProductionPlan.detail.orderAbbr')
            }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="cambioCargando" class="cp-empty" style="padding:18px 0">{{
            $t('Reports.ProductionPlan.empty.loadingDestinations') }}</div>
          <template v-else>
            <div v-if="cambioError" class="cambio-error">{{ cambioError }}</div>
            <div v-if="!destinos.length" class="cp-empty" style="padding:18px 0">
              {{ $t('Reports.ProductionPlan.empty.noDestinations') }}
            </div>
            <template v-else>
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetMold') }}</label>
              <v-autocomplete v-model="destinoMoldeSel" :items="opcionesMolde" item-text="text" item-value="value" dense
                outlined hide-details :placeholder="$t('Reports.ProductionPlan.changeInstance.selectMold')"
                @change="destinoInstanciaSel = null" />
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetInstance') }}</label>
              <v-autocomplete v-model="destinoInstanciaSel" :items="opcionesInstancia" item-text="text" item-value="value"
                dense outlined hide-details :disabled="!destinoMoldeSel"
                :no-data-text="destinoMoldeSel ? $t('Reports.ProductionPlan.changeInstance.noInstances') : $t('Reports.ProductionPlan.changeInstance.chooseMoldFirst')"
                :placeholder="$t('Reports.ProductionPlan.changeInstance.selectInstance')" />
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCambio = false" :disabled="cambioGuardando" color="success">{{
            $t('Reports.ProductionPlan.changeInstance.cancel') }}</v-btn>
          <v-btn color="primary" :loading="cambioGuardando" :disabled="!destinoMoldeSel || !destinoInstanciaSel"
            @click="confirmarCambioInstancia">{{ $t('Reports.ProductionPlan.changeInstance.moveOdt') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: finalizar una ODT eligiendo quién la cierra — solo gamemode.
         Equivale al diálogo de Moldes V2 de OPS, que confirma con PIN contra la sesión de la
         máquina. Desde el reporte no hay sesión, así que el operador se elige del padrón. -->
    <v-dialog v-model="dialogFinalizar" max-width="760" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left :color="finalizarForzado ? 'warning' : 'error'">
            {{ finalizarForzado ? 'mdi-flag-variant' : 'mdi-flag-checkered' }}
          </v-icon>
          <span>{{ $t(finalizarForzado ? 'Reports.ProductionPlan.forceFinish.title' : 'Reports.ProductionPlan.finish.title') }}
            {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="finalizarError" class="cambio-error">{{ finalizarError }}</div>
          <p class="mb-3">
            {{ $t(finalizarForzado ? 'Reports.ProductionPlan.forceFinish.messageStart' : 'Reports.ProductionPlan.finish.messageStart') }}
            <b>{{ fragmento.clave }}</b> ({{ fragmento.articulo }}).
            {{ $t(finalizarForzado ? 'Reports.ProductionPlan.forceFinish.messageEnd' : 'Reports.ProductionPlan.finish.messageEnd') }}
          </p>
          <p v-if="finalizarForzado" class="reopen-help mb-3">{{ $t('Reports.ProductionPlan.forceFinish.help') }}</p>

          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.finish.operatorLabel') }}</label>
          <v-text-field v-model="finalizarBusquedaOperador" dense outlined hide-details clearable
            prepend-inner-icon="mdi-magnify" :disabled="finalizarCargandoOperadores || finalizarGuardando"
            :placeholder="$t('Reports.ProductionPlan.finish.searchOperator')" />

          <div v-if="finalizarCargandoOperadores" class="cp-empty" style="padding:18px 0">
            {{ $t('Reports.ProductionPlan.finish.loadingOperators') }}
          </div>
          <div v-else-if="!operadoresFinalizarFiltrados.length" class="cp-empty" style="padding:18px 0">
            {{ $t('Reports.ProductionPlan.finish.noOperators') }}
          </div>
          <div v-else class="fin-op-grid mt-3">
            <button v-for="op in operadoresFinalizarFiltrados" :key="op.idPersona" type="button"
              class="fin-op-card" :class="{ 'fin-op-card--sel': finalizarOperadorSel === op.idPersona }"
              :disabled="finalizarGuardando" @click="finalizarOperadorSel = op.idPersona">
              <span class="fin-op-avatar">{{ op.iniciales }}</span>
              <span class="fin-op-body">
                <span class="fin-op-nombre">{{ op.nombre }}</span>
                <span class="fin-op-meta">{{ op.codigo }}<template v-if="op.puesto"> · {{ op.puesto }}</template></span>
              </span>
              <v-icon v-if="finalizarOperadorSel === op.idPersona" small color="error">mdi-check-circle</v-icon>
            </button>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogFinalizar = false" :disabled="finalizarGuardando" color="success">{{
            $t('Reports.ProductionPlan.finish.cancel') }}</v-btn>
          <v-btn :color="finalizarForzado ? 'warning' : 'error'" :loading="finalizarGuardando" :disabled="!finalizarOperadorSel"
            @click="confirmarFinalizarOdt">{{
            $t(finalizarForzado ? 'Reports.ProductionPlan.forceFinish.confirm' : 'Reports.ProductionPlan.finish.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: eliminar fragmento (fase de la OT) — solo gamemode. El backend apaga todo lo de la
         fase; si era la vigente y hay una anterior, esa vuelve a ser vigente; si era la última, la
         OT completa queda en condición 0. Rechaza con 409 si la fase aún tiene algo abierto. -->
    <v-dialog v-model="dialogEliminar" max-width="640" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="error">mdi-delete-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.deleteFragment.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="eliminarError" class="cambio-error">{{ eliminarError }}</div>
          <p class="mb-3">
            {{ $t('Reports.ProductionPlan.deleteFragment.messageStart') }}
            <b>{{ fragmento.numeroVersion }}</b>
            {{ $t('Reports.ProductionPlan.deleteFragment.messageEnd') }}
            <b>{{ fragmento.clave }}</b> ({{ fragmento.articulo }}) · {{ fragmento.maquina }} ·
            {{ fragmento.inicio }} – {{ fragmento.fin }}.
            {{ $t('Reports.ProductionPlan.deleteFragment.irreversible') }}
          </p>
          <p class="reopen-help mb-2">{{ $t('Reports.ProductionPlan.deleteFragment.help') }}</p>
          <p class="reopen-help mb-0">{{ $t('Reports.ProductionPlan.deleteFragment.lastPhase') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogEliminar = false" :disabled="eliminarGuardando" color="success">{{
            $t('Reports.ProductionPlan.deleteFragment.cancel') }}</v-btn>
          <v-btn color="error" :loading="eliminarGuardando" @click="confirmarEliminarFragmento">{{
            $t('Reports.ProductionPlan.deleteFragment.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: reabrir una ODT finalizada anticipadamente — solo gamemode -->
    <v-dialog v-model="dialogReabrir" max-width="900" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="success">mdi-lock-open-variant-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.reopen.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="reabrirCargando" class="cp-empty" style="padding:18px 0">
            {{ $t('Reports.ProductionPlan.empty.loadingDestinations') }}
          </div>
          <template v-else>
            <div v-if="reabrirError" class="cambio-error">{{ reabrirError }}</div>
            <p class="mb-3">
              {{ $t('Reports.ProductionPlan.reopen.messageStart') }} <b>{{ fragmento.clave }}</b>.
              {{ $t('Reports.ProductionPlan.reopen.messageEnd') }}
            </p>
            <div v-if="!reabrirError && !reabrirDestinos.length" class="cp-empty" style="padding:18px 0">
              {{ $t('Reports.ProductionPlan.empty.noDestinations') }}
            </div>
            <template v-else>
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetMold') }}</label>
              <v-autocomplete v-model="reabrirMoldeSel" :items="opcionesMoldeReabrir" item-text="text"
                item-value="value" dense outlined hide-details clearable
                :placeholder="$t('Reports.ProductionPlan.changeInstance.selectMold')"
                @change="reabrirInstanciaSel = null" />
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetInstance') }}</label>
              <v-autocomplete v-model="reabrirInstanciaSel" :items="opcionesInstanciaReabrir" item-text="text"
                item-value="value" dense outlined hide-details clearable :disabled="!reabrirMoldeSel"
                :placeholder="$t('Reports.ProductionPlan.changeInstance.selectInstance')" />

              <v-checkbox v-model="reabrirMismaFechaInicio" class="mt-3" hide-details
                :label="$t('Reports.ProductionPlan.reopen.keepStartDate')" @change="cambiarModoFechaReapertura" />
              <div v-if="reabrirMismaFechaInicio" class="reopen-help mt-1">
                {{ $t('Reports.ProductionPlan.reopen.currentStartDate', {
                  date: fechaInicioOriginalReaperturaTexto
                }) }}
              </div>
              <VueCtkDateTimePicker v-else v-model="reabrirFechaInicio" class="mt-3" format="YYYY-MM-DD HH:mm"
                formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay :locale="currentLocale"
                :label="$t('Reports.ProductionPlan.reopen.newStartDate')"
                :button-now-translation="$t('Reports.ProductionPlan.chart.now')" />

              <!-- La reapertura siempre vuelve a trabajar la cantidad ORIGINAL de cada detalle
                   (primera versión); la meta total queda en producido + original. Solo lectura. -->
              <v-simple-table v-if="reabrirDetalles.length" dense class="mt-4">
                <thead>
                  <tr>
                    <th>{{ $t('Reports.ProductionPlan.detail.article') }}</th>
                    <th class="text-right">{{ $t('Reports.ProductionPlan.reopen.produced') }}</th>
                    <th class="text-right">{{ $t('Reports.ProductionPlan.reopen.originalQuantity') }}</th>
                    <th class="text-right">{{ $t('Reports.ProductionPlan.reopen.newTotalTarget') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in reabrirDetalles" :key="d.idTrabajoOrdenDetalle">
                    <td>{{ d.codigoArticulo }} · {{ d.articulo }}</td>
                    <td class="text-right">{{ fmtNum(d.cantidadProducida) }}</td>
                    <td class="text-right"><b>{{ fmtNum(d.cantidad) }}</b></td>
                    <td class="text-right">{{ fmtNum(d.metaNueva) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div class="reopen-help mt-2">{{ $t('Reports.ProductionPlan.reopen.originalQuantityHelp') }}</div>
              <v-textarea v-model="reabrirMotivo" :label="$t('Reports.ProductionPlan.reopen.reason')" outlined dense
                rows="2" class="mt-4" hide-details />
              <div class="reopen-help mt-3">{{ $t('Reports.ProductionPlan.reopen.newVersionHelp') }}</div>
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogReabrir = false" :disabled="reabrirGuardando" color="error">{{
            $t('Reports.ProductionPlan.reopen.cancel') }}</v-btn>
          <v-btn color="success" :loading="reabrirGuardando" :disabled="!reaperturaValida"
            @click="confirmarReabrirOdt">{{
            $t('Reports.ProductionPlan.reopen.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: actualizar el TC por operación de la ODT — solo gamemode -->
    <v-dialog v-model="dialogTc" max-width="560" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-timer-cog-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.updateTc.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="tcError" class="cambio-error">{{ tcError }}</div>
          <p class="mb-2 mt-0" style="color:#6b7682;font-size:12px">{{ $t('Reports.ProductionPlan.updateTc.help') }}</p>
          <table class="frag-ops">
            <thead>
              <tr>
                <th>{{ $t('Reports.ProductionPlan.detail.articleCode') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updateTc.currentTc') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updateTc.newTc') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(op, i) in tcOperaciones" :key="op.idOperacion || i">
                <td>{{ op.codigo || '—' }}</td>
                <td class="num">{{ op.tcActual ? op.tcActual + ' s' : '—' }}</td>
                <td class="num">
                  <v-text-field v-model.number="op.tc" type="number" min="1" dense outlined hide-details suffix="s"
                    style="max-width:120px;margin-left:auto" />
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogTc = false" :disabled="tcGuardando" color="success">{{
            $t('Reports.ProductionPlan.updateTc.cancel') }}</v-btn>
          <v-btn color="primary" :loading="tcGuardando" :disabled="!tcValido" @click="confirmarActualizarTc">{{
            $t('Reports.ProductionPlan.updateTc.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: actualizar piezas programadas por operación/cavidad — solo gamemode -->
    <v-dialog v-model="dialogPiezas" max-width="560" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-counter</v-icon>
          <span>{{ $t('Reports.ProductionPlan.updatePieces.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="piezasError" class="cambio-error">{{ piezasError }}</div>
          <p class="mb-2 mt-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.updatePieces.help') }}
          </p>
          <table class="frag-ops">
            <thead>
              <tr>
                <th>{{ $t('Reports.ProductionPlan.detail.articleCode') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updatePieces.current') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updatePieces.new') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(op, i) in piezasOperaciones" :key="op.idOperacion || i">
                <td>{{ op.codigo || '—' }}</td>
                <td class="num">{{ fmtNum(op.cantidadActual) }}</td>
                <td class="num">
                  <v-text-field v-model.number="op.cantidad" type="number" min="1" step="1" dense outlined
                    hide-details :suffix="$t('Reports.ProductionPlan.summary.pieces')"
                    style="max-width:140px;margin-left:auto" />
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogPiezas = false" :disabled="piezasGuardando" color="success">
            {{ $t('Reports.ProductionPlan.updatePieces.cancel') }}
          </v-btn>
          <v-btn color="primary" :loading="piezasGuardando" :disabled="!piezasValidas"
            @click="confirmarActualizarPiezas">
            {{ $t('Reports.ProductionPlan.updatePieces.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: detalle del KPI de máquinas activas, sobre el tablero ya filtrado. Hasta cinco
         pestañas (ver detalleActivas y tabsActivas): activas que corren la orden programada para
         ahora (con su molde), activas que corren una orden programada pero desfasada (chip
         atrasada/adelantada entre el nombre y el molde), activas que no corren una orden
         programada, programadas sin arrancar, de ahora o vencidas (con sus moldes pendientes; las
         vencidas llevan chip y el resumen las cuenta aparte, "de ahora + vencidas") y, si la config
         la enciende (mostrarProgSinProducir), programadas sin producir (chip paro no prog. / paro
         prog. / sin actividad / pausada / finalizada). Sin título; arriba de las pestañas va el
         resumen con el conteo de cada una (clic en un conteo abre su pestaña). El ancho se ajusta
         al contenido (clase global pp-act-dialog, al final del archivo). -->
    <v-dialog v-model="dialogActivas" content-class="pp-act-dialog">
      <v-card class="frag-card">
        <div v-if="dialogActivasArea" class="act-area">{{ dialogActivasArea.nombre }}</div>
        <div class="act-resumen">
          <button v-for="(tab, i) in tabsActivas" :key="'res-' + tab.clave" type="button" class="act-stat"
            :class="{ 'act-stat--sel': tabActivas === i, 'act-stat--ambar': tab.tono === 'ambar' }"
            @click="tabActivas = i">
            <span class="act-stat-val">{{ tab.conteo || tab.items.length }}</span>
            <span class="act-stat-lbl">{{ tab.titulo }}</span>
          </button>
        </div>
        <v-divider />
        <v-tabs v-model="tabActivas" grow color="primary">
          <v-tab v-for="tab in tabsActivas" :key="'tab-' + tab.clave">{{ tab.titulo }}</v-tab>
        </v-tabs>
        <v-divider />
        <v-card-text class="pt-3">
          <v-tabs-items v-model="tabActivas">
            <v-tab-item v-for="tab in tabsActivas" :key="tab.clave">
              <div v-if="tab.items.length === 0" class="cp-empty" style="padding:18px 0">
                {{ tab.vacio }}
              </div>
              <div v-for="(it, i) in tab.items" :key="tab.clave + '-' + i" class="frag-row">
                <span class="act-name" :class="{ 'act-name--ambar': tab.tono === 'ambar' }">
                  {{ it.maquina }}
                  <span v-if="it.chip" class="act-chip" :class="'act-chip--' + it.chip.tono">{{ it.chip.texto }}</span>
                </span>
                <span v-if="it.molde" class="act-right"><b>{{ it.molde }}</b></span>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="dialogActivas = false">{{ $t('Reports.ProductionPlan.detail.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: configuración del reporte (eficiencia + bandas de color) — solo gamemode -->
    <v-dialog v-model="dialogConfig" max-width="600" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="orange darken-2">mdi-cog-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.settings.title') }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <label class="cambio-lbl">OT del periodo por</label>
          <v-select v-model="configReporte.filtroFecha" :items="filtroFechaOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:300px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Con qué fecha se decide si una OT cae en el rango consultado. Por defecto,
            <b>fecha de entrega</b>. El filtro lo aplica el backend con esta misma configuración,
            así que también cambia el Excel y los reportes que se alimentan de este tablero.
            Con <b>fecha de entrega</b>, una OT sin fecha de inicio de producción entra al reporte
            pero no puede dibujar su barra teórica.
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.periodEnd') }}</label>
          <v-select v-model="configReporte.modoFinPeriodo" :items="modoFinPeriodoOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:300px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.periodEndHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Jornada de la empresa</label>
          <div class="jornada-val">{{ jornadaEmpresaTexto || 'Sin horarios configurados' }}</div>
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Inicio y fin de la semana laboral según los horarios activos. Es solo informativo: se
            edita en el catálogo de horarios, no aquí. La gráfica lo usa para cortar los días
            (el día cambia al inicio de la jornada, no a las 00:00) y para llegar siempre hasta
            el final de la jornada aunque el filtro Fin caiga en otra hora.
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Filtro de fechas</label>
          <v-select v-model="configReporte.modoFiltroFechas" :items="modoFiltroFechasOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:300px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Con <b>Inicio y fin</b> se eligen las dos fechas a mano. Con <b>Semana</b> queda un solo
            selector: al elegir una semana, el Inicio y el Fin se calculan igual que el rango por
            defecto del reporte (arranca el primer día configurado a la hora de inicio de la empresa
            y termina según el ajuste de arriba), y la consulta se recarga sola.
          </p>
          <div v-if="configReporte.modoFiltroFechas === 'semana'" class="mt-3"
            style="padding-left:18px;border-left:2px solid #e3e7ec">
            <label class="cambio-lbl mt-0">Nombre de cada semana</label>
            <v-select v-model="configReporte.formatoSemana" :items="formatoSemanaOpciones" item-text="text"
              item-value="value" dense outlined hide-details style="max-width:300px" />
            <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
              Cómo se lee cada opción del selector: con el número de semana delante
              (<b>{{ ejemplosFormatoSemana.wk }}</b>) o solo con las fechas del periodo
              (<b>{{ ejemplosFormatoSemana.fechas }}</b>). Solo cambia el texto; el periodo
              consultado es el mismo. La numeración es la ISO 8601, de lunes a domingo.
            </p>
          </div>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Botón "Mostrar todo"</label>
          <v-switch v-model="configReporte.mostrarBotonTodo" dense hide-details inset class="mt-1"
            :label="configReporte.mostrarBotonTodo
              ? 'Visible cuando hay órdenes fuera de la jornada'
              : 'Oculto (la gráfica siempre corta en la jornada)'" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            La gráfica termina en el fin de la jornada. Con esto activo, cuando alguna orden se
            pasa de esa hora aparece un check "Mostrar todo" junto a Pantalla completa que estira
            el eje hasta la última barra. Apagado, el check no se ofrece y lo que sobrepasa la
            jornada queda recortado. No cambia los filtros ni los datos consultados.
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.percentBase') }}</label>
          <v-select v-model="configReporte.modoPorcentaje" :items="modoPorcentajeOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:260px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.percentBaseHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.fullscreenMode') }}</label>
          <v-select v-model="configReporte.modoPantallaCompleta" :items="modoPantallaCompletaOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:260px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.fullscreenModeHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.showLegend') }}</label>
          <v-switch v-model="configReporte.mostrarLeyenda" dense hide-details inset class="mt-1"
            :label="configReporte.mostrarLeyenda
              ? $t('Reports.ProductionPlan.settings.showLegendOn')
              : $t('Reports.ProductionPlan.settings.showLegendOff')" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.showLegendHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.notProducingTab') }}</label>
          <v-switch v-model="configReporte.mostrarProgSinProducir" dense hide-details inset class="mt-1"
            :label="configReporte.mostrarProgSinProducir
              ? $t('Reports.ProductionPlan.settings.notProducingTabOn')
              : $t('Reports.ProductionPlan.settings.notProducingTabOff')" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.notProducingTabHelp') }}
          </p>

          <!-- Sección "Agrupadores": los ajustes de cómo se agrupan los carriles del tablero. -->
          <v-divider class="my-4" />
          <div class="cfg-seccion">{{ $t('Reports.ProductionPlan.settings.groupsSection') }}</div>
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.groupView') }}</label>
          <v-switch v-model="configReporte.vistaPorConjunto" dense hide-details inset class="mt-1"
            :label="configReporte.vistaPorConjunto
              ? $t('Reports.ProductionPlan.settings.groupViewOn')
              : $t('Reports.ProductionPlan.settings.groupViewOff')" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.groupViewHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.groupScope') }}</label>
          <v-select v-model="configReporte.alcanceConjuntos" :items="alcanceConjuntosOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:360px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.groupScopeHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.groupOrder') }}</label>
          <v-select v-model="configReporte.ordenConjuntos" :items="ordenConjuntosOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:360px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.groupOrderHelp') }}
          </p>
          <div v-if="configReporte.ordenConjuntos === 'porArea'" class="mt-3"
            style="padding-left:18px;border-left:2px solid #e3e7ec">
            <label class="cambio-lbl mt-0">{{ $t('Reports.ProductionPlan.settings.areaTitle') }}</label>
            <v-switch v-model="configReporte.mostrarTituloArea" dense hide-details inset class="mt-1"
              :label="configReporte.mostrarTituloArea
                ? $t('Reports.ProductionPlan.settings.areaTitleOn')
                : $t('Reports.ProductionPlan.settings.areaTitleOff')" />
            <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
              {{ $t('Reports.ProductionPlan.settings.areaTitleHelp') }}
            </p>
          </div>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.redSecondRule') }}</label>
          <v-select v-model="configReporte.criterioRojoSinOrden" :items="criterioRojoOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:360px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.redSecondRuleHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.earlyOrderMode') }}</label>
          <v-select v-model="configReporte.modoAdelantada" :items="modoAdelantadaOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:360px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.earlyOrderModeHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.groupTitles') }}</label>
          <v-switch v-model="configReporte.mostrarTituloGrupos" dense hide-details inset class="mt-1"
            :disabled="configReporte.ordenConjuntos === 'original'"
            :label="configReporte.mostrarTituloGrupos
              ? $t('Reports.ProductionPlan.settings.groupTitlesOn')
              : $t('Reports.ProductionPlan.settings.groupTitlesOff')" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.groupTitlesHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.noStartedOrderMode') }}</label>
          <v-select v-model="configReporte.modoSinOrdenArrancada" :items="modoSinOrdenOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:360px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.noStartedOrderModeHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.noStartedOrderColor') }}</label>
          <div class="color-cfg-row">
            <v-menu :close-on-content-click="false" offset-y>
              <template #activator="{ on, attrs }">
                <button type="button" class="banda-swatch" :style="{ background: configReporte.colorSinOrdenArrancada }"
                  v-bind="attrs" v-on="on" :title="configReporte.colorSinOrdenArrancada" />
              </template>
              <v-color-picker v-model="configReporte.colorSinOrdenArrancada" mode="hexa" hide-mode-switch flat />
            </v-menu>
            <span class="color-cfg-muestra" :style="{ color: configReporte.colorSinOrdenArrancada }">Aa</span>
            <span class="banda-rango-txt">{{ configReporte.colorSinOrdenArrancada }}</span>
            <v-btn text small color="primary" @click="restablecerColorSinOrden">
              {{ $t('Reports.ProductionPlan.settings.noStartedOrderColorReset') }}
            </v-btn>
          </div>
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.noStartedOrderColorHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.hideForecastReal') }}</label>
          <v-switch v-model="configReporte.ocultarRealesNoIniciadas" dense hide-details inset class="mt-1"
            :label="configReporte.ocultarRealesNoIniciadas
              ? $t('Reports.ProductionPlan.settings.hideForecastRealOn')
              : $t('Reports.ProductionPlan.settings.hideForecastRealOff')" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.hideForecastRealHelp') }}
          </p>
          <div v-if="configReporte.ocultarRealesNoIniciadas" class="mt-2" style="padding-left:18px;border-left:2px solid #e3e7ec">
            <v-checkbox v-model="configReporte.dejarSiguienteProgramada" dense hide-details class="mt-0 pt-0"
              :label="$t('Reports.ProductionPlan.settings.keepNextScheduled')" />
            <p class="mt-1 mb-0" style="color:#6b7682;font-size:12px">
              {{ $t('Reports.ProductionPlan.settings.keepNextScheduledHelp') }}
            </p>
          </div>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.efficiency') }}</label>
          <v-text-field v-model.number="eficienciaPct" type="number" min="1" max="100" step="1" dense
            outlined hide-details suffix="%" style="max-width:160px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.efficiencyHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Actualización automática</label>
          <v-text-field v-model.number="configReporte.intervaloSeg" type="number" :min="10" :max="3600" step="10" dense
            outlined hide-details suffix="seg" style="max-width:160px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Cada cuántos segundos se recarga el reporte automáticamente (entre 10 y 3600). Por defecto 180 (3 min).
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.gapMode') }}</label>
          <v-select v-model="configReporte.modoDesfase" :items="modoDesfaseOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:300px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.gapModeHelp') }}
          </p>
          <label class="cambio-lbl mt-3">{{ $t('Reports.ProductionPlan.settings.gapRef') }}</label>
          <v-autocomplete v-model="configReporte.referenciaDesfase" :items="referenciaDesfaseOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:340px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.gapRefHelp') }}
          </p>

          <div v-for="grupo in gruposBandas" :key="grupo.clave">
            <v-divider class="my-4" />
            <label class="cambio-lbl">{{ grupo.titulo }}</label>
            <p class="mt-0 mb-3" style="color:#6b7682;font-size:12px">
              {{ grupo.ayuda }}
            </p>

            <div class="bandas-cards">
              <div v-for="(b, i) in configReporte[grupo.clave]" :key="i" class="banda-card">
                <div class="banda-card-top">
                  <v-menu :close-on-content-click="false" offset-y>
                    <template #activator="{ on, attrs }">
                      <button type="button" class="banda-swatch" :style="{ background: b.color }" v-bind="attrs"
                        v-on="on" :title="b.color" />
                    </template>
                    <v-color-picker v-model="b.color" mode="hexa" hide-mode-switch flat />
                  </v-menu>
                  <span class="banda-rango-txt">{{ bandaRangoTexto(grupo, i) }}</span>
                  <v-spacer />
                  <v-btn icon small :disabled="configReporte[grupo.clave].length <= 1"
                    @click="quitarBanda(grupo.clave, i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div class="banda-card-body">
                  <div class="banda-field banda-field--grow">
                    <label>Nombre</label>
                    <v-text-field v-model="b.etiqueta" dense outlined hide-details placeholder="Nombre de la banda" />
                  </div>
                  <div v-if="i < configReporte[grupo.clave].length - 1" class="banda-field">
                    <label>Cambia a los</label>
                    <v-text-field v-model.number="b.hasta" type="number" min="0" step="1" dense outlined hide-details
                      suffix="%" class="banda-hasta-input" />
                  </div>
                  <div v-else class="banda-ultima">
                    Última banda · cubre todo lo que supere {{ bandaDesde(grupo.clave, i) }}%
                  </div>
                </div>
              </div>
            </div>

            <v-btn text small color="primary" class="mt-2" @click="agregarBanda(grupo.clave)">
              <v-icon left small>mdi-plus</v-icon> Agregar banda
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="primary" :loading="configGuardando" @click="reestablecerConfig">
            {{ $t('Reports.ProductionPlan.settings.reset') }}
          </v-btn>
          <v-spacer />
          <v-btn text color="success" :disabled="configGuardando" @click="cerrarConfig">
            {{ $t('Reports.ProductionPlan.settings.cancel') }}
          </v-btn>
          <v-btn color="primary" :loading="configGuardando" @click="guardarConfig">
            {{ $t('Reports.ProductionPlan.settings.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>

import * as echarts from 'echarts'
import axios from 'axios'

// Bandas de color por desfase de tiempo (3 por defecto): verde 0–10%, amarillo 10–15%,
// rojo >15%. La última banda (hasta: null) es el catch-all. Debe coincidir con BandasDesfaseDefault
// del backend. Cada banda: { hasta: % límite superior inclusivo (null = "y más"), color hex, etiqueta }.
const getBandasDesfaseDefault = () => ([
  { hasta: 10, color: '#5b9bd5', etiqueta: '0%–10% (en meta)' },
  { hasta: 15, color: '#f0ac22', etiqueta: '10%–15% desfase' },
  { hasta: null, color: '#d64545', etiqueta: '>15% desfase' },
])

// Bandas del % de avance de piezas. OJO: van al revés que las de desfase — aquí MÁS por ciento
// es mejor, así que la primera banda (la más baja) es la roja y la última (catch-all) la verde.
const getBandasAvanceDefault = () => ([
  { hasta: 33, color: '#d64545', etiqueta: '0%–33%' },
  { hasta: 66, color: '#f0ac22', etiqueta: '33%–66%' },
  { hasta: null, color: '#22a84e', etiqueta: '>66%' },
])

// Rojo por defecto del nombre de una instancia sin orden arrancada con plan en curso: el mismo
// del paro no programado (estilo np de richNombreEje y .i-paro-np de la leyenda).
const COLOR_SIN_ORDEN_DEFAULT = '#E83535'
// Color hex válido para la config: #rrggbb o #rrggbbaa (lo que emite v-color-picker en modo hexa).
const esColorHex = (c) => typeof c === 'string' && /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(c)

// Configuración persistida del reporte (vía ReportesConfig, código r100). La llave histórica
// porcentajeHolgura se conserva por compatibilidad, pero su valor representa la eficiencia.
const getDefaultConfigReporte = () => ({
  porcentajeHolgura: 85,
  bandasDesfase: getBandasDesfaseDefault(),
  // Colores del % de avance de piezas: se aplican por separado al avance del periodo y al de
  // "programadas al momento", pero con estos mismos rangos (no se configuran por separado).
  bandasAvance: getBandasAvanceDefault(),
  // Intervalo de auto-actualización en segundos (default 3 min).
  intervaloSeg: 180,
  // Base del % de avance/progreso: 'semana' (objetivo total, como estaba) o 'hastaAhora'
  // (piezas programadas hasta ahora). Afecta piezas, %, color de barra y etiqueta secundaria.
  modoPorcentaje: 'semana',
  // Pantalla completa: 'compacta' (ajusta las filas para ver más máquinas) u 'original'
  // (alto fijo por fila, con scroll). Solo afecta la vista de pantalla completa.
  modoPantallaCompleta: 'compacta',
  // Fecha con la que el backend decide si una OT cae en el periodo: 'entrega' (default) o
  // 'inicio' (FechaInicioDeProduccion). El filtro lo aplica el back leyendo esta misma config,
  // así que también afecta al Excel y a los reportes que consumen el tablero.
  filtroFecha: 'entrega',
  // Fin del rango inicial: una semana exacta desde el inicio (comportamiento histórico)
  // o la última hora del último día configurado en los horarios de la empresa.
  modoFinPeriodo: 'semana',
  // Cómo se calcula el desfase que pinta las OT: 'tiempo' (termina más tarde que el fin
  // programado) o 'piezas' (piezas ok vs programadas hasta ahora). Lo aplica el backend
  // al resolver color/banda de cada barra; el front solo muestra el % del modo aplicado.
  modoDesfase: 'tiempo',
  // Contra qué se mide el desfase: 'finProgramado' (fin de la barra real contra el fin de la barra
  // teórica, así que un arranque tardío que no se recupera también cuenta) o 'duracionReal' (solo
  // cuánto tardó de más, sin importar a qué hora arrancó). Lo aplica el backend en ambos modos.
  referenciaDesfase: 'finProgramado',
  // Ocultar las barras "reales" de OT que nunca han arrancado (ni en curso, ni cerradas, ni
  // finalizadas): el backend genera un pronóstico real para TODA teórica y, con esto activo, solo
  // se dibuja la teórica de esas OT. Solo afecta la vista; los datos/KPI no cambian.
  ocultarRealesNoIniciadas: true,
  // Sub-opción de la anterior: al ocultar los pronósticos, conservar el de la SIGUIENTE OT que
  // debe arrancar en cada máquina (la no iniciada con inicio más próximo). Solo aplica si
  // ocultarRealesNoIniciadas está activo.
  dejarSiguienteProgramada: true,
  // Filtro de fechas de la toolbar: 'semana' (un solo selector; el periodo se calcula igual que
  // el arranque del reporte, desde los horarios) o 'rango' (los dos pickers Inicio/Fin).
  modoFiltroFechas: 'semana',
  // Cómo se nombra cada semana en ese selector: 'wk' (el número de semana del año, "WK 36") o
  // 'fechas' (el periodo, "30 ago – 4 sep"). Solo aplica con modoFiltroFechas = 'semana'.
  formatoSemana: 'wk',
  // Ofrecer el check "Mostrar todo" en la toolbar (solo aparece, además, si hay barras que se
  // pasan del fin de jornada). Apagado por defecto: la gráfica siempre corta en la jornada.
  mostrarBotonTodo: false,
  // Mostrar la leyenda de colores (plan, bandas de desfase, finalizada, paro no programado)
  // arriba de la gráfica. Apagada, la gráfica gana ese alto. Solo afecta la vista.
  mostrarLeyenda: true,
  // Pestaña "Prog sin producir" del diálogo de máquinas activas (ver detalleActivas): máquinas
  // programadas para ahora cuya OT ya arrancó o ya terminó y no está produciendo. Visible por
  // defecto; apagada, el diálogo se queda con sus otras cuatro pestañas. Solo afecta la vista.
  mostrarProgSinProducir: true,
  // Vista por conjunto: resalta los dos conjuntos de excepción (sin arrancar / paro no programado
  // y orden adelantada; ver agruparCarriles) según alcanceConjuntos y ordenConjuntos. Apagada,
  // las máquinas van en el orden del backend, como siempre. Solo afecta la vista.
  vistaPorConjunto: false,
  // Dónde aplica la vista por conjunto: 'ambas' (default), 'general' (solo sin áreas en el
  // filtro) o 'areas' (solo con una o más áreas seleccionadas). Fuera del alcance, orden normal.
  alcanceConjuntos: 'ambas',
  // Posición de los conjuntos: 'arriba' (default; suben al inicio del tablero con renglón de
  // título y marco), 'original' (nadie se mueve; las máquinas del conjunto llevan punto y línea
  // de su color junto al nombre, compartidos si son consecutivas; ver renderMarcaMaquina) o
  // 'porArea' (una sección por área, en orden de nombre, y dentro de cada una los conjuntos arriba
  // y el resto en orden natural de nombre de máquina; ver agruparCarriles).
  ordenConjuntos: 'arriba',
  // Renglón con el nombre del área al inicio de cada sección de la posición 'porArea'. Visible por
  // defecto; solo aplica con ordenConjuntos 'porArea' y solo afecta la vista.
  mostrarTituloArea: true,
  // Hasta cuándo una orden cuenta como ADELANTADA para el conjunto naranja (ver
  // tieneOrdenAdelantada): 'hastaFinalizar' (default; mientras la orden siga en curso, aunque ya
  // esté dentro de su fragmento programado) o 'hastaFragmento' (solo hasta que la hora actual
  // alcance el inicio de su fragmento programado).
  modoAdelantada: 'hastaFinalizar',
  // Segunda condición del conjunto rojo (la primera, paro no programado sin actividad, es fija):
  // 'activaSinOt' (default; el contador la reporta activa y no tiene operación abierta; ver
  // esActivaSinOt) o 'programadaSinArrancar' (la orden programada sin arrancar de
  // tieneOrdenSinArrancar, la misma que pinta el nombre en rojo).
  criterioRojoSinOrden: 'activaSinOt',
  // Nombre en el renglón de título de cada conjunto ("Sin arrancar / paro no programado" /
  // "Orden adelantada"). Oculto por defecto: el renglón conserva solo el punto de color y el marco no
  // cambia. Solo aplica con vistaPorConjunto encendida y ordenConjuntos 'arriba'; solo afecta la
  // vista.
  mostrarTituloGrupos: false,
  // Cuándo se pinta el nombre de una instancia con orden programada sin arrancar (ver el bloque
  // sinOrden de datos()); en los dos casos solo si la máquina no tiene ninguna otra orden abierta:
  // 'planEnCurso' = SOLO si la línea de "ahora" cae sobre un fragmento teórico cuya OT no se ha
  // arrancado; 'planAtrasado' = lo anterior MÁS cualquier fragmento teórico atrasado (ya debió
  // correr) cuya OT tampoco se ha arrancado.
  modoSinOrdenArrancada: 'planEnCurso',
  // Color del nombre en ese caso. Por defecto el mismo rojo del paro no programado. Solo afecta
  // la vista.
  colorSinOrdenArrancada: COLOR_SIN_ORDEN_DEFAULT,
})

// Límites del intervalo de auto-actualización (segundos).
const INTERVALO_MIN_SEG = 10
const INTERVALO_MAX_SEG = 3600

const COLOR = {
  planFill: '#dcf0d6',
  planBorde: '#7cbd74',
  planTexto: '#2f6e2b',
  azul: '#2563d6',
  // Piezas y horas de la orden en curso, junto al nombre de la instancia: gris azulado, para que
  // el dato se lea sin competir con los colores de las barras.
  datoEje: '#64748b',
  // Líneas verticales de guía del tablero (cortes de jornada).
  guia: '#e6e9ed',
  // Barra de jornada de las máquinas sin plan: gris claro, sin peso visual.
  jornadaFill: '#eceff3',
  jornadaBorde: '#d3dae2',
  finJornada: '#1f78d1',
  ahora: '#c0392b',
  // Borde de ODT finalizada (algún detalle activo en estado 5).
  finalizada: '#22a84e',
}

const BANDA_COLOR = {
  // "En meta": azul claro (antes verde #63b46a).
  verde: '#5b9bd5',
  amarillo: '#f0ac22',
  naranja: '#e67e22',
  rojo: '#d64545',
}

// Columna de instancias: hueco a la izquierda del nombre y hueco entre el último dato y la
// primera línea de la gráfica. Los usa anchoColMaquina (grid.left) y el margin de la etiqueta.
const COL_INSET = 2
const COL_AIRE = 8

// Grupos de carriles del tablero: un título (categoría fantasma del eje Y, sin barras: punto de
// color y, solo con mostrarTituloGrupos, el nombre) seguido de sus máquinas y un marco a la
// izquierda: una sola línea vertical que arranca en la esquina redondeada del título. Con
// ordenConjuntos 'original' no hay título ni marco: cada tramo de máquinas consecutivas del mismo
// conjunto lleva punto y línea propios (renderMarcaMaquina). La llave nombra los estilos rich del
// título (gd_/gt_) y el color va en el punto, el texto y la línea.
const GRUPO_PREFIJO = '__grupo:'
const GRUPOS_CARRIL = {
  notStartedOrUnplannedStop: { color: '#E0524D', i18n: 'Reports.ProductionPlan.groups.notStartedOrUnplannedStop' },
  startedEarly: { color: '#F2A31B', i18n: 'Reports.ProductionPlan.groups.startedEarly' },
}
// Título de área (posición 'porArea'): categoría fantasma del eje Y con el nombre del área, sin
// marco ni punto; separa las secciones del tablero. Estilo rich `ga`.
const AREA_PREFIJO = '__area:'
const COLOR_TITULO_AREA = '#283139'
// Orden natural de nombres: 101 < 102 < 103A < 111C y NAVE 1 < NAVE 2 < NAVE 10 (el orden de
// cadena pondría NAVE 10 antes que NAVE 2). Sin distinguir mayúsculas ni acentos.
const cmpNatural = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' })
// Ancho reservado a la izquierda de los nombres para el corchete (solo cuando hay grupos).
const ANCHO_CORCHETE = 16

const DIAS = {
  es: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
}
// Inicial del día para la cabecera de la gráfica, donde el espacio es poco. Martes y miércoles
// comparten letra (igual que jueves y sábado en inglés): la fecha que va al lado los distingue.
const DIAS_INICIAL = {
  es: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
  en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
}

const MESES = {
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
}

// Lee una propiedad aceptando camelCase o PascalCase.
const prop = (obj, camel) => {
  if (!obj) return undefined
  if (obj[camel] !== undefined) return obj[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return obj[pascal]
}

const toMs = (iso) => {
  if (!iso) return NaN
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? NaN : t
}

// Fin de una barra TEÓRICA para dibujarla y para decidir si la máquina cuenta como programada: el
// fin efectivo del backend (se recorta a la hora en que la OT terminó) o, si no viene, el fin
// programado. El fin programado sigue en 'fin' y es el que dan los textos de fin y de duración.
const finTeorica = (b) => prop(b, 'finEfectivo') ?? prop(b, 'fin')

// Bool tolerante (true / 1 / '1'), igual que bool() del reporte de Estado de Máquinas (r61).
const esTrue = (v) => v === true || v === 1 || v === '1'

const colorBanda = (ok, objetivo, colorBackend) => {
  // El backend manda el color hex ya resuelto (banda de desfase configurable). Se respeta tal cual,
  // salvo el verde histórico de "en meta" (#63b46a), que se remapea al azul claro del indicador.
  if (typeof colorBackend === 'string' && colorBackend.charAt(0) === '#') {
    return colorBackend.toLowerCase() === '#63b46a' ? BANDA_COLOR.verde : colorBackend
  }
  if (colorBackend && BANDA_COLOR[colorBackend]) return BANDA_COLOR[colorBackend]
  if (!objetivo || objetivo <= 0) return BANDA_COLOR.verde
  const falt = Math.max(0, objetivo - ok)
  const pct = (falt * 100) / objetivo
  if (pct <= 1) return BANDA_COLOR.verde
  if (pct <= 10) return BANDA_COLOR.amarillo
  if (pct <= 15) return BANDA_COLOR.naranja
  return BANDA_COLOR.rojo
}

const fmtNum = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })

const fmtHoras = (seg) => {
  const h = Math.round(Number(seg || 0) / 3600)
  return `${h} h`
}

const fmtFechaHora = (ms, locale = 'es') => {
  const d = new Date(ms)
  const p = (n) => String(n).padStart(2, '0')
  const dias = DIAS[locale] || DIAS.es
  const meses = MESES[locale] || MESES.es
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const fmtDuracion = (seg) => {
  const s = Math.max(0, Math.round(Number(seg) || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h} h ${m} min`
  if (m > 0) return `${m} min`
  return `${s} s`
}

// Devuelve la CLAVE de la banda de desfase ('verde' | 'amarillo' | 'naranja' | 'rojo')
// para resolver el texto con i18n (reactivo al idioma), tanto en el template como en el tooltip.
const bandaKey = (ok, objetivo, colorBackend) => {
  const KEYS = ['verde', 'amarillo', 'naranja', 'rojo']
  if (colorBackend && KEYS.includes(colorBackend)) return colorBackend
  if (!objetivo || objetivo <= 0) return 'verde'
  const pct = (Math.max(0, objetivo - ok) * 100) / objetivo
  if (pct <= 1) return 'verde'
  if (pct <= 10) return 'amarillo'
  if (pct <= 15) return 'naranja'
  return 'rojo'
}

export default {
  name: 'ReportePlanDeProduccionV4',

  props: {
    active: { type: Boolean, default: false },
  },

  data() {
    return {
      chart: null,
      // Segundo gráfico: solo el eje de fechas, fijo (sticky) y sincronizado con el zoom del principal.
      ejeChart: null,
      // Alto de la cabecera fija (leyenda + avance), para anclar la barra de fechas justo debajo.
      stickyTopH: 96,
      resizeHandler: null,
      cargando: false,
      error: false,
      tablero: null,
      // Máquinas en producción según /ReportesMoldes/MaquinasActivas (tokens físicos activos con
      // actividad reciente según el contador de la PC de planta; NO mira OTs ni paros de ops).
      // Alimenta el KPI azul "Máquinas activas" y, junto con el estado, decide qué nombres van en
      // rojo (paro no programado) o amarillo (paro programado).
      maquinasActivasApi: [],
      // true tras la primera respuesta de MaquinasActivas: hasta entonces no se agrupan carriles
      // (con el contador vacío TODO lo programado saldría "no activa" y el tablero brincaría).
      maquinasActivasListas: false,
      // Loading del KPI/botón de máquinas activas mientras se consulta el endpoint.
      maquinasActivasCargando: false,
      // Estado en tiempo real de las instancias del área (/OEEV2/EstadoMaquinasTiempoReal): el
      // mismo endpoint y la misma regla que el reporte de Estado de Máquinas (r61). Se usa para
      // pintar el nombre de las instancias en paro (rojo no programado, amarillo programado) y
      // para saber si una máquina activa tiene OT abierta. Si falla se conserva el último valor conocido.
      estadoMaquinasApi: [],
      vista: 'tiempoReal',
      filtros: {
        inicio: '',
        fin: '',
        // Áreas preseleccionadas desde la URL (?idsArea=4,3); sin parámetro = todas.
        idsArea: this.$utils.reportes.idsAreaUrl(this.$route.query),
        idsTrabajoOrden: [],
        idsInstancia: [],
        idsArticulo: [],
        idsMolde: [],
      },
      dialogFragmento: false,
      dialogActivas: false,
      // Área elegida en la tarjeta para el diálogo de activas ({ id, nombre }); null = todas.
      dialogActivasArea: null,
      // Pestaña activa del diálogo de máquinas: 0 = activas con plan de ahora, 1 = activas con plan
      // desfasado, 2 = activas sin plan, 3 = programadas sin arrancar, 4 = programadas sin producir
      // (solo si la config la enciende; ver tabsActivas).
      tabActivas: 0,
      // Cambio de instancia (molde + instancia destino) para ODTs no iniciadas.
      dialogCambio: false,
      cambioCargando: false,
      cambioGuardando: false,
      cambioError: '',
      destinos: [],
      destinoMoldeSel: null,
      destinoInstanciaSel: null,
      // Finalizar (terminar anticipadamente) una ODT — solo gamemode.
      dialogFinalizar: false,
      finalizarGuardando: false,
      finalizarError: '',
      // Operador que queda registrado como quien finalizó (IdOperadorFinalizo). En OPS lo
      // resuelve el PIN; acá se elige del padrón de operadores de producción.
      finalizarOperadores: [],
      finalizarOperadorSel: null,
      finalizarCargandoOperadores: false,
      finalizarBusquedaOperador: '',
      // true cuando el diálogo se abrió desde "Forzar finalización": mismo diálogo y operador,
      // pero el backend cierra aunque no haya bloque abierto y barre todo lo residual de la OT.
      finalizarForzado: false,
      // Eliminar fragmento (fase de la OT a condición 0).
      dialogEliminar: false,
      eliminarGuardando: false,
      eliminarError: '',
      // Reabrir una ODT finalizada anticipadamente — solo gamemode.
      dialogReabrir: false,
      reabrirCargando: false,
      reabrirGuardando: false,
      reabrirError: '',
      reabrirDestinos: [],
      reabrirMoldeSel: null,
      reabrirInstanciaSel: null,
      reabrirDetalles: [],
      reabrirMotivo: '',
      reabrirIdSolicitud: '',
      reabrirMismaFechaInicio: true,
      reabrirFechaInicioOriginal: '',
      reabrirFechaInicio: '',
      // Actualizar el TC por operación de la ODT — solo gamemode.
      dialogTc: false,
      tcGuardando: false,
      tcError: '',
      tcOperaciones: [],
      // Actualizar las piezas programadas por operación/cavidad — solo gamemode.
      dialogPiezas: false,
      piezasGuardando: false,
      piezasError: '',
      piezasOperaciones: [],
      // Inicio y fin de la semana laboral según /Horarios, como { dia (0=domingo…6=sábado),
      // hh, mm }. El inicio ancla los cortes del eje cada 12/24 h; el fin marca hasta dónde
      // debe llegar la gráfica. Null mientras no se haya podido leer el horario.
      horaInicioEmpresa: null,
      horaFinEmpresa: null,
      // Semanas hacia atrás que ofrece el selector. Crece al llegar al final de la lista
      // (scroll), así el desplegable no arranca con cientos de opciones.
      semanasAtras: 12,
      // Corte al fin de jornada: apagado, el eje termina ahí aunque haya barras más allá.
      // El check solo se ofrece cuando existen esas barras (ver datos.excedeJornada).
      mostrarTodo: false,
      esFullscreen: false,
      // Ventana de tiempo visible del eje (ms). La fija el render y la actualiza el zoom; de aquí
      // salen las celdas de día de la cabecera.
      ejeExtent: null,
      // Alto disponible (px) para la gráfica en pantalla completa; se mide al entrar/redimensionar.
      fsAltoDisponible: 0,
      fsHandler: null,
      fragmento: {},
      // Configuración del reporte — solo gamemode.
      codigoReporte: 'r100',
      configReporte: getDefaultConfigReporte(),
      dialogConfig: false,
      configGuardando: false,
      // Auto-actualización: la primera carga usa la semana en curso; a partir de
      // ahí cada recarga (auto o manual) consulta con los filtros actuales.
      // El intervalo es configurable (configReporte.intervaloSeg).
      restante: 180,
      timerTick: null,
    }
  },

  computed: {
    gamemode() {
      return localStorage.getItem("gamemode") === "true";
    },

    // Operadores del selector de finalización filtrados por el buscador (nombre, código o puesto).
    operadoresFinalizarFiltrados() {
      const q = (this.finalizarBusquedaOperador || '').trim().toLowerCase()
      if (!q) return this.finalizarOperadores
      return this.finalizarOperadores.filter((o) => o.busqueda.includes(q))
    },

    // Los dos grupos de bandas configurables del diálogo. Comparten tarjetas, helpers y forma
    // (lista ascendente por "hasta", la última sin límite), solo cambia sobre qué % se aplican.
    gruposBandas() {
      return [
        {
          clave: 'bandasDesfase',
          concepto: 'Desfase',
          titulo: 'Bandas de color por desfase',
          ayuda: 'Cada banda pinta las OT según su % de desfase. Toca el cuadro de color para ' +
            'cambiarlo; el límite inferior se calcula solo y la última banda cubre todo lo que ' +
            'supere su límite.',
        },
        {
          clave: 'bandasAvance',
          concepto: 'Avance',
          titulo: 'Bandas de color por avance de piezas',
          ayuda: 'Pintan los dos porcentajes de avance de piezas: el total del periodo y el de ' +
            '"programadas al momento". Se configuran una sola vez, pero cada porcentaje toma el ' +
            'color de la banda donde cae su propio valor. Aquí más por ciento es mejor, así que ' +
            'la primera banda es la más baja y la última cubre todo lo que supere su límite.',
        },
      ]
    },

    // Modo del filtro de fechas: un solo selector de semana en vez de los pickers Inicio/Fin.
    filtroPorSemana() {
      return !!(this.configReporte && this.configReporte.modoFiltroFechas === 'semana')
        && !!this.horaInicioEmpresa
    },

    // Semanas ofrecidas en el selector: 4 hacia adelante y las que lleve el contador hacia atrás
    // (aumenta al hacer scroll hasta el final de la lista). Si el rango actual no coincide con
    // ninguna (venía de un filtro manual), se agrega para no perderlo.
    opcionesSemana() {
      const base = this.rangoSemanaDe(new Date())
      if (!base) return []
      const SEMANA = 7 * 24 * 60 * 60 * 1000
      const actualMs = base.inicio.getTime()
      const items = []
      for (let i = -this.semanasAtras; i <= 4; i++) {
        const rango = this.rangoSemanaDe(new Date(actualMs + i * SEMANA))
        if (!rango) continue
        const valor = rango.inicio.getTime()
        let texto = this.etiquetaSemana(rango.inicio, rango.fin)
        if (valor === actualMs) texto += ` · ${this.$t('Reports.ProductionPlan.filters.weekCurrent')}`
        items.push({ text: texto, value: valor })
      }
      const sel = this.semanaSeleccionada
      if (Number.isFinite(sel) && !items.some((it) => it.value === sel)) {
        const rango = this.rangoSemanaDe(new Date(sel))
        if (rango) items.push({ text: this.etiquetaSemana(new Date(sel), rango.fin), value: sel })
      }
      return items.sort((a, b) => b.value - a.value)
    },

    // Semana vigente = el Inicio del filtro. Al elegir otra se recalcula el rango y se recarga.
    semanaSeleccionada: {
      get() {
        const ms = toMs(this.filtroAIso(this.filtros.inicio))
        return Number.isFinite(ms) ? ms : null
      },
      set(v) {
        this.aplicarSemana(v)
      },
    },

    // Fechas de la cabecera: una etiqueta por día, centrada JUSTO ENCIMA del corte de la mañana.
    // La jornada parte el día en dos cortes de 12 h (p. ej. 18:00 y 06:00, o 19:00 y 07:00); de
    // esos dos manda el que cae antes del mediodía, y a partir de ahí la fecha se repite cada 24 h.
    // Así, con jornada de domingo 18:00, los días se leen sobre las 06:00 de lunes a viernes en vez
    // de sobre el arranque de la jornada. Sin horarios leídos el ancla son las 00:00.
    marcasDia() {
      const ext = this.ejeExtent
      if (!ext || !(ext.max > ext.min)) return []
      const { min, max } = ext
      const DIA = 24 * 60 * 60 * 1000
      // Solo la inicial del día: la cabecera es angosta y el resto ya lo dice la fecha.
      const dias = DIAS_INICIAL[this.$i18n.locale] || DIAS_INICIAL.es
      const meses = MESES[this.$i18n.locale] || MESES.es
      const etiqueta = (t) => {
        const f = new Date(t)
        return `${dias[f.getDay()]} ${f.getDate()} ${meses[f.getMonth()]}`
      }
      const ini = this.horaInicioEmpresa
      const hh = ini ? ini.hh : 0
      const mm = ini ? ini.mm : 0
      const span = max - min
      // Primer corte de la mañana dentro del rango.
      const d = new Date(min)
      d.setHours(hh < 12 ? hh : hh - 12, mm, 0, 0)
      if (d.getTime() < min) d.setTime(d.getTime() + DIA)
      const marcas = []
      for (let t = d.getTime(); t <= max && marcas.length < 60; t += DIA) {
        marcas.push({ key: t, texto: etiqueta(t), izq: `${((t - min) / span) * 100}%` })
      }
      // Zoom tan corto que no alcanza ningún corte de la mañana: al menos la fecha del tramo.
      if (!marcas.length) marcas.push({ key: min, texto: etiqueta(min), izq: '50%' })
      return marcas
    },

    // ¿Hay barras que terminan después del fin de la jornada? Es la otra condición del check.
    hayDatosFueraJornada() {
      return !!(this.datos && this.datos.excedeJornada)
    },

    // El check "Mostrar todo" se ofrece si la configuración lo habilita Y hay algo recortado.
    mostrarCheckTodo() {
      return this.botonTodoHabilitado && this.hayDatosFueraJornada
    },

    // Interruptor de la config que habilita el check (apagado por defecto).
    botonTodoHabilitado() {
      return !!(this.configReporte && this.configReporte.mostrarBotonTodo)
    },

    // Estirar el eje más allá de la jornada: solo si el check está habilitado y activado. Así,
    // al ocultar el botón desde la configuración, la gráfica vuelve a cortar sin estado colgado.
    expandirFueraJornada() {
      return this.botonTodoHabilitado && this.mostrarTodo
    },

    // Semana laboral de la empresa en texto ("domingo 06:00 → sábado 22:00"), para mostrarla
    // como dato informativo en la configuración. Null si /Horarios no se pudo leer.
    jornadaEmpresaTexto() {
      const ini = this.horaInicioEmpresa
      const fin = this.horaFinEmpresa
      if (!ini || !fin) return null
      const hhmm = (h) => `${String(h.hh).padStart(2, '0')}:${String(h.mm).padStart(2, '0')}`
      return `${this.nombreDiaSemana(ini.dia)} ${hhmm(ini)} → ${this.nombreDiaSemana(fin.dia)} ${hhmm(fin)}`
    },

    // Leyenda de colores arriba de la gráfica: visible salvo que la config la apague.
    mostrarLeyenda() {
      return !this.configReporte || this.configReporte.mostrarLeyenda !== false
    },

    // Switch "Prog sin producir" de la config: si el diálogo de máquinas activas lleva esa
    // pestaña (ver tabsActivas). Encendido salvo que la config lo apague.
    mostrarProgSinProducir() {
      return !this.configReporte || this.configReporte.mostrarProgSinProducir !== false
    },

    // Switch "Vista por conjunto" de la config: agrupa arriba las sin arrancar / en paro no
    // programado y las de orden adelantada (ver agruparCarriles). Apagado por defecto.
    vistaPorConjunto() {
      return !!this.configReporte && this.configReporte.vistaPorConjunto === true
    },

    // Switch "Títulos de los conjuntos" de la config: si el renglón de título de cada conjunto
    // lleva el nombre junto al punto de color o solo el punto. Apagado (solo el punto) por defecto.
    mostrarTituloGrupos() {
      return !!this.configReporte && this.configReporte.mostrarTituloGrupos === true
    },

    // Posición de los conjuntos: 'arriba' (default; agrupados al inicio con renglón de título y
    // marco), 'original' (orden del backend; punto y línea junto a las máquinas del conjunto) o
    // 'porArea' (una sección por área y, dentro de cada una, los conjuntos arriba).
    ordenConjuntos() {
      const v = this.configReporte && this.configReporte.ordenConjuntos
      return v === 'original' || v === 'porArea' ? v : 'arriba'
    },

    ordenConjuntosOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.groupOrderTop'), value: 'arriba' },
        { text: this.$t('Reports.ProductionPlan.settings.groupOrderByArea'), value: 'porArea' },
        { text: this.$t('Reports.ProductionPlan.settings.groupOrderOriginal'), value: 'original' },
      ]
    },

    // Switch "Nombre del área" de la config: si cada sección de la posición por área abre con el
    // renglón del nombre del área. Encendido por defecto.
    mostrarTituloArea() {
      return !this.configReporte || this.configReporte.mostrarTituloArea !== false
    },

    // Hasta cuándo una orden cuenta como adelantada: 'hastaFinalizar' (default) o 'hastaFragmento'.
    modoAdelantada() {
      return (this.configReporte && this.configReporte.modoAdelantada) === 'hastaFragmento'
        ? 'hastaFragmento'
        : 'hastaFinalizar'
    },

    modoAdelantadaOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.earlyOrderModeUntilFinished'), value: 'hastaFinalizar' },
        { text: this.$t('Reports.ProductionPlan.settings.earlyOrderModeUntilPlanned'), value: 'hastaFragmento' },
      ]
    },

    // Segunda condición del conjunto rojo: 'activaSinOt' (default) o 'programadaSinArrancar'.
    criterioRojoSinOrden() {
      return (this.configReporte && this.configReporte.criterioRojoSinOrden) === 'programadaSinArrancar'
        ? 'programadaSinArrancar'
        : 'activaSinOt'
    },

    criterioRojoOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.redSecondRuleActiveNoOrder'), value: 'activaSinOt' },
        { text: this.$t('Reports.ProductionPlan.settings.redSecondRuleScheduled'), value: 'programadaSinArrancar' },
      ]
    },

    // Dónde aplica la vista por conjunto: 'ambas' (default), 'general' (solo sin filtro de áreas)
    // o 'areas' (solo con filtro de áreas).
    alcanceConjuntos() {
      const v = this.configReporte && this.configReporte.alcanceConjuntos
      return v === 'general' || v === 'areas' ? v : 'ambas'
    },

    alcanceConjuntosOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.groupScopeBoth'), value: 'ambas' },
        { text: this.$t('Reports.ProductionPlan.settings.groupScopeGeneral'), value: 'general' },
        { text: this.$t('Reports.ProductionPlan.settings.groupScopeAreas'), value: 'areas' },
      ]
    },

    // Si, con el filtro de áreas actual, la vista por conjunto está dentro del alcance
    // configurado. "Vista general" = sin áreas seleccionadas; "filtrada" = una o más. Es el mismo
    // filtro que usan las columnas de áreas del KPI de máquinas activas.
    conjuntosAplicanAqui() {
      const conAreas = ((this.filtros && this.filtros.idsArea) || []).length > 0
      if (this.alcanceConjuntos === 'general') return !conAreas
      if (this.alcanceConjuntos === 'areas') return conAreas
      return true
    },

    // Criterio configurado para pintar la máquina sin orden arrancada: 'planEnCurso' (default) o
    // 'planAtrasado'. Ver el bloque sinOrden de datos().
    modoSinOrdenArrancada() {
      return (this.configReporte && this.configReporte.modoSinOrdenArrancada) === 'planAtrasado'
        ? 'planAtrasado'
        : 'planEnCurso'
    },

    modoSinOrdenOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.noStartedOrderModeCurrent'), value: 'planEnCurso' },
        { text: this.$t('Reports.ProductionPlan.settings.noStartedOrderModeLate'), value: 'planAtrasado' },
      ]
    },

    // Color configurado para el nombre de las instancias sin orden arrancada (leyenda y estilo
    // `so` del eje). Si la config trae algo inválido, el rojo por defecto.
    colorSinOrdenArrancada() {
      const c = this.configReporte && this.configReporte.colorSinOrdenArrancada
      return esColorHex(c) ? c : COLOR_SIN_ORDEN_DEFAULT
    },

    // Intervalo de auto-actualización vigente (segundos), tomado de la config.
    intervaloSeg() {
      const n = Number(this.configReporte && this.configReporte.intervaloSeg)
      return Number.isFinite(n) && n > 0 ? n : 180
    },

    currentLocale() {
      return this.$i18n.locale
    },

    // Nombre de la planta a partir de la empresa activa (store.state.nombreEmpresa).
    // CAZEL-TLT se muestra como "Tultitlán" y Cazel como "Querétaro"; el resto usa el nombre tal cual.
    nombrePlanta() {
      const emp = String((this.$store && this.$store.state && this.$store.state.nombreEmpresa) || '').trim()
      const MAPA = { 'cazel-tlt': 'Tultitlán', 'cazel': 'Querétaro' }
      return MAPA[emp.toLowerCase()] || emp || '—'
    },

    maquinas() {
      return (this.tablero && (prop(this.tablero, 'maquinas') || [])) || []
    },

    maquinasFiltradas() {
      return this.maquinas
    },

    catAreas() {
      // El backend ya omite las áreas con Retrabajo = null (p. ej. "SIN DESIGNAR").
      return this.catalogoItems('areas')
    },

    // Opciones del filtro "Vista": tiempo real vs programado (teórico).
    vistaOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.toolbar.realTime'), value: 'tiempoReal' },
        { text: this.$t('Reports.ProductionPlan.toolbar.theoretical'), value: 'teorico' },
      ]
    },

    // Eficiencia editable en la config (%). La llave porcentajeHolgura se mantiene únicamente
    // por compatibilidad con la configuración r100 existente.
    eficienciaPct: {
      get() {
        return Number(this.configReporte && this.configReporte.porcentajeHolgura) || 0
      },
      set(v) {
        let n = Number(v)
        if (!Number.isFinite(n)) n = 85
        n = Math.min(100, Math.max(1, n))
        this.configReporte.porcentajeHolgura = n
      },
    },

    // Opciones del filtro de fechas de la toolbar (config).
    modoFiltroFechasOpciones() {
      return [
        { text: 'Inicio y fin', value: 'rango' },
        { text: 'Semana', value: 'semana' },
      ]
    },

    // Opciones de cómo se nombra cada semana en el selector.
    formatoSemanaOpciones() {
      return [
        { text: 'Número de semana y fechas', value: 'wk' },
        { text: 'Solo las fechas del periodo', value: 'fechas' },
      ]
    },

    // ¿El selector de semana muestra "WK n" en vez de las fechas del periodo?
    formatoSemanaWk() {
      return !!(this.configReporte && this.configReporte.formatoSemana === 'wk')
    },

    // La semana en curso escrita de las dos formas, para que la ayuda de la configuración
    // muestre el formato real y no un ejemplo inventado.
    ejemplosFormatoSemana() {
      const r = this.rangoSemanaDe(new Date())
      if (!r) return { fechas: '02 Ene - 11 Ene', wk: 'W01 02 Ene - 11 Ene' }
      return {
        fechas: this.textoSemanaFechas(r.inicio, r.fin),
        wk: this.textoSemanaWk(r.inicio, r.fin),
      }
    },

    // Opciones del modo de cálculo del % (config): semana vs hasta ahora.
    modoPorcentajeOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.percentWeek'), value: 'semana' },
        { text: this.$t('Reports.ProductionPlan.settings.percentSoFar'), value: 'hastaAhora' },
      ]
    },

    modoPantallaCompletaOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.fullscreenCompact'), value: 'compacta' },
        { text: this.$t('Reports.ProductionPlan.settings.fullscreenOriginal'), value: 'original' },
      ]
    },

    // Fecha con la que se decide si una OT entra al periodo consultado.
    filtroFechaOpciones() {
      return [
        { text: 'Fecha de entrega', value: 'entrega' },
        { text: 'Fecha de inicio de producción', value: 'inicio' },
      ]
    },

    modoFinPeriodoOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.periodEndWeek'), value: 'semana' },
        { text: this.$t('Reports.ProductionPlan.settings.periodEndSchedule'), value: 'horarioEmpresa' },
      ]
    },

    // Modo de cálculo del desfase (config): tiempo vs piezas.
    modoDesfaseOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.gapModeTime'), value: 'tiempo' },
        { text: this.$t('Reports.ProductionPlan.settings.gapModePieces'), value: 'piezas' },
      ]
    },

    // Contra qué se mide el desfase (config): el fin programado de la OT o solo su duración real.
    referenciaDesfaseOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.gapRefScheduledEnd'), value: 'finProgramado' },
        { text: this.$t('Reports.ProductionPlan.settings.gapRefRealDuration'), value: 'duracionReal' },
      ]
    },

    // Modo de desfase que REALMENTE aplicó el backend al colorear las barras (tablero.modoDesfase).
    // Si no llega (datos antiguos), se asume el de la config local. Decide qué % se muestra en
    // detalle y tooltip, para que el número coincida con el color de la barra.
    modoDesfaseAplicado() {
      const delTablero = prop(this.tablero, 'modoDesfase')
      if (delTablero === 'piezas' || delTablero === 'tiempo') return delTablero
      return (this.configReporte && this.configReporte.modoDesfase) === 'piezas' ? 'piezas' : 'tiempo'
    },

    catInstancias() {
      return this.catalogoItems('instancias')
    },

    catOrdenes() {
      return this.catalogoItems('ordenesTrabajo')
    },

    catArticulos() {
      return this.catalogoItems('articulos')
    },

    catMoldes() {
      return this.catalogoItems('moldes')
    },

    avance() {
      const a = (this.tablero && prop(this.tablero, 'avance')) || {}
      return {
        porcentaje: Number(prop(a, 'porcentaje') || 0),
        piezasOk: Number(prop(a, 'piezasOk') || 0),
        piezasObjetivo: Number(prop(a, 'piezasObjetivo') || 0),
        // Meta "hasta ahora" ya prorrateada por el backend (línea de fechaConsulta).
        piezasProgramadasHastaAhora: Number(prop(a, 'piezasProgramadasHastaAhora') || 0),
      }
    },

    // Piezas que el plan teórico esperaba producidas HASTA AHORA. La calcula el backend
    // (prorrateo por la línea de fechaConsulta) y la expone en avance.
    piezasProgramadasHastaAhora() {
      return this.avance.piezasProgramadasHastaAhora
    },

    // Modo del % de avance: true = "hasta ahora" (contra programadas hasta ahora),
    // false = "semana" (contra el objetivo total de la semana, como estaba).
    modoHastaAhora() {
      return (this.configReporte && this.configReporte.modoPorcentaje) === 'hastaAhora'
    },

    // Denominador del avance según el modo: programadas hasta ahora vs objetivo de la semana.
    denomAvance() {
      return this.modoHastaAhora ? this.piezasProgramadasHastaAhora : this.avance.piezasObjetivo
    },

    // % de avance sobre el denominador del modo (1 decimal, como el backend). La barra vive junto
    // a la fracción de la izquierda (contra el objetivo del periodo), así que ese es su
    // denominador; el modo solo decide el % que se anuncia.
    porcentajeAvance() {
      const d = this.denomAvance
      if (d <= 0) return 0
      return Math.round((this.avance.piezasOk / d) * 1000) / 10
    },

    // % de lo producido contra lo que el plan esperaba HASTA AHORA (piezas ok / programadas al
    // momento). Es el complemento del desfase que colorea la barra, así que comparte su color.
    porcentajeHastaAhora() {
      const d = this.piezasProgramadasHastaAhora
      if (d <= 0) return 0
      return Math.round((this.avance.piezasOk / d) * 1000) / 10
    },

    // Color del avance del periodo (barra + %), según las bandas configurables de avance.
    colorAvance() {
      return this.colorPorAvance(this.porcentajeAvance)
    },

    // Color del % de "programadas al momento". Mismas bandas, pero evaluadas con SU porcentaje:
    // cada indicador lleva su propio color aunque la configuración sea una sola.
    colorAvanceHastaAhora() {
      return this.colorPorAvance(this.porcentajeHastaAhora)
    },

    // Bandas para la leyenda: las que aplicó el backend (tablero.bandasDesfase); si no llegan,
    // las de la config local o las default.
    bandasLeyenda() {
      const delTablero = prop(this.tablero, 'bandasDesfase')
      const bandas = Array.isArray(delTablero) && delTablero.length
        ? delTablero
        : ((this.configReporte && this.configReporte.bandasDesfase) || getBandasDesfaseDefault())
      // "En meta" (primera banda, menor rango) siempre en azul claro, aunque el backend o la
      // config persistida traigan otro color. El resto de bandas se respetan tal cual.
      return bandas.map((b, i) => ({
        color: i === 0 ? BANDA_COLOR.verde : prop(b, 'color'),
        etiqueta: prop(b, 'etiqueta'),
        hasta: prop(b, 'hasta'),
      }))
    },

    altoGrafica() {
      // Alto reservado por máquina (fila). Menor = filas más juntas. Las barras se dibujan
      // como fracción de este alto (ver geometriaFila), así que también se ajustan solas.
      const n = this.filasTablero
      // En pantalla completa (modo "compacta"): llenar el alto disponible para que quepan TODAS las
      // máquinas sin scroll (las filas se comprimen solas). Piso por fila para no perder de vista los
      // datos: si hay tantas máquinas que no caben ni con el piso, se permite scroll legible.
      // En modo "original" se usa el alto fijo por fila de siempre (con scroll).
      const modoCompacto = !this.configReporte || this.configReporte.modoPantallaCompleta !== 'original'
      if (this.esFullscreen && modoCompacto && this.fsAltoDisponible > 0) {
        const MIN_FILA = 26
        return Math.max(n * MIN_FILA, this.fsAltoDisponible)
      }
      const ALTO_FILA = 44
      return Math.max(300, n * ALTO_FILA + 80)
    },

    // Ancho de la columna de nombres de máquina, ajustado al nombre más largo para que
    // ninguno se oculte pero sin dejar espacio muerto. Se usa como grid.left en ambos
    // gráficos (tablero y eje de fechas) para que sigan alineados.
    anchoColMaquina() {
      // COL_INSET a la izquierda del nombre + COL_AIRE entre el último dato y la línea del eje.
      // El margin de la etiqueta se deriva de aquí (anchoColMaquina - COL_INSET), así que este
      // aire es lo que impide que las cifras se encimen con la primera línea de la gráfica.
      return COL_INSET + this.anchoCorchete + this.anchoColNombre + this.anchoColEnCurso + COL_AIRE
    },

    // Hueco del corchete de grupo, entre el borde izquierdo y el nombre. Cero si no hay grupos.
    anchoCorchete() {
      const grupos = (this.datos && this.datos.grupos) || []
      return grupos.length ? ANCHO_CORCHETE : 0
    },

    // margin de la etiqueta del eje Y: la ancla a COL_INSET + corchete del borde izquierdo.
    margenEtiquetaEje() {
      return this.anchoColMaquina - COL_INSET - this.anchoCorchete
    },

    // Filas del eje Y: máquinas más un renglón por título de grupo.
    filasTablero() {
      return ((this.datos && this.datos.etiquetas) || []).length
    },

    // Ancho del nombre de la instancia, ajustado al más largo. El padding es solo la separación
    // con lo que venga a su derecha: el aire con el eje lo pone anchoColMaquina.
    anchoColNombre() {
      const et = ((this.datos && this.datos.etiquetas) || [])
        .filter((s) => !String(s || '').startsWith(GRUPO_PREFIJO))
        // El título de área entra con su nombre (va en letra más chica, así que sobra espacio).
        .map((s) => (String(s || '').startsWith(AREA_PREFIJO) ? String(s).slice(AREA_PREFIJO.length) : s))
      const maxLen = et.reduce((m, s) => Math.max(m, String(s || '').length), 0)
      // ~9.3px por carácter (fontSize 15, bold).
      return Math.min(188, Math.max(46, Math.round(maxLen * 9.3) + 10))
    },

    // Anchos (px) de las columnas del dato de la orden en curso: piezas ok, programadas y horas
    // faltantes, cada una ajustada a su valor más largo para que todas las instancias alineen.
    // Null cuando ninguna máquina tiene orden abierta: ahí la columna no existe.
    colsEnCurso() {
      const enCurso = (this.datos && this.datos.enCurso) || {}
      const claves = Object.keys(enCurso)
      if (!claves.length) return null
      // Medida carácter a carácter (fontSize 12, bold): los dígitos miden ~6.9px pero la coma y
      // el punto menos de la mitad, y contarlos igual inflaba la columna varios px por fila.
      // Todas las columnas se alinean a la derecha, así que el extra de cada una queda como aire
      // a su izquierda: en "ok" separa del nombre y en las otras dos, del "/" y del "·".
      const ancho = (campo, extra) => {
        const px = claves.reduce((m, k) => {
          const txt = String(enCurso[k][campo] || '')
          let w = 0
          for (let i = 0; i < txt.length; i++) w += (txt[i] === ',' || txt[i] === '.') ? 3.2 : 6.9
          return Math.max(m, w)
        }, 0)
        return Math.round(px) + extra
      }
      const ok = ancho('ok', 8)
      const prog = ancho('prog', 5)
      const hrs = ancho('hrs', 5)
      const sep = 9
      return { ok, prog, hrs, sep, total: ok + sep + prog + sep + hrs }
    },

    // Ancho total del bloque de la orden en curso dentro de la columna de instancias.
    anchoColEnCurso() {
      return this.colsEnCurso ? this.colsEnCurso.total : 0
    },

    // Todas las máquinas en producción que reporta /ReportesMoldes/MaquinasActivas, sin
    // restringir al tablero. Se ordenan según la posición de la máquina en la gráfica
    // (por nombre, sin distinción de mayúsculas/espacios) y las que no figuran en el
    // tablero quedan al final, pero ninguna se descarta.
    maquinasActivasFiltradas() {
      const orden = new Map()
      this.maquinasFiltradas.forEach((m, i) => { 
        const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
        if (nombre && !orden.has(nombre)) orden.set(nombre, i)
      })
      const pos = (m) => {
        const i = orden.get(String(prop(m, 'maquina') || '').trim().toUpperCase())
        return i === undefined ? Number.MAX_SAFE_INTEGER : i
      }
      return (this.maquinasActivasApi || []).slice().sort((a, b) => pos(a) - pos(b))
    },

    // KPI "Máquinas activas": activas = máquinas en producción (ya restringidas a los filtros);
    // total = instancias que, según el plan, deberían tener una ODT abierta ahora. Este
    // denominador lo calcula el backend (maquinasDebenProducir): máquinas cuyo plan teórico
    // cruza la línea de "ahora".
    maquinasActivas() {
      const total = Number(prop(this.tablero, 'maquinasDebenProducir') || 0)
      return { activas: this.maquinasActivasFiltradas.length, total }
    },

    // Resumen por área para la card "Maq. Activas": una columna por área del catálogo con
    // activas / esperadas. Activas = máquinas del endpoint MaquinasActivas agrupadas por idArea;
    // esperadas = máquinas cuya barra teórica cruza "ahora", agrupadas por el nombre de área de
    // esa barra. Una OT que ya terminó deja de contar desde la hora en que terminó (fin efectivo,
    // igual que maquinasDebenProducir del backend). El catálogo ya viene sin "SIN DESIGNAR"
    // (omitida en el backend).
    resumenPorArea() {
      const activasPorId = {}
        ; (this.maquinasActivasFiltradas || []).forEach((m) => {
          const id = prop(m, 'idArea')
          if (id == null) return
          activasPorId[id] = (activasPorId[id] || 0) + 1
        })

      const ahora = toMs(prop(this.tablero, 'fechaConsulta')) || Date.now()
      const esperadasPorNombre = {}
        ; (this.maquinasFiltradas || []).forEach((m) => {
          const cruza = (prop(m, 'barrasTeoricas') || []).find((b) => {
            const ini = toMs(prop(b, 'inicio'))
            const fin = toMs(finTeorica(b))
            return !Number.isNaN(ini) && !Number.isNaN(fin) && ahora >= ini && ahora < fin
          })
          if (!cruza) return
          const nombre = String(prop(cruza, 'area') || '').trim().toUpperCase()
          if (!nombre) return
          esperadasPorNombre[nombre] = (esperadasPorNombre[nombre] || 0) + 1
        })

      // Si hay áreas seleccionadas en el filtro, solo se muestran esas columnas; si no, todas.
      const sel = (this.filtros && this.filtros.idsArea) || []
      const areas = sel.length
        ? this.catAreas.filter((a) => sel.includes(a.value))
        : this.catAreas

      return areas.map((a) => ({
        id: a.value,
        nombre: a.text,
        activas: activasPorId[a.value] || 0,
        esperadas: esperadasPorNombre[String(a.text).trim().toUpperCase()] || 0,
      }))
    },

    // Índice de las máquinas activas (misma lógica que r61): por idMaquinaIns cuando el back lo
    // resolvió (token -> instancia) y, si no, por nombre normalizado. "El contador manda": lo que
    // esté aquí es Activa aunque ops tenga un paro abierto.
    indiceActivas() {
      const porId = new Set()
      const porNombre = new Set()
        ; (this.maquinasActivasApi || []).forEach((m) => {
          const id = Number(prop(m, 'idMaquinaIns'))
          const nombre = String(prop(m, 'maquina') || '').trim().toUpperCase()
          if (Number.isFinite(id) && id > 0) porId.add(id)
          else if (nombre) porNombre.add(nombre)
        })
      return { porId, porNombre }
    },

    // Instancias en paro NO programado según ops que el contador NO reporta activas. Es la misma
    // regla que la pestaña "Paros No Prog." del reporte de estado (statusEstado + contador manda),
    // para que los nombres en rojo de aquí coincidan con esa pestaña.
    instanciasParoNoProg() {
      return this.instanciasEnParo(4)
    },

    // Instancias en paro programado según ops que el contador NO reporta activas: status 3, la
    // pestaña "Cambio de molde" (renombrable) del reporte de estado. Van en amarillo en el eje.
    instanciasParoProg() {
      return this.instanciasEnParo(3)
    },

    // Instancias con OT abierta. Fuente principal: TieneOperacionAbierta de EstadoMaquinasTiempoReal
    // (la misma que usa el reporte de estado para el distintivo "Activa sin OT"). Si el estado aún
    // no llegó, se cae al criterio anterior: al menos una barra real en el tablero. Se cruza por
    // idMaquinaIns y por nombre normalizado (el idMaquinaIns de las activas puede no venir resuelto).
    instanciasConOt() {
      const ids = new Set()
      const nombres = new Set()
      const estado = this.estadoMaquinasApi || []
      if (estado.length) {
        estado.forEach((e) => {
          if (!esTrue(prop(e, 'tieneOperacionAbierta'))) return
          const id = Number(prop(e, 'idMaquinaIns'))
          const nombre = String(prop(e, 'nombreInstancia') || '').trim().toUpperCase()
          if (Number.isFinite(id) && id > 0) ids.add(id)
          if (nombre) nombres.add(nombre)
        })
        return { ids, nombres }
      }
      this.maquinasFiltradas.forEach((m) => {
        const tieneOt = (prop(m, 'barrasReales') || []).length > 0
        if (!tieneOt) return
        const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
        const nombre = String(prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || '').trim().toUpperCase()
        if (Number.isFinite(id) && id > 0) ids.add(id)
        if (nombre) nombres.add(nombre)
      })
      return { ids, nombres }
    },

    // Área de cada instancia según el estado en tiempo real: EstadoMaquinasTiempoReal trae
    // nombreArea por instancia, tenga o no plan y esté activa o no. Índices por idMaquinaIns y por
    // nombre normalizado, como instanciasConOt. Es el respaldo de areaDeMaquina cuando el tablero
    // no manda el área (backend anterior a TableroMaquinaV4ViewModel.Area).
    areasPorInstancia() {
      const porId = new Map()
      const porNombre = new Map()
        ; (this.estadoMaquinasApi || []).forEach((e) => {
          const area = String(prop(e, 'nombreArea') || '').trim()
          if (!area) return
          const id = Number(prop(e, 'idMaquinaIns'))
          const nombre = String(prop(e, 'nombreInstancia') || '').trim().toUpperCase()
          if (Number.isFinite(id) && id > 0) porId.set(id, area)
          if (nombre) porNombre.set(nombre, area)
        })
      return { porId, porNombre }
    },

    // Molde de la operación abierta de cada instancia según el estado en tiempo real (primer
    // molde de `moldes` cuando tieneOperacionAbierta). Índices por idMaquinaIns y por nombre
    // normalizado, como instanciasConOt. Lo usa moldeAbiertoDeMaquina para el diálogo de activas.
    moldesAbiertosPorInstancia() {
      const porId = new Map()
      const porNombre = new Map()
        ; (this.estadoMaquinasApi || []).forEach((e) => {
          if (!esTrue(prop(e, 'tieneOperacionAbierta'))) return
          const molde = String(prop((prop(e, 'moldes') || [])[0] || {}, 'nombreMolde') || '').trim()
          if (!molde) return
          const id = Number(prop(e, 'idMaquinaIns'))
          const nombre = String(prop(e, 'nombreInstancia') || '').trim().toUpperCase()
          if (Number.isFinite(id) && id > 0) porId.set(id, molde)
          if (nombre) porNombre.set(nombre, molde)
        })
      return { porId, porNombre }
    },

    // Órdenes con operación abierta en cada instancia según el estado en tiempo real
    // (idTrabajoOrden de operacionesAbiertas). Índices por idMaquinaIns y por nombre normalizado,
    // como instanciasConOt. Lo usa otsAbiertasDeMaquina para saber qué orden corre cada máquina.
    otsAbiertasPorInstancia() {
      const porId = new Map()
      const porNombre = new Map()
        ; (this.estadoMaquinasApi || []).forEach((e) => {
          const ots = new Set()
            ; (prop(e, 'operacionesAbiertas') || []).forEach((o) => {
              const idOt = Number(prop(o, 'idTrabajoOrden'))
              if (Number.isFinite(idOt) && idOt > 0) ots.add(idOt)
            })
          if (!ots.size) return
          const id = Number(prop(e, 'idMaquinaIns'))
          const nombre = String(prop(e, 'nombreInstancia') || '').trim().toUpperCase()
          if (Number.isFinite(id) && id > 0) porId.set(id, ots)
          if (nombre) porNombre.set(nombre, ots)
        })
      return { porId, porNombre }
    },

    // Detalle del KPI de máquinas activas (diálogo), sobre las máquinas del tablero ya filtrado:
    //  - conPlan: activas según el contador que están corriendo la orden PROGRAMADA PARA AHORA:
    //    alguna de sus órdenes abiertas (otsAbiertasDeMaquina) tiene fragmento teórico en la
    //    máquina que cruza "ahora" (inicio <= ahora < fin, el criterio de maquinasDebenProducir).
    //    Se indican los moldes de sus órdenes programadas; si además corre otra desfasada, la
    //    máquina cuenta una sola vez, aquí.
    //  - desfasado: activas según el contador que corren una orden programada pero NO para este
    //    momento: tienen fragmento teórico en la máquina y ninguno cruza "ahora". `desfase` dice
    //    el caso: 'atrasada' (el fragmento ya terminó, fin <= ahora) o 'adelantada' (aún no
    //    empieza, inicio > ahora); con órdenes en los dos casos gana atrasada. Un fragmento sin
    //    fechas no tiene contra qué desfasarse y se queda en conPlan.
    //  - sinPlan: activas según el contador que NO están corriendo una orden programada: sin
    //    operación abierta, o con una orden abierta que no tiene fragmento teórico en la máquina.
    //    Con OT abierta se indica su molde.
    //  - sinArrancar: con al menos un fragmento teórico que ya debió arrancar (inicio <= ahora,
    //    en curso o ya vencido) y cuya OT no se ha iniciado ni finalizado (la misma marca que usa
    //    sinOrden en datos()). Se indican los moldes pendientes, del más atrasado al más
    //    reciente. No mira el contador ni la orden abierta: una máquina que corre OTRA orden
    //    también entra si tiene una programada sin arrancar. `vencida` = ninguno de sus
    //    fragmentos pendientes cruza "ahora" (todos ya pasaron); el resumen las cuenta aparte
    //    ("de ahora + vencidas", ver tabsActivas). Una máquina con un pendiente de ahora y otro
    //    vencido cuenta como de ahora.
    //  - sinProducir: programadas para ahora cuya OT ya arrancó o ya terminó y no está
    //    produciendo: algún fragmento teórico cruza "ahora" con OT iniciada o finalizada, en una
    //    máquina que no quedó en conPlan ni tiene otro fragmento de ahora sin arrancar (ese caso
    //    ya cuenta en sinArrancar). `estado` por máquina. Con operación abierta en la máquina y el
    //    contador sin reportarla activa: 'paroNoProg' o 'paroProg' según el paro abierto en ops
    //    (esParoNoProgramado / esParoProgramado, los mismos índices que pintan el eje) o
    //    'sinActividad' si no hay paro registrado. Sin operación abierta aquí: 'pausada' (OT
    //    iniciada) o 'finalizada' (OT finalizada que aún tiene operación abierta en alguna máquina:
    //    sin operación abierta su fragmento ya no cruza "ahora", ver finTeorica). Con varios
    //    fragmentos manda ese mismo orden y
    //    se indica un solo molde, el del primer fragmento con ese estado.
    //    Con esto cada máquina esperada cae en una sola de conPlan, sinArrancar de ahora o
    //    sinProducir, y las tres suman las esperadas (salvo dos OT programadas a la vez en la
    //    misma máquina con una corriendo y otra sin arrancar).
    // "Activa" es el criterio del contador (esMaquinaActiva), el mismo de las tarjetas y de la
    // vista por conjunto. "Programada" se evalúa contra el tablero consultado: una orden de otro
    // periodo no trae fragmento teórico y cuenta como sin programa. Las tarjetas (esperadas,
    // maquinasDebenProducir) siguen con el criterio "cruza ahora"; este detalle lo usa para
    // separar conPlan de desfasado, las vencidas de sinArrancar y armar sinProducir. En todos el
    // fin de un fragmento teórico es su fin efectivo (finTeorica): la OT terminada sale a la hora
    // en que terminó.
    detalleActivas() {
      const ahora = toMs(prop(this.tablero, 'fechaConsulta')) || Date.now()
      const conPlan = []
      const desfasado = []
      const sinPlan = []
      const sinArrancar = []
      const sinProducir = []
      const sinArrancarB = (b) => prop(b, 'iniciada') !== true && prop(b, 'finalizada') !== true
      // "Ahora" cae dentro del fragmento (inicio <= ahora < fin), ya terminó (fin <= ahora) o
      // todavía no empieza (inicio > ahora). Sin la fecha necesaria, ninguna aplica.
      const cruzaAhora = (b) => {
        const ini = toMs(prop(b, 'inicio'))
        const fin = toMs(finTeorica(b))
        return Number.isFinite(ini) && Number.isFinite(fin) && ini <= ahora && ahora < fin
      }
      const yaTermino = (b) => {
        const fin = toMs(finTeorica(b))
        return Number.isFinite(fin) && fin <= ahora
      }
      const noEmpieza = (b) => {
        const ini = toMs(prop(b, 'inicio'))
        return Number.isFinite(ini) && ini > ahora
      }
      // Moldes distintos de una lista de barras, en su orden; '—' si ninguna lo trae.
      const moldesDe = (barras) => {
        const nombres = barras.map((b) => String(prop(b, 'molde') || prop(b, 'clave') || '').trim())
        return Array.from(new Set(nombres.filter(Boolean))).join(', ') || '—'
      }
      // Con un área elegida en la tarjeta, solo sus máquinas (mismo nombre de área que las
      // columnas del KPI; ver areaDeMaquina).
      const areaSel = this.dialogActivasArea ? String(this.dialogActivasArea.nombre || '').trim().toUpperCase() : ''
      this.maquinasFiltradas.forEach((m) => {
        if (areaSel && this.areaDeMaquina(m).trim().toUpperCase() !== areaSel) return
        const teoricas = prop(m, 'barrasTeoricas') || []
        const maquina = prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || `#${prop(m, 'idMaquina')}`
        const otsAbiertas = this.otsAbiertasDeMaquina(m, ahora)
        const enCurso = teoricas.filter((b) => otsAbiertas.has(Number(prop(b, 'idTrabajoOrden'))))
        let enConPlan = false
        if (this.esMaquinaActiva(m)) {
          if (!enCurso.length) {
            // Sin programa pero con OT abierta: se muestra el molde de esa OT (del estado en tiempo
            // real o, si no lo trae, de la barra real en curso).
            sinPlan.push({ maquina, molde: this.tieneOtAbierta(m) ? this.moldeAbiertoDeMaquina(m, ahora) : '' })
          } else if (enCurso.some(cruzaAhora)) {
            conPlan.push({ maquina, molde: moldesDe(enCurso) })
            enConPlan = true
          } else {
            const desfase = enCurso.some(yaTermino) ? 'atrasada' : enCurso.some(noEmpieza) ? 'adelantada' : null
            if (desfase) {
              desfasado.push({ maquina, molde: moldesDe(enCurso), desfase })
            } else {
              conPlan.push({ maquina, molde: moldesDe(enCurso) })
              enConPlan = true
            }
          }
        }
        const pendientes = teoricas
          .filter((b) => {
            if (!sinArrancarB(b)) return false
            const ini = toMs(prop(b, 'inicio'))
            return Number.isFinite(ini) && ini <= ahora
          })
          .sort((a, b) => toMs(prop(a, 'inicio')) - toMs(prop(b, 'inicio')))
        const pendienteDeAhora = pendientes.some(cruzaAhora)
        if (pendientes.length) sinArrancar.push({ maquina, molde: moldesDe(pendientes), vencida: !pendienteDeAhora })
        if (!enConPlan && !pendienteDeAhora) {
          const deAhora = teoricas.filter((b) => cruzaAhora(b) && !sinArrancarB(b))
          if (deAhora.length) {
            // El tipo de paro es de la máquina, no del fragmento: todos los fragmentos con
            // operación abierta comparten ese estado.
            const abiertaSinActividad = this.esParoNoProgramado(m) ? 'paroNoProg'
              : this.esParoProgramado(m) ? 'paroProg' : 'sinActividad'
            const estados = deAhora.map((b) => (prop(b, 'finalizada') === true
              ? 'finalizada'
              : otsAbiertas.has(Number(prop(b, 'idTrabajoOrden'))) ? abiertaSinActividad : 'pausada'))
            const estado = ['paroNoProg', 'paroProg', 'sinActividad', 'pausada', 'finalizada']
              .find((e) => estados.includes(e))
            // Un solo molde: el del primer fragmento con ese estado.
            sinProducir.push({ maquina, molde: moldesDe([deAhora[estados.indexOf(estado)]]), estado })
          }
        }
      })
      return { conPlan, desfasado, sinPlan, sinArrancar, sinProducir }
    },

    // Pestañas del diálogo de máquinas activas: título, lista, texto de lista vacía y, en
    // "Prog sin arrancar", el tono ámbar que pinta su cuadro del resumen y sus nombres, más el
    // conteo "de ahora + vencidas" que sustituye al total en el resumen. Cada fila puede llevar
    // un chip entre el nombre y el molde (texto + tono, ver .act-chip): en "desfasado" el caso
    // (atrasada/adelantada), en "sin arrancar" solo las vencidas (las de ahora van sin chip) y en
    // "sin producir" el estado (paro no prog. / paro prog. / sin actividad / pausada / finalizada).
    // Esta última pestaña va al final y solo si la config la enciende (mostrarProgSinProducir).
    tabsActivas() {
      const d = this.detalleActivas
      const t = (k) => this.$t(`Reports.ProductionPlan.activeDialog.${k}`)
      const conChip = (items, chipDe) => items.map((it) => ({ ...it, chip: chipDe(it) }))
      const desfasado = conChip(d.desfasado, (it) => (it.desfase === 'atrasada'
        ? { texto: t('chipBehind'), tono: 'atrasada' }
        : { texto: t('chipAhead'), tono: 'adelantada' }))
      const sinArrancar = conChip(d.sinArrancar, (it) => (it.vencida ? { texto: t('chipOverdue'), tono: 'vencida' } : null))
      const vencidas = d.sinArrancar.filter((it) => it.vencida).length
      const CHIP_ESTADO = {
        paroNoProg: 'chipUnplannedStop',
        paroProg: 'chipPlannedStop',
        sinActividad: 'chipNoActivity',
        pausada: 'chipPaused',
        finalizada: 'chipFinished',
      }
      const sinProducir = conChip(d.sinProducir, (it) => ({ texto: t(CHIP_ESTADO[it.estado]), tono: it.estado }))
      const tabs = [
        { clave: 'conPlan', titulo: t('tabActiveWithPlan'), vacio: t('emptyActiveWithPlan'), items: d.conPlan },
        { clave: 'desfasado', titulo: t('tabActiveShifted'), vacio: t('emptyActiveShifted'), items: desfasado },
        { clave: 'sinPlan', titulo: t('tabActiveNoPlan'), vacio: t('emptyActiveNoPlan'), items: d.sinPlan },
        {
          clave: 'sinArrancar',
          titulo: t('tabScheduledNotStarted'),
          vacio: t('emptyScheduledNotStarted'),
          items: sinArrancar,
          conteo: `${d.sinArrancar.length - vencidas} + ${vencidas}`,
          tono: 'ambar',
        },
      ]
      if (this.mostrarProgSinProducir) {
        tabs.push({
          clave: 'sinProducir',
          titulo: t('tabScheduledNotProducing'),
          vacio: t('emptyScheduledNotProducing'),
          items: sinProducir,
        })
      }
      return tabs
    },

    restanteTexto() {
      const s = Math.max(0, this.restante)
      const m = Math.floor(s / 60)
      return `${m}:${String(s % 60).padStart(2, '0')}`
    },

    // Opciones del selector de molde destino (cambio de instancia).
    opcionesMolde() {
      return (this.destinos || []).map((d) => ({
        value: prop(d, 'idMolde'),
        text: `${prop(d, 'codigoMolde') || 'Molde'} · ${prop(d, 'modeloMaquina') || ''}`.trim(),
      }))
    },

    // Instancias del molde destino seleccionado.
    opcionesInstancia() {
      const d = (this.destinos || []).find((x) => prop(x, 'idMolde') === this.destinoMoldeSel)
      if (!d) return []
      return (prop(d, 'instancias') || []).map((i) => {
        const tk = Number(prop(i, 'tokensFisicos') || 0)
        const nombre = prop(i, 'nombre') || `#${prop(i, 'idMaquinaIns')}`
        const modelo = prop(i, 'modeloMaquina') || ''
        const requiereClonar = prop(i, 'requiereClonarMolde') === true
        const partes = [nombre, modelo].filter(Boolean)
        if (requiereClonar) partes.push(this.$t('Reports.ProductionPlan.changeInstance.moldWillBeCreated'))
        return {
          value: prop(i, 'idMaquinaIns'),
          // text: tk > 0 ? `${nombre} · ${tk} token(s)` : nombre,
          text: partes.join(' · '),
          tokensFisicos: tk,
          requiereClonarMolde: requiereClonar,
        }
      })
    },

    opcionesMoldeReabrir() {
      return (this.reabrirDestinos || []).map((d) => {
        const articulos = (prop(d, 'articulos') || []).map((a) => {
          const codigo = prop(a, 'codigo') || prop(a, 'nombre') || '—'
          const cavidades = Number(prop(a, 'cavidadesActivas') || 0)
          return `${codigo} (${cavidades} ${this.$t('Reports.ProductionPlan.reopen.cavitiesAbbr')})`
        })
        const instanciasLigadas = (prop(d, 'instancias') || [])
          .filter((i) => prop(i, 'requiereClonarMolde') !== true)
          .map((i) => prop(i, 'nombre') || `#${prop(i, 'idMaquinaIns')}`)
        const instancia = instanciasLigadas.length
          ? instanciasLigadas.join(', ')
          : this.$t('Reports.ProductionPlan.reopen.noLinkedInstance')
        const partes = [
          prop(d, 'codigoMolde') || 'Molde',
          articulos.length ? articulos.join(', ') : null,
          `${Number(prop(d, 'numCavidades') || 0)} ${this.$t('Reports.ProductionPlan.reopen.totalCavities')}`,
          `${this.$t('Reports.ProductionPlan.reopen.linkedInstance')}: ${instancia}`,
        ].filter(Boolean)
        return { value: prop(d, 'idMolde'), text: partes.join(' · ') }
      })
    },

    opcionesInstanciaReabrir() {
      const d = (this.reabrirDestinos || []).find((x) => prop(x, 'idMolde') === this.reabrirMoldeSel)
      if (!d) return []
      return (prop(d, 'instancias') || []).map((i) => {
        const nombre = prop(i, 'nombre') || `#${prop(i, 'idMaquinaIns')}`
        const modelo = prop(i, 'modeloMaquina') || ''
        const partes = [nombre, modelo].filter(Boolean)
        if (prop(i, 'requiereClonarMolde') === true) {
          partes.push(this.$t('Reports.ProductionPlan.changeInstance.moldWillBeCreated'))
        }
        return { value: prop(i, 'idMaquinaIns'), text: partes.join(' · ') }
      })
    },

    // Las cantidades ya no se capturan: el backend reabre con la cantidad original de cada detalle.
    reaperturaValida() {
      const fechaValida = this.reabrirMismaFechaInicio || !!this.reabrirFechaInicio
      return !!this.reabrirMoldeSel && !!this.reabrirInstanciaSel && fechaValida && this.reabrirDetalles.length > 0
    },

    fechaInicioOriginalReaperturaTexto() {
      return this.reabrirFechaInicioOriginal
        ? fmtFechaHora(this.reabrirFechaInicioOriginal, this.$i18n.locale)
        : '—'
    },

    // Habilita el guardado del nuevo TC: todas las filas con un TC entero positivo.
    tcValido() {
      return this.tcOperaciones.length > 0 &&
        this.tcOperaciones.every((o) => Number(o.tc) > 0)
    },

    piezasValidas() {
      return this.piezasOperaciones.length > 0 &&
        this.piezasOperaciones.every((o) => Number.isInteger(Number(o.cantidad)) && Number(o.cantidad) > 0)
    },

    // Etiqueta del tipo de fragmento (real ajustado vs plan teórico), reactiva al idioma.
    tipoLabel() {
      if (this.fragmento.esReal == null) return ''
      return this.fragmento.esReal
        ? this.$t('Reports.ProductionPlan.detail.realAdjusted')
        : this.$t('Reports.ProductionPlan.detail.theoreticalPlan')
    },

    // Texto de la banda de desfase. Usa la etiqueta configurable que manda el backend;
    // si no hay (datos antiguos), cae al texto i18n por clave.
    bandaLabel() {
      if (this.fragmento.bandaEtiqueta) return this.fragmento.bandaEtiqueta
      const map = { verde: 'onTarget', amarillo: 'off1to10', naranja: 'off10to15', rojo: 'offOver15' }
      const k = map[this.fragmento.bandaKey]
      return k ? this.$t('Reports.ProductionPlan.band.' + k) : ''
    },

    // Desfase en porcentaje del MODO APLICADO por el backend (tiempo o piezas), para que el
    // número coincida con el color/banda de la barra.
    desfasePctTexto() {
      const pct = this.modoDesfaseAplicado === 'piezas'
        ? Number(this.fragmento.desfasePiezasPct || 0)
        : Number(this.fragmento.desfasePct || 0)
      return pct <= 0 ? this.$t('Reports.ProductionPlan.band.onTarget') : `${pct.toFixed(1)}%`
    },

    // Desfase de TIEMPO expresado como duración (cuánto más tarde termina que el fin programado).
    desfaseTiempo() {
      const seg = Number(this.fragmento.desfaseSeg || 0)
      return seg <= 0 ? this.$t('Reports.ProductionPlan.band.onTarget') : fmtDuracion(seg)
    },

    // Desfase por PIEZAS: cuántas faltan respecto a las programadas hasta ahora.
    desfasePiezasTexto() {
      const pz = Number(this.fragmento.desfasePiezas || 0)
      return pz <= 0
        ? this.$t('Reports.ProductionPlan.band.onTarget')
        : `${fmtNum(pz)} ${this.$t('Reports.ProductionPlan.summary.pieces')}`
    },

    datos() {
      const maquinas = this.maquinasFiltradas
      const etiquetaDe = (m) => prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || `#${prop(m, 'idMaquina')}`
      // Etiqueta de cada máquina en el orden del backend. Los conjuntos de abajo (rojas, enCurso,
      // sinOrden, meta) se indexan por etiqueta, así que no dependen del orden de los carriles.
      const etiquetas = maquinas.map(etiquetaDe)
      // Carriles del eje Y: máquinas reordenadas por grupo más una categoría fantasma por título
      // (de conjunto o de área). Las series (plan, real, jornada, corchetes) se indexan por
      // posición en este arreglo.
      const { carriles, grupos } = this.agruparCarriles(maquinas)
      const etiquetasEje = carriles.map((c) => {
        if (c.maquina) return etiquetaDe(c.maquina)
        return c.tituloArea != null ? AREA_PREFIJO + c.tituloArea : GRUPO_PREFIJO + c.titulo
      })

      // Etiquetas a colorear en el eje: rojo = paro no programado, amarillo = paro programado, en
      // ambos casos solo si el contador no reporta la instancia activa (ver esParoNoProgramado /
      // esParoProgramado). El rojo tiene prioridad.
      const rojas = new Set()
      const amarillas = new Set()
      maquinas.forEach((m, i) => {
        if (this.esParoNoProgramado(m)) rojas.add(etiquetas[i])
        else if (this.esParoProgramado(m)) amarillas.add(etiquetas[i])
      })

      // Orden EN CURSO de cada máquina: piezas ok / programadas y horas que le faltan según el
      // fin estimado de su barra real. Solo las máquinas que tienen una, y siempre con los datos
      // de esa orden (no acumulados del periodo).
      // El tablero V4 no manda una marca "en curso" por barra, así que se resuelve con las dos
      // señales que el reporte ya usa: la instancia tiene operación ABIERTA según
      // EstadoMaquinasTiempoReal (instanciasConOt) y su barra real arrancada —ni finalizada ni
      // pronóstico— cruza la línea de "ahora". El backend estira el fin de la barra en curso hasta
      // el corte, así que esa es justo la que contiene el instante actual. Se lee del payload y no
      // de las barras dibujadas, para que también aplique en la vista teórica.
      const ahoraMs = Date.now()
      const conOt = this.instanciasConOt
      const enCurso = {}
      maquinas.forEach((m, i) => {
        const idIns = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
        const nombreIns = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
        const tieneAbierta = (Number.isFinite(idIns) && conOt.ids.has(idIns))
          || (!!nombreIns && conOt.nombres.has(nombreIns))
        if (!tieneAbierta) return
        const barra = (prop(m, 'barrasReales') || []).find((b) => {
          if (prop(b, 'iniciada') !== true || prop(b, 'finalizada') === true) return false
          const ini = toMs(prop(b, 'inicio'))
          const fin = toMs(prop(b, 'fin'))
          return Number.isFinite(ini) && Number.isFinite(fin) && ini <= ahoraMs && ahoraMs < fin
        })
        if (!barra) return
        const ok = Number(prop(barra, 'piezasOk') || 0)
        const obj = Number(prop(barra, 'piezasObjetivo') || 0)
        const horas = this.horasFaltantesBarra(barra)
        // Cada dato va por separado: la etiqueta del eje los pinta en columnas de ancho fijo
        // para que ok, programadas y horas queden alineadas entre instancias.
        enCurso[etiquetas[i]] = {
          ok: fmtNum(ok),
          prog: fmtNum(obj),
          hrs: horas == null ? '' : horas.toFixed(1),
        }
      })

      // Etiquetas con orden programada SIN ARRANCAR según el criterio configurado (ver
      // tieneOrdenSinArrancar). El color lo pone la config (rojo por defecto) y, si la instancia
      // además está en paro, el color del paro tiene prioridad (ver formatterNombreEje).
      const sinOrden = new Set()
      maquinas.forEach((m, i) => {
        if (this.tieneOrdenSinArrancar(m, ahoraMs)) sinOrden.add(etiquetas[i])
      })

      const meta = {}
      maquinas.forEach((m, i) => {
        meta[etiquetas[i]] = {
          molde: prop(m, 'molde'),
          avanceSeg: Number(prop(m, 'segundosAvance') || 0),
          faltanteSeg: Number(prop(m, 'segundosFaltante') || 0),
          tcProgramado: prop(m, 'tcProgramado'),
          tcReal: prop(m, 'tcReal'),
          tcEsperado: prop(m, 'tcEsperado'),
        }
      })

      const plan = []
      const real = []
      // Carriles de máquinas sin plan: el backend las devuelve sin barras de ningún tipo. Se les
      // dibuja una sola barra gris con el horario de la empresa (ver serie 'Jornada').
      const idxSinPlan = []
      let minMs = Infinity
      let maxMs = -Infinity

      carriles.forEach((c, idx) => {
        const m = c.maquina
        if (!m) return // título de grupo: sin barras
        const barrasPlan = prop(m, 'barrasTeoricas') || []
        const barrasReal = prop(m, 'barrasReales') || []
        if (barrasPlan.length === 0 && barrasReal.length === 0) idxSinPlan.push(idx)

        barrasPlan.forEach((b) => {
          // La barra se dibuja hasta su fin efectivo: si la OT terminó antes de su fin programado
          // se recorta a la hora en que terminó, y si terminó antes de su inicio programado no se
          // dibuja (largo cero). Su duración programada viaja aparte (índice 6) para los textos.
          const ini = toMs(prop(b, 'inicio'))
          const fin = toMs(finTeorica(b))
          if (Number.isNaN(ini) || Number.isNaN(fin) || fin <= ini) return
          minMs = Math.min(minMs, ini)
          maxMs = Math.max(maxMs, fin)
          const finPlan = toMs(prop(b, 'fin'))
          plan.push({
            value: [
              idx,
              ini,
              fin,
              prop(b, 'clave') || '',
              Number(prop(b, 'piezasObjetivo') || 0),
              prop(b, 'molde') || '',
              Number.isFinite(finPlan) ? finPlan - ini : fin - ini,
            ],
            raw: b,
          })
        })

        // ODT críticas de este carril (TrabajoOrden.OTCritica). El fragmento real no trae la
        // marca, así que la hereda de su barra teórica: el backend garantiza que toda barra real
        // tiene su teórica en la MISMA máquina (las teóricas del carril salen de las ODT del grupo).
        const odtCriticas = new Set()
        barrasPlan.forEach((b) => {
          if (prop(b, 'otCritica')) odtCriticas.add(Number(prop(b, 'idTrabajoOrden')))
        })

        if (this.vista === 'tiempoReal') {
          const cfg = this.configReporte || {}
          const ocultarNoIniciadas = !!cfg.ocultarRealesNoIniciadas
          const dejarSiguiente = ocultarNoIniciadas && !!cfg.dejarSiguienteProgramada
          // Pronóstico puro: la OT no ha arrancado (sin producción real) ni está finalizada.
          const esPronostico = (b) => prop(b, 'iniciada') !== true && prop(b, 'finalizada') !== true
          // "Siguiente orden programada" de la máquina: el pronóstico con inicio más próximo
          // (el backend ya los encadena tras la producción real). Se conserva aunque se oculten
          // los demás pronósticos.
          let siguiente = null
          if (dejarSiguiente) {
            barrasReal.forEach((b) => {
              if (!esPronostico(b)) return
              const ini = toMs(prop(b, 'inicio'))
              if (Number.isNaN(ini)) return
              if (!siguiente || ini < siguiente.ini) siguiente = { b, ini }
            })
          }
          barrasReal.forEach((b) => {
            // Con la config activa los pronósticos no se dibujan (queda solo la barra teórica),
            // salvo el de la siguiente OT a arrancar si así se configuró.
            if (ocultarNoIniciadas && esPronostico(b) && !(siguiente && siguiente.b === b)) return
            const ini = toMs(prop(b, 'inicio'))
            const fin = toMs(prop(b, 'fin'))
            if (Number.isNaN(ini) || Number.isNaN(fin) || fin <= ini) return
            minMs = Math.min(minMs, ini)
            maxMs = Math.max(maxMs, fin)
            const ok = Number(prop(b, 'piezasOk') || 0)
            const obj = Number(prop(b, 'piezasObjetivo') || 0)
            real.push({
              value: [
                idx,
                ini,
                fin,
                prop(b, 'clave') || '',
                ok,
                obj,
                colorBanda(ok, obj, prop(b, 'color')),
                prop(b, 'enCurso') ? 1 : 0,
                prop(b, 'molde') || '',
                prop(b, 'finalizada') ? 1 : 0,
                odtCriticas.has(Number(prop(b, 'idTrabajoOrden'))) ? 1 : 0,
              ],
              raw: b,
            })
          })
        }
      })

      // Sin zoom, el dominio debe representar el periodo solicitado completo y no solamente
      // el tramo donde existen barras. Así los dos extremos del eje coinciden con Inicio/Fin
      // (p. ej. domingo → domingo), aunque no haya producción en alguno de esos días.
      const inicioFiltroMs = toMs(this.filtroAIso(this.filtros.inicio))
      const finFiltroMs = toMs(this.filtroAIso(this.filtros.fin))
      const rangoFiltroValido = Number.isFinite(inicioFiltroMs) &&
        Number.isFinite(finFiltroMs) && finFiltroMs > inicioFiltroMs
      // Fin de las barras dibujadas (antes de pisar el dominio con el rango del filtro).
      const finDatosMs = maxMs

      let excedeJornada = false
      if (rangoFiltroValido) {
        minMs = inicioFiltroMs
        // El eje NO se topa en el Fin del filtro (7×24 h exactas en el modo semana): corta en el
        // fin de la jornada de la empresa. Los filtros no se tocan: esto es solo el dominio del
        // gráfico. Sin horario leído el corte cae en el Fin del filtro, como antes.
        const finJornadaMs = this.finJornadaCercano(finFiltroMs)
        maxMs = Number.isFinite(finJornadaMs) ? finJornadaMs : finFiltroMs
        // Barras que se pasan del corte: habilitan el check "Mostrar todo" de la toolbar. Con el
        // check activo el eje crece hasta la última barra; apagado, esas barras quedan recortadas.
        excedeJornada = Number.isFinite(finDatosMs) && finDatosMs > maxMs
        if (excedeJornada && this.expandirFueraJornada) maxMs = finDatosMs
        // Red de seguridad: si el horario dejara un fin antes del inicio, manda el filtro.
        if (!(maxMs > minMs)) maxMs = finFiltroMs
      } else {
        // Fallback para estados transitorios del picker o respuestas sin fechas utilizables.
        if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) {
          const ahora = Date.now()
          minMs = ahora - 12 * 3600 * 1000
          maxMs = ahora + 12 * 3600 * 1000
        } else {
          const margen = 60 * 60 * 1000
          minMs -= margen
          maxMs += margen
        }
      }

      // Barra de jornada de las máquinas sin plan, ya con el dominio del eje resuelto.
      const ventana = idxSinPlan.length ? this.ventanaJornada(minMs, maxMs) : null
      const jornada = ventana
        ? idxSinPlan.map((idx) => ({ value: [idx, ventana.desde, ventana.hasta] }))
        : []

      // Corchete por grupo: del renglón del título al último carril de sus máquinas; o, con los
      // conjuntos en su lugar (porMaquina), el punto y la línea del tramo de carriles consecutivos
      // del mismo conjunto. La x no se usa (se dibuja en el margen izquierdo); va el inicio del
      // eje solo para cumplir el encode.
      const corchetes = grupos.map((g) => ({
        value: [g.idxTitulo, minMs, minMs, g.idxFin, g.color, g.porMaquina ? 1 : 0],
      }))

      return {
        etiquetas: etiquetasEje,
        grupos,
        corchetes,
        rojas,
        amarillas,
        sinOrden,
        enCurso,
        meta,
        jornada,
        excedeJornada,
        plan,
        real,
        min: minMs,
        max: maxMs,
        finJornada: toMs(prop(this.tablero, 'fechaFinJornada')),
      }
    },
  },

  watch: {
    active(val) {
      if (val) {
        if (!this.tablero) this.cargar()
        else this.$nextTick(() => this.render())
        this.iniciarAutoActualizacion()
      } else {
        this.detenerAutoActualizacion()
      }
    },
    vista() {
      this.render()
    },
    // Al cambiar de idioma, el canvas de echarts no es reactivo: hay que repintarlo
    // para que días/meses y las etiquetas "Ahora"/"Fin Jornada" tomen el nuevo locale.
    currentLocale() {
      this.render()
    },
    // El check cambia el dominio del eje, así que hay que repintar con los mismos datos.
    expandirFueraJornada() {
      this.render()
    },
    // Al mostrar/ocultar la leyenda cambia el alto de la cabecera fija: hay que volver a medir
    // (ancla de la barra de fechas y alto disponible en pantalla completa) y redimensionar echarts.
    mostrarLeyenda() {
      this.$nextTick(() => {
        this.medirStickyTop()
        this.medirAltoGrafica()
        if (this.chart) this.chart.resize()
        if (this.ejeChart) this.ejeChart.resize()
        this.sincronizarEjeFecha()
      })
    },
    // Al cambiar el alto de la gráfica (p. ej. al entrar/salir de pantalla completa) echarts
    // no reacciona solo: hay que redimensionarlo y re-anclar la barra de fechas.
    altoGrafica() {
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
        if (this.ejeChart) this.ejeChart.resize()
        this.medirStickyTop()
        this.sincronizarEjeFecha()
      })
    },
  },

  async mounted() {
    this.resizeHandler = () => {
      this.medirAltoGrafica()
      if (this.chart) this.chart.resize()
      if (this.ejeChart) this.ejeChart.resize()
      this.medirStickyTop()
      this.sincronizarEjeFecha()
    }
    window.addEventListener('resize', this.resizeHandler)
    this.fsHandler = () => this.syncFullscreen()
    document.addEventListener('fullscreenchange', this.fsHandler)
    document.addEventListener('webkitfullscreenchange', this.fsHandler)
    // Carga primero la configuración que decide cómo calcular el fin del rango inicial.
    await this.cargarPreferencias()
    // Ajusta el rango por defecto con los horarios activos antes de la primera carga.
    await this.inicializarDesdeHorarios()
    if (this.active || !this.tablero) this.cargar(true)
    if (this.active) this.iniciarAutoActualizacion()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    document.removeEventListener('fullscreenchange', this.fsHandler)
    document.removeEventListener('webkitfullscreenchange', this.fsHandler)
    this.detenerAutoActualizacion()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    if (this.ejeChart) {
      this.ejeChart.dispose()
      this.ejeChart = null
    }
  },

  methods: {
    fmtNum,

    // Pantalla completa de todo el reporte (la tarjeta raíz).
    toggleFullscreen() {
      const root = this.$refs.fullscreenRoot
      const el = root && root.$el ? root.$el : root
      if (!el) return
      const doc = document
      const enFs = doc.fullscreenElement || doc.webkitFullscreenElement
      if (!enFs) {
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
      } else {
        if (doc.exitFullscreen) doc.exitFullscreen()
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
      }
    },

    syncFullscreen() {
      const doc = document
      const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement
      const root = this.$refs.fullscreenRoot
      const el = root && root.$el ? root.$el : root
      this.esFullscreen = !!fsEl && fsEl === el
      // El cambio de pantalla completa no dispara resize de window: re-ajustar la gráfica.
      // Además la cabecera cambia de alto (se ocultan filtros/selector y se compacta),
      // así que re-medimos su altura y re-anclamos la barra de fechas.
      this.$nextTick(() => {
        this.medirAltoGrafica()
        this.$nextTick(() => {
          if (this.chart) this.chart.resize()
          if (this.ejeChart) this.ejeChart.resize()
          this.medirStickyTop()
          this.sincronizarEjeFecha()
        })
      })
    },

    // Mide el alto disponible bajo la gráfica en pantalla completa (viewport menos lo que hay
    // arriba: cabecera compacta, cards, leyenda). Con esto la gráfica llena la pantalla y las
    // filas se ajustan para que quepan más máquinas.
    medirAltoGrafica() {
      if (!this.esFullscreen) { this.fsAltoDisponible = 0; return }
      const el = this.$refs.grafica
      if (!el) return
      const top = el.getBoundingClientRect().top
      this.fsAltoDisponible = Math.max(200, Math.round(window.innerHeight - top - 8))
    },

    // Lunes (00:00) de la semana en curso.
    lunesSemanaActual() {
      const hoy = new Date()
      const diff = (hoy.getDay() + 6) % 7 // lunes = 0
      const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
      lunes.setDate(lunes.getDate() - diff)
      return lunes
    },

    // Nombre largo del día de la semana (0=domingo … 6=sábado) en el idioma activo.
    nombreDiaSemana(dia) {
      // El 7 de enero de 2024 fue domingo: sumando el índice se llega a cualquier día.
      const d = new Date(2024, 0, 7 + (Number(dia) || 0))
      return d.toLocaleDateString(this.$i18n.locale, { weekday: 'long' })
    },

    // Formatea una fecha al formato del picker: "YYYY-MM-DD HH:mm".
    fmtFiltro(d) {
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },

    // Convierte el valor del picker ("YYYY-MM-DD HH:mm" o "YYYY-MM-DD") a ISO para el query.
    filtroAIso(v) {
      if (!v) return v
      const s = String(v).trim().replace(' ', 'T')
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00`
      if (/T\d{2}:\d{2}$/.test(s)) return `${s}:00`
      return s
    },

    // Fallback síncrono: semana en curso (lunes 00:00 → lunes siguiente 00:00).
    inicializarSemana() {
      const inicio = this.lunesSemanaActual()
      const fin = new Date(inicio)
      fin.setDate(inicio.getDate() + 7)
      this.filtros.inicio = this.fmtFiltro(inicio)
      this.filtros.fin = this.fmtFiltro(fin)
    },

    // Ventana de la jornada de la empresa dentro del rango visible: del primer inicio de jornada
    // (día + hora de inicio) al primer fin de jornada posterior, recortada al eje. Es el mismo
    // horario que el diálogo muestra como dato informativo. Null si no se conoce el horario o si
    // la ventana queda fuera del rango.
    ventanaJornada(minMs, maxMs) {
      const ini = this.horaInicioEmpresa
      const fin = this.horaFinEmpresa
      if (!ini || !fin || !Number.isFinite(minMs) || !Number.isFinite(maxMs)) return null
      const SEMANA = 7 * 24 * 60 * 60 * 1000

      // Primer fin de jornada posterior a un inicio dado (puede caer el mismo día si cruza medianoche).
      const finDe = (t) => {
        const h = new Date(t)
        h.setHours(fin.hh, fin.mm, 0, 0)
        h.setDate(h.getDate() + ((fin.dia - h.getDay() + 7) % 7))
        const x = h.getTime()
        return x <= t ? x + SEMANA : x
      }

      // Último inicio de jornada en o antes del arranque del eje: si esa jornada todavía cubre el
      // inicio del rango (filtro que arranca a media semana), la barra empieza en el borde del eje.
      const d = new Date(minMs)
      d.setHours(ini.hh, ini.mm, 0, 0)
      d.setDate(d.getDate() - ((d.getDay() - ini.dia + 7) % 7))
      let desde = d.getTime()
      if (desde > minMs) desde -= SEMANA
      let hasta = finDe(desde)
      // Esa jornada ya cerró antes del rango: se usa la de la semana siguiente.
      if (hasta <= minMs) {
        desde += SEMANA
        hasta = finDe(desde)
      }

      // Recorte al rango visible.
      if (desde < minMs) desde = minMs
      if (hasta > maxMs) hasta = maxMs
      if (desde >= hasta) return null
      return { desde, hasta }
    },

    // Rango de la semana laboral que contiene a la fecha dada, con el MISMO criterio del arranque
    // del reporte: empieza en la última ocurrencia del primer día configurado (a la hora de inicio
    // de la empresa) en o antes de la referencia y termina según la config de fin de periodo —una
    // semana exacta o el último día de la jornada a su hora de fin. Null sin horarios leídos.
    rangoSemanaDe(ref) {
      const ini = this.horaInicioEmpresa
      if (!ini) return null
      const inicio = new Date(ref)
      inicio.setHours(0, 0, 0, 0)
      inicio.setDate(inicio.getDate() - ((inicio.getDay() - ini.dia + 7) % 7))
      inicio.setHours(ini.hh, ini.mm, 0, 0)

      const fin = new Date(inicio)
      const f = this.horaFinEmpresa
      if (this.configReporte && this.configReporte.modoFinPeriodo === 'horarioEmpresa' && f) {
        fin.setDate(inicio.getDate() + ((f.dia - ini.dia + 7) % 7))
        fin.setHours(f.hh, f.mm, 0, 0)
        // Un único horario puede cruzar medianoche (p. ej. 22:00 → 06:00).
        if (fin <= inicio) fin.setDate(fin.getDate() + 1)
      } else {
        fin.setDate(inicio.getDate() + 7)
      }
      return { inicio, fin }
    },

    // Scroll al final del desplegable de semanas: ofrece un bloque más hacia atrás. El tope
    // (5 años) evita que un scroll sostenido genere una lista sin fin.
    cargarMasSemanas(entries, observer, isIntersecting) {
      if (!isIntersecting || this.semanasAtras >= 260) return
      this.semanasAtras += 12
    },

    // Aplica a los filtros la semana que contiene la fecha dada y recarga.
    async aplicarSemana(refMs) {
      const rango = this.rangoSemanaDe(new Date(Number(refMs)))
      if (!rango) return
      this.filtros.inicio = this.fmtFiltro(rango.inicio)
      this.filtros.fin = this.fmtFiltro(rango.fin)
      await this.cargar()
    },

    // Etiqueta de una semana en el selector, en el formato que diga la configuración.
    etiquetaSemana(inicio, fin) {
      return this.formatoSemanaWk
        ? this.textoSemanaWk(inicio, fin)
        : this.textoSemanaFechas(inicio, fin)
    },

    // Formato "fechas": el periodo, "02 Ene - 11 Ene". Día a dos dígitos y mes capitalizado.
    textoSemanaFechas(inicio, fin) {
      const meses = MESES[this.$i18n.locale] || MESES.es
      // El fin es exclusivo en la práctica (arranque de la siguiente jornada), así que se muestra
      // el último día con actividad para que la etiqueta no invada la semana siguiente.
      const ultimo = new Date(fin.getTime() - 1)
      const txt = (d) => {
        const mes = meses[d.getMonth()]
        return `${String(d.getDate()).padStart(2, '0')} ${mes.charAt(0).toUpperCase()}${mes.slice(1)}`
      }
      return `${txt(inicio)} - ${txt(ultimo)}`
    },

    // Formato "wk": el número de semana del año delante del periodo, "W01 02 Ene - 11 Ene".
    // El número se calcula sobre el PUNTO MEDIO del periodo y no sobre su inicio: la jornada
    // arranca la tarde del domingo y las semanas ISO cierran ese mismo domingo, así que tomar el
    // inicio devolvería la semana anterior. El medio siempre cae entre semana, dentro de su ISO.
    textoSemanaWk(inicio, fin) {
      const medio = new Date((inicio.getTime() + fin.getTime()) / 2)
      const n = String(this.numeroSemanaIso(medio)).padStart(2, '0')
      // Separador ancho: un espacio normal no basta para despegar el número del periodo, y
      // varios seguidos el navegador los colapsa en uno. El espacio EM ni colapsa ni parte la
      // línea, y mide una eme. Va escapado para que no quede un carácter invisible en el fuente.
      return `W${n}\u2003${this.textoSemanaFechas(inicio, fin)}`
    },

    // Semana ISO 8601 de una fecha: las semanas van de lunes a domingo y la semana 1 es la que
    // contiene el primer jueves del año. Se opera en UTC para que un cambio de horario no corra
    // la fecha un día.
    numeroSemanaIso(fecha) {
      const d = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()))
      // Jueves de su misma semana: es el día que decide a qué año ISO pertenece.
      d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) + 3)
      const jueves = d.getTime()
      // Jueves de la semana 1 de ese año (la que contiene al 4 de enero).
      const semana1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
      semana1.setUTCDate(semana1.getUTCDate() - ((semana1.getUTCDay() + 6) % 7) + 3)
      return 1 + Math.round((jueves - semana1.getTime()) / (7 * 24 * 60 * 60 * 1000))
    },

    // Fin de la jornada de la empresa (último día configurado, a su última hora) más cercano a
    // la referencia dada, que es el Fin del filtro. Se elige la ocurrencia más próxima —anterior
    // o posterior— para que un rango largo no se recorte a la primera semana y uno de una semana
    // termine donde termina la jornada, no a las 7×24 h exactas. Null si no se conoce el horario.
    finJornadaCercano(refMs) {
      const f = this.horaFinEmpresa
      if (!f || !Number.isFinite(refMs)) return null
      const SEMANA = 7 * 24 * 60 * 60 * 1000
      const d = new Date(refMs)
      d.setHours(f.hh, f.mm, 0, 0)
      // Retrocede al día de la semana del fin de jornada (mismo día o anterior).
      d.setDate(d.getDate() - ((d.getDay() - f.dia + 7) % 7))
      let previa = d.getTime()
      if (previa > refMs) previa -= SEMANA
      const siguiente = previa + SEMANA
      return (refMs - previa) <= (siguiente - refMs) ? previa : siguiente
    },

    // Cortes del eje de fechas anclados a la hora de inicio de la empresa: el día cambia al
    // inicio de la jornada, no a las 00:00. El paso es 24 h multiplicado/dividido (siempre en
    // proporciones exactas del día, para que los cortes de día caigan siempre en el ancla) hasta
    // que queden al menos 8 cortes en el rango visible: una semana se parte cada 12 h (inicio de
    // jornada y media jornada) y al hacer zoom los cortes se van afinando.
    // Devuelve null si no se conoce el horario: el eje usa entonces los cortes automáticos.
    marcasEje(min, max) {
      const h = this.horaInicioEmpresa
      if (!h || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null
      const H = 60 * 60 * 1000
      const DIA = 24 * H
      const PASOS = [7 * DIA, 2 * DIA, DIA, 12 * H, 6 * H, 3 * H, H, H / 2, H / 4]
      const span = max - min
      const paso = PASOS.find((x) => span / x >= 8) || PASOS[PASOS.length - 1]
      const ancla = new Date(min)
      ancla.setHours(h.hh, h.mm, 0, 0)
      const anclaMs = ancla.getTime()
      // Se retrocede al inicio de jornada anterior al rango y se avanza con el paso hasta entrar.
      let t = anclaMs
      while (t > min) t -= DIA
      while (t < min) t += paso
      const marcas = []
      for (; t <= max && marcas.length < 400; t += paso) {
        // Corte de día: múltiplo exacto de 24 h desde el inicio de jornada.
        marcas.push({ t, esDia: (t - anclaMs) % DIA === 0 })
      }
      return marcas
    },

    // Valores donde el eje de fechas dibuja etiqueta y marca: los cortes de jornada más los dos
    // extremos del rango (que siempre llevan su fecha). Null = cortes automáticos de echarts.
    valoresCorteEje(min, max) {
      const marcas = this.marcasEje(min, max)
      if (!marcas) return null
      const valores = new Set([Number(min), ...marcas.map((m) => m.t), Number(max)])
      return Array.from(valores).sort((a, b) => a - b)
    },

    // Calcula el rango por defecto a partir de los horarios activos (CAT_Horarios): inicia en
    // la primera hora del día con menor diaSemana (0=domingo … 6=sábado). Según la configuración,
    // termina una semana después o en la última hora del día con mayor diaSemana.
    async inicializarDesdeHorarios() {
      try {
        // /Horarios responde { events, start, intervals }: los horarios están en events.
        const { data } = await axios.get('/Horarios')
        const horarios = Array.isArray(data)
          ? data
          : Array.isArray(data && data.events)
            ? data.events
            : []
        if (!horarios.length) {
          this.inicializarSemana()
          return
        }

        const minutosHora = (valor) => {
          const [hora, minuto] = String(valor || '00:00').split(':').map(Number)
          return (hora || 0) * 60 + (minuto || 0)
        }
        const horariosValidos = horarios.filter((h) => {
          const dia = Number(prop(h, 'diaSemana'))
          return Number.isInteger(dia) && dia >= 0 && dia <= 6
        })
        if (!horariosValidos.length) {
          this.inicializarSemana()
          return
        }

        const primerDia = Math.min(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const ultimoDia = Math.max(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const primerHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === primerDia)
          .reduce((primero, h) =>
            minutosHora(prop(h, 'inicio')) < minutosHora(prop(primero, 'inicio')) ? h : primero,
          )
        const ultimoHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === ultimoDia)
          .reduce((ultimo, h) =>
            minutosHora(prop(h, 'fin')) > minutosHora(prop(ultimo, 'fin')) ? h : ultimo,
          )
        const [hhInicio, mmInicio] = String(prop(primerHorario, 'inicio') || '00:00').split(':').map(Number)
        const [hhFin, mmFin] = String(prop(ultimoHorario, 'fin') || '00:00').split(':').map(Number)
        // Se guardan para anclar los cortes del eje y el tope de la gráfica.
        this.horaInicioEmpresa = { dia: primerDia, hh: hhInicio || 0, mm: mmInicio || 0 }
        this.horaFinEmpresa = { dia: ultimoDia, hh: hhFin || 0, mm: mmFin || 0 }

        // El rango por defecto es la semana laboral que contiene a hoy: la última ocurrencia del
        // primer día configurado (0=domingo … 6=sábado) a su hora de inicio. Es el mismo cálculo
        // que usa el selector de semana, para que los dos modos de filtro no se separen.
        const rango = this.rangoSemanaDe(new Date())
        if (!rango) {
          this.inicializarSemana()
          return
        }
        this.filtros.inicio = this.fmtFiltro(rango.inicio)
        this.filtros.fin = this.fmtFiltro(rango.fin)
      } catch (e) {
        // Si falla, se usa el fallback de la semana en curso.
        this.inicializarSemana()
        // eslint-disable-next-line no-console
        console.error('Error cargando horarios para fechas por defecto', e)
      }
    },

    // Convierte el catálogo del backend (camel/Pascal) a items {text, value} para v-autocomplete.
    catalogoItems(clave) {
      const cat = prop(this.tablero, 'catalogos') || {}
      const lista = prop(cat, clave) || []
      return lista.map((it) => ({ text: prop(it, 'nombre'), value: prop(it, 'id') }))
    },

    // Reinicia la cuenta regresiva a su valor completo (5 min).
    reiniciarContador() {
      this.restante = this.intervaloSeg
    },

    // Tick de 1 s: descuenta el contador y dispara la recarga al llegar a 0.
    // No descuenta mientras hay una carga en curso para no encimar peticiones.
    iniciarAutoActualizacion() {
      this.detenerAutoActualizacion()
      this.reiniciarContador()
      this.timerTick = setInterval(() => {
        if (this.cargando) return
        if (this.restante <= 1) this.cargar()
        else this.restante -= 1
      }, 1000)
    },

    detenerAutoActualizacion() {
      if (this.timerTick) {
        clearInterval(this.timerTick)
        this.timerTick = null
      }
    },

    async cargar(omitirPreferencias = false) {
      // Toda recarga (manual o automática) reinicia el contador.
      this.reiniciarContador()
      // Cada recarga refresca las preferencias del reporte (las crea si no existen).
      if (omitirPreferencias !== true) await this.cargarPreferencias()
      this.cargando = true
      this.error = false
      try {
        const p = new URLSearchParams()
        if (this.filtros.inicio) p.append('inicio', this.filtroAIso(this.filtros.inicio))
        if (this.filtros.fin) p.append('fin', this.filtroAIso(this.filtros.fin))
          ; (this.filtros.idsArea || []).forEach((v) => p.append('idsArea', v))
          ; (this.filtros.idsTrabajoOrden || []).forEach((v) => p.append('idsTrabajoOrden', v))
          ; (this.filtros.idsInstancia || []).forEach((v) => p.append('idsInstancia', v))
          ; (this.filtros.idsArticulo || []).forEach((v) => p.append('idsArticulo', v))
          ; (this.filtros.idsMolde || []).forEach((v) => p.append('idsMolde', v))

        const { data } = await axios.get(`/ReportesMoldes/TableroProduccionV4?${p.toString()}`)
        this.tablero = data
        this.$nextTick(() => this.render())
      } catch (e) {
        this.error = true
        // eslint-disable-next-line no-console
        console.error('Error cargando tablero de producción', e)
      } finally {
        this.cargando = false
      }
      // Máquinas activas y estado de las instancias son tiempo real (no dependen del periodo del
      // tablero, solo del área). Se piden en paralelo y al terminar se repintan los nombres del eje.
      await Promise.all([this.cargarMaquinasActivas(), this.cargarEstadoMaquinas()])
      // Los grupos de carriles salen del contador: si con las activas cambió el orden del eje hay
      // que reconstruir el tablero (las series se indexan por carril); si no, basta con los nombres.
      if (this.claveCarriles(this.datos) !== this._carrilesRenderizados) this.render()
      else this.repintarNombresEje()
    },

    // Carga las máquinas en producción (KPI azul). Aplica el mismo filtro de área que el
    // tablero (resuelto en backend vía token → instancia → área). Si falla, se conserva el
    // último valor conocido sin marcar el tablero como erróneo.
    async cargarMaquinasActivas() {
      this.maquinasActivasCargando = true
      try {
        const p = new URLSearchParams()
          ; (this.filtros.idsArea || []).forEach((v) => p.append('idsArea', v))
        const qs = p.toString()
        const { data } = await axios.get(`/ReportesMoldes/MaquinasActivas${qs ? `?${qs}` : ''}`)
        this.maquinasActivasApi = Array.isArray(data) ? data : []
        this.maquinasActivasListas = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando máquinas activas', e)
      } finally {
        this.maquinasActivasCargando = false
      }
    },

    // Estado en tiempo real de las instancias del área: el mismo endpoint que consume el reporte de
    // Estado de Máquinas (r61), para que "paro no programado" y "con OT" signifiquen lo mismo en los
    // dos reportes. Solo filtra por área (igual que MaquinasActivas). Si falla, se conserva el
    // último valor conocido sin marcar el tablero como erróneo.
    async cargarEstadoMaquinas() {
      try {
        const p = new URLSearchParams()
          ; (this.filtros.idsArea || []).forEach((v) => p.append('idsAreas', v))
        const qs = p.toString()
        const { data } = await axios.get(`/OEEV2/EstadoMaquinasTiempoReal${qs ? `?${qs}` : ''}`)
        this.estadoMaquinasApi = Array.isArray(data) ? data : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando estado de máquinas', e)
      }
    },

    // Instancias cuyo estado según ops es `status` (3 paro programado, 4 no programado) y que el
    // contador NO reporta activas ("el contador manda", igual que r61). Devuelve ids y nombres
    // normalizados para cruzarlos con las máquinas del tablero.
    instanciasEnParo(status) {
      const { porId, porNombre } = this.indiceActivas
      const ids = new Set()
      const nombres = new Set()
        ; (this.estadoMaquinasApi || []).forEach((e) => {
          const id = Number(prop(e, 'idMaquinaIns'))
          const nombre = String(prop(e, 'nombreInstancia') || '').trim().toUpperCase()
          const activa = (Number.isFinite(id) && porId.has(id)) || (!!nombre && porNombre.has(nombre))
          if (activa || this.statusEstado(e) !== status) return
          if (Number.isFinite(id) && id > 0) ids.add(id)
          if (nombre) nombres.add(nombre)
        })
      return { ids, nombres }
    },

    // Copia de statusDesdeEstado de EstadoMaquinaCazels.vue: 4 = paro no programado, 3 = paro
    // programado, 1 = operación abierta, 2 = sin nada. Si cambia allá, cambiar aquí.
    statusEstado(e) {
      if (esTrue(prop(e, 'enParoNoProgramado'))) return 4
      if (esTrue(prop(e, 'enParoProgramado'))) return 3
      if (esTrue(prop(e, 'tieneParoAbierto'))) {
        const paro = (prop(e, 'parosAbiertos') || [])[0] || {}
        const sub = prop(paro, 'subParoTipoProgramado')
        const programado = sub !== undefined && sub !== null ? sub : prop(paro, 'paroTipoProgramado')
        return esTrue(programado) ? 3 : 4
      }
      if (esTrue(prop(e, 'tieneOperacionAbierta'))) return 1
      return 2
    },

    // Estilos de la etiqueta del eje. El bloque del nombre solo lleva ancho fijo cuando hay
    // columna de datos a su derecha, para que todas las cifras arranquen alineadas.
    richNombreEje() {
      const anchoNombre = this.anchoColEnCurso ? this.anchoColNombre : undefined
      const c = this.colsEnCurso
      const dato = { color: COLOR.datoEje, fontSize: 12, fontWeight: 700, align: 'right' }
      const sep = { color: '#aab4c0', fontSize: 12, fontWeight: 700, align: 'right', width: c ? c.sep : 0 }
      // Todos los tramos del dato van alineados a la derecha y con ancho fijo: zrender los coloca
      // de derecha a izquierda como un bloque único, así que cada valor cae siempre en la misma
      // columna y los dígitos cuadran entre instancias. El nombre, a la izquierda, no entra en ese
      // bloque porque su alineación es distinta.
      const rich = {
        nom: { color: '#283139', fontSize: 15, fontWeight: 800, align: 'left', width: anchoNombre },
        np: { color: '#E83535', fontSize: 15, fontWeight: 800, align: 'left', width: anchoNombre },
        pp: { color: '#D99A00', fontSize: 15, fontWeight: 800, align: 'left', width: anchoNombre },
        // Orden programada sin arrancar: color configurable (rojo por defecto).
        so: { color: this.colorSinOrdenArrancada, fontSize: 15, fontWeight: 800, align: 'left', width: anchoNombre },
        ok: { ...dato, width: c ? c.ok : 0 },
        sl: sep,
        prog: { ...dato, width: c ? c.prog : 0 },
        md: sep,
        hrs: { ...dato, width: c ? c.hrs : 0 },
      }
      // Título de grupo de carriles: punto y texto en el color del grupo, letra chica en negritas.
      Object.keys(GRUPOS_CARRIL).forEach((k) => {
        const color = GRUPOS_CARRIL[k].color
        rich[`gd_${k}`] = { color, fontSize: 9, fontWeight: 900 }
        rich[`gt_${k}`] = { color, fontSize: 10.5, fontWeight: 800 }
      })
      // Título de área (posición por área): nombre en mayúsculas, tinta oscura, letra chica.
      rich.ga = { color: COLOR_TITULO_AREA, fontSize: 11.5, fontWeight: 900 }
      return rich
    },

    // Etiqueta del eje de instancias: nombre (rojo en paro no programado, amarillo en paro
    // programado, color configurado si no tiene orden arrancada con plan en curso; en ese orden de
    // prioridad) y, cuando la máquina tiene una orden EN CURSO, sus piezas y horas faltantes a la
    // derecha.
    formatterNombreEje(datos) {
      const rojas = (datos && datos.rojas) || new Set()
      const amarillas = (datos && datos.amarillas) || new Set()
      const sinOrden = (datos && datos.sinOrden) || new Set()
      const enCurso = (datos && datos.enCurso) || {}
      return (name) => {
        // Renglón de título de área (posición por área): el nombre del área en mayúsculas.
        if (String(name).startsWith(AREA_PREFIJO)) {
          return `{ga|${String(name).slice(AREA_PREFIJO.length).toUpperCase()}}`
        }
        // Renglón de título de grupo: punto del color del grupo y, con los títulos encendidos en
        // la config (mostrarTituloGrupos), el nombre en mayúsculas del mismo color.
        if (String(name).startsWith(GRUPO_PREFIJO)) {
          const clave = String(name).slice(GRUPO_PREFIJO.length)
          const g = GRUPOS_CARRIL[clave]
          if (!g) return ''
          const punto = `{gd_${clave}|●}`
          if (!this.mostrarTituloGrupos) return punto
          return `${punto}{gt_${clave}|  ${String(this.$t(g.i18n)).toUpperCase()}}`
        }
        const estilo = rojas.has(name) ? 'np'
          : amarillas.has(name) ? 'pp'
            : sinOrden.has(name) ? 'so'
              : 'nom'
        const nombre = `{${estilo}|${name}}`
        const d = enCurso[name]
        if (!d) return nombre
        // Sin horas los dos últimos tramos van vacíos, pero SE EMITEN igual: conservan su ancho y
        // mantienen el bloque del mismo largo, que es lo que sostiene la alineación por columnas.
        return `${nombre}{ok|${d.ok}}{sl|/}{prog|${d.prog}}{md|${d.hrs ? '·' : ''}}{hrs|${d.hrs}}`
      }
    },

    // Repinta solo las etiquetas del eje de instancias sin reconstruir el gráfico: no toca el zoom
    // ni las series. Se llama cuando llegan activas y estado, que es también cuando aparece el dato
    // de la orden en curso; por eso se reajusta el ancho de la columna (grid.left) en los dos
    // gráficos, o la barra de fechas quedaría desalineada.
    repintarNombresEje() {
      if (!this.chart || !this.tablero) return
      const ancho = this.anchoColMaquina
      this.chart.setOption({
        grid: { left: ancho },
        yAxis: {
          axisLabel: {
            margin: ancho - COL_INSET - this.anchoCorchete,
            formatter: this.formatterNombreEje(this.datos),
            rich: this.richNombreEje(),
          },
        },
      })
      if (this.ejeChart) this.ejeChart.setOption({ grid: { left: ancho } })
      this.sincronizarEjeFecha()
    },

    // Abre el selector de cambio de instancia y carga los moldes/instancias destino válidos.
    async abrirCambioInstancia() {
      this.cambioError = ''
      this.destinos = []
      this.destinoMoldeSel = null
      this.destinoInstanciaSel = null
      this.dialogCambio = true
      this.cambioCargando = true
      try {
        const { data } = await axios.get(
          `/TrabajoOrdenes/DestinosCambioInstancia/${this.fragmento.idTrabajoOrden}`,
        )
        this.destinos = Array.isArray(data) ? data : []
      } catch (e) {
        this.cambioError = this.$t('Reports.ProductionPlan.errors.loadDestinations')
        // eslint-disable-next-line no-console
        console.error('Error cargando destinos de cambio de instancia', e)
      } finally {
        this.cambioCargando = false
      }
    },

    // Ejecuta el cambio (PUT) y recarga el tablero para reflejar la nueva instancia.
    async confirmarCambioInstancia() {
      if (!this.fragmento.idTrabajoOrden || !this.destinoMoldeSel || !this.destinoInstanciaSel) return
      this.cambioGuardando = true
      this.cambioError = ''
      try {
        await axios.put(
          `/TrabajoOrdenes/CambiarInstanciaClonandoMolde/${this.fragmento.idTrabajoOrden}/${this.destinoMoldeSel}`,
          null,
          { params: { idMaquinaIns: this.destinoInstanciaSel } },
        )
        this.dialogCambio = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        const status = e && e.response && e.response.status
        this.cambioError =
          status === 409
            ? this.$t('Reports.ProductionPlan.errors.moveConflict')
            : this.$t('Reports.ProductionPlan.errors.moveFailed')
        // eslint-disable-next-line no-console
        console.error('Error en cambio de instancia', e)
      } finally {
        this.cambioGuardando = false
      }
    },

    // Abre el diálogo de finalización y carga el padrón de operadores.
    abrirFinalizarOdt() {
      this.finalizarForzado = false
      this.abrirDialogoFinalizar()
    },

    // Forzar finalización: mismo diálogo y misma elección de operador, pero contra el endpoint
    // forzado. Cierra como OPS (Moldes V2) cada bloque abierto de la OT, tenga uno o ninguno, y
    // después barre por orden todo lo que siga abierto (operaciones reales sueltas, paros, pausas,
    // asignaciones) dejándola en estado 5 con fecha de finalización.
    abrirForzarFinalizacion() {
      this.finalizarForzado = true
      this.abrirDialogoFinalizar()
    },

    abrirDialogoFinalizar() {
      this.finalizarError = ''
      this.finalizarOperadorSel = null
      this.finalizarBusquedaOperador = ''
      this.dialogFinalizar = true
      this.cargarOperadoresFinalizar()
    },

    // Padrón de operadores de producción (Personas activas con puesto de producción). Se pide
    // una sola vez por sesión del reporte: es un catálogo, no cambia entre finalizaciones.
    async cargarOperadoresFinalizar() {
      if (this.finalizarOperadores.length) return
      this.finalizarCargandoOperadores = true
      try {
        const { data } = await axios.get('/Personas/OperadoresBasico')
        this.finalizarOperadores = (data || [])
          .map((p) => {
            const nombre = [prop(p, 'nombre'), prop(p, 'paterno'), prop(p, 'materno')]
              .filter((x) => x && String(x).trim())
              .join(' ')
              .trim()
            const partes = nombre.split(/\s+/).filter(Boolean)
            const codigo = String(prop(p, 'codigo') || '')
            const puesto = prop(p, 'puesto') || ''
            return {
              idPersona: Number(prop(p, 'idPersona')),
              nombre: nombre || `#${prop(p, 'idPersona')}`,
              codigo,
              puesto,
              iniciales: ((partes[0] || '').charAt(0) + (partes[1] || '').charAt(0)).toUpperCase() || '?',
              busqueda: `${nombre} ${codigo} ${puesto}`.toLowerCase(),
            }
          })
          .sort((a, b) => a.nombre.localeCompare(b.nombre))
      } catch (e) {
        this.finalizarError = this.$t('Reports.ProductionPlan.errors.loadOperators')
        // eslint-disable-next-line no-console
        console.error('Error cargando operadores', e)
      } finally {
        this.finalizarCargandoOperadores = false
      }
    },

    // Finaliza la ODT por el camino de moldes: el mismo cierre que dispara OPS en Moldes V2.
    // El backend resuelve el bloque de operación real abierto de la OT y delega en el
    // orquestador; por eso acá solo viajan la orden y el operador.
    async confirmarFinalizarOdt() {
      if (!this.fragmento.idTrabajoOrden || !this.finalizarOperadorSel) return
      this.finalizarGuardando = true
      this.finalizarError = ''
      const forzado = this.finalizarForzado
      const payload = {
        idTrabajoOrden: this.fragmento.idTrabajoOrden,
        idOperadorFinalizo: this.finalizarOperadorSel,
      }
      try {
        // Forzado: no exige bloque abierto ni máquina; el backend cierra todos los bloques
        // abiertos de la OT y luego lo residual. Normal: el cierre por bloque de Moldes V2.
        await axios.put(
          forzado
            ? '/OperacionesMolde/ForzarFinalizacionOT'
            : '/OperacionesMolde/FinalizarOTOperacionesMoldePorTrabajoOrden',
          payload,
        )
        this.dialogFinalizar = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        const status = e && e.response && e.response.status
        // Forzado: solo 404 (la OT no existe o está inactiva) tiene mensaje propio.
        // Normal: 404 = la OT no tiene bloque de molde abierto; 409 = lo tiene en más de una
        // máquina y el backend no elige por su cuenta (el fragmento no lleva la instancia).
        if (forzado) {
          this.finalizarError = status === 404
            ? this.$t('Reports.ProductionPlan.errors.forceFinishNotFound')
            : this.$t('Reports.ProductionPlan.errors.forceFinishFailed')
        } else if (status === 404) {
          this.finalizarError = this.$t('Reports.ProductionPlan.errors.finishNoOpenBlock')
        } else if (status === 409) {
          this.finalizarError = this.$t('Reports.ProductionPlan.errors.finishAmbiguousMachine')
        } else {
          this.finalizarError = this.$t('Reports.ProductionPlan.errors.finishFailed')
        }
        // eslint-disable-next-line no-console
        console.error('Error finalizando ODT', e)
      } finally {
        this.finalizarGuardando = false
      }
    },

    // Eliminar fragmento: manda a condición 0 la fase (versión) completa de la OT que representa
    // el fragmento real: detalles, operaciones, producción real, bloques vacíos, paros,
    // asignaciones, pausas, reservas y costos. Para pedacitos abiertos por error. El backend
    // responde 409 si la fase aún tiene algo abierto (primero Forzar finalización).
    abrirEliminarFragmento() {
      this.eliminarError = ''
      this.dialogEliminar = true
    },

    async confirmarEliminarFragmento() {
      if (!this.fragmento.idTrabajoOrden || !this.fragmento.numeroVersion) return
      this.eliminarGuardando = true
      this.eliminarError = ''
      try {
        await axios.put(
          `/TrabajoOrdenes/EliminarFaseOT/${this.fragmento.idTrabajoOrden}/${this.fragmento.numeroVersion}`,
        )
        this.dialogEliminar = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        const status = e && e.response && e.response.status
        if (status === 409) {
          this.eliminarError = this.$t('Reports.ProductionPlan.errors.deleteFragmentOpen')
        } else if (status === 404) {
          this.eliminarError = this.$t('Reports.ProductionPlan.errors.deleteFragmentNotFound')
        } else {
          this.eliminarError = this.$t('Reports.ProductionPlan.errors.deleteFragmentFailed')
        }
        // eslint-disable-next-line no-console
        console.error('Error eliminando fragmento', e)
      } finally {
        this.eliminarGuardando = false
      }
    },

    // Abre el diálogo de confirmación para reabrir la ODT finalizada anticipadamente.
    async abrirReabrirOdt() {
      this.reabrirError = ''
      this.reabrirDestinos = []
      this.reabrirDetalles = []
      this.reabrirMoldeSel = null
      this.reabrirInstanciaSel = null
      this.reabrirMotivo = ''
      this.reabrirMismaFechaInicio = true
      this.reabrirFechaInicioOriginal = ''
      this.reabrirFechaInicio = ''
      this.reabrirIdSolicitud = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.floor(Math.random() * 16)
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
        })
      this.dialogReabrir = true
      this.reabrirCargando = true
      try {
        const { data } = await axios.get(
          `/TrabajoOrdenes/DestinosReapertura/${this.fragmento.idTrabajoOrden}`,
        )
        this.reabrirDestinos = prop(data, 'destinos') || []
        this.reabrirFechaInicioOriginal = prop(data, 'fechaInicioDeProduccion') || ''
        this.reabrirDetalles = (prop(data, 'detalles') || []).map((d) => ({
          idTrabajoOrdenDetalle: prop(d, 'idTrabajoOrdenDetalle'),
          codigoArticulo: prop(d, 'codigoArticulo') || '',
          articulo: prop(d, 'articulo') || '',
          cantidadProducida: Number(prop(d, 'cantidadProducida') || 0),
          // Cantidad original (primera versión) y meta resultante = producido + original.
          cantidad: Number(prop(d, 'cantidadOriginal') || 0),
          metaNueva: Number(prop(d, 'cantidadMetaNueva') || 0),
        }))
      } catch (e) {
        this.reabrirError = this.$t('Reports.ProductionPlan.errors.loadDestinations')
      } finally {
        this.reabrirCargando = false
      }
    },

    cambiarModoFechaReapertura() {
      if (!this.reabrirMismaFechaInicio && !this.reabrirFechaInicio) {
        this.reabrirFechaInicio = this.fmtFiltro(new Date())
      }
    },

    // Reabre la ODT y recarga el tablero. El operador deberá arrancarla nuevamente desde OPS.
    async confirmarReabrirOdt() {
      if (!this.fragmento.idTrabajoOrden || !this.reaperturaValida) return
      this.reabrirGuardando = true
      this.reabrirError = ''
      try {
        await axios.put(
          `/TrabajoOrdenes/ReiniciarAnticipadamenteOT/${this.fragmento.idTrabajoOrden}`,
          {
            idMolde: this.reabrirMoldeSel,
            idMaquinaIns: this.reabrirInstanciaSel,
            idSolicitudReapertura: this.reabrirIdSolicitud,
            motivo: this.reabrirMotivo,
            confirmarAjusteMeta: true,
            cambiarFechaInicioProduccion: !this.reabrirMismaFechaInicio,
            fechaInicioProduccion: this.reabrirMismaFechaInicio
              ? null
              : this.filtroAIso(this.reabrirFechaInicio),
            // Sin detalles: el backend reabre cada detalle con su cantidad original.
            detalles: [],
          },
        )
        this.dialogReabrir = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        const status = e && e.response && e.response.status
        const respuesta = e && e.response && e.response.data
        const mensajeApi = typeof respuesta === 'string'
          ? respuesta
          : respuesta && (respuesta.message || respuesta.Message)
        this.reabrirError = mensajeApi || (status === 409
          ? this.$t('Reports.ProductionPlan.errors.reopenConflict')
          : this.$t('Reports.ProductionPlan.errors.reopenFailed'))
        // eslint-disable-next-line no-console
        console.error('Error reabriendo ODT finalizada anticipadamente', e)
      } finally {
        this.reabrirGuardando = false
      }
    },

    // Abre el diálogo para actualizar el TC, precargando el TC actual de cada operación.
    abrirActualizarTc() {
      this.tcError = ''
      this.tcOperaciones = (this.fragmento.operaciones || []).map((o) => ({
        idOperacion: o.idOperacion,
        codigo: o.codigo,
        tcActual: Number(o.tc || 0),
        tc: Number(o.tc || 0),
      }))
      this.dialogTc = true
    },

    // Envía el nuevo TC por operación (PUT) y recarga el tablero. El backend aplica el TC
    // igual que OtsServiceV4 (TiempoMin/Max y TiempoTotalMin/Max).
    async confirmarActualizarTc() {
      if (!this.fragmento.idTrabajoOrden || !this.tcValido) return
      this.tcGuardando = true
      this.tcError = ''
      try {
        const payload = this.tcOperaciones
          .filter((o) => o.idOperacion && Number(o.tc) > 0)
          .map((o) => ({ idOperacion: o.idOperacion, tc: Math.round(Number(o.tc)) }))
        await axios.put(
          `/TrabajoOrdenes/ActualizarTiemposCicloOT/${this.fragmento.idTrabajoOrden}`,
          payload,
        )
        this.dialogTc = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        this.tcError = this.$t('Reports.ProductionPlan.errors.updateTcFailed')
        // eslint-disable-next-line no-console
        console.error('Error actualizando TC de la ODT', e)
      } finally {
        this.tcGuardando = false
      }
    },

    // Abre el diálogo con la cantidad programada actual de cada operación/cavidad.
    abrirActualizarPiezas() {
      this.piezasError = ''
      this.piezasOperaciones = (this.fragmento.operaciones || []).map((o) => ({
        idOperacion: o.idOperacion,
        codigo: o.codigo,
        cantidadActual: Number(o.esperadas || 0),
        cantidad: Number(o.esperadas || 0),
      }))
      this.dialogPiezas = true
    },

    // Guarda cada cantidad directamente en su operación/cavidad y recarga el tablero.
    async confirmarActualizarPiezas() {
      if (!this.fragmento.idTrabajoOrden || !this.piezasValidas) return
      this.piezasGuardando = true
      this.piezasError = ''
      try {
        const payload = this.piezasOperaciones
          .filter((o) => o.idOperacion && Number(o.cantidad) > 0)
          .map((o) => ({ idOperacion: o.idOperacion, cantidad: Math.round(Number(o.cantidad)) }))
        await axios.put(
          `/TrabajoOrdenes/ActualizarPiezasProgramadasOT/${this.fragmento.idTrabajoOrden}`,
          payload,
        )
        this.dialogPiezas = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        this.piezasError = this.$t('Reports.ProductionPlan.errors.updatePiecesFailed')
        // eslint-disable-next-line no-console
        console.error('Error actualizando piezas programadas de la ODT', e)
      } finally {
        this.piezasGuardando = false
      }
    },

    // ===================== CONFIGURACIÓN DEL REPORTE (eficiencia + bandas) =====================
    // Normaliza el objeto de configuración, asegurando tipos y rellenando faltantes.
    normalizarConfig(cfg = {}) {
      const source = cfg && typeof cfg === 'object' ? cfg : {}
      const def = getDefaultConfigReporte()
      let eficiencia = Number(source.porcentajeHolgura)
      if (!Number.isFinite(eficiencia)) eficiencia = def.porcentajeHolgura
      eficiencia = Math.min(100, Math.max(1, eficiencia))
      let intervalo = Number(source.intervaloSeg)
      if (!Number.isFinite(intervalo)) intervalo = def.intervaloSeg
      intervalo = Math.round(Math.min(INTERVALO_MAX_SEG, Math.max(INTERVALO_MIN_SEG, intervalo)))
      const modoPorcentaje = source.modoPorcentaje === 'hastaAhora' ? 'hastaAhora' : 'semana'
      const modoPantallaCompleta = source.modoPantallaCompleta === 'original' ? 'original' : 'compacta'
      // Cualquier valor distinto de 'inicio' cae en 'entrega', que es el default.
      const filtroFecha = source.filtroFecha === 'inicio' ? 'inicio' : 'entrega'
      const modoFinPeriodo = source.modoFinPeriodo === 'horarioEmpresa' ? 'horarioEmpresa' : 'semana'
      // Cualquier valor distinto de 'piezas' cae en 'tiempo', que es el default histórico.
      const modoDesfase = source.modoDesfase === 'piezas' ? 'piezas' : 'tiempo'
      // 'inicioReal' es el nombre viejo de 'duracionReal' (la resta es la misma); cualquier otro
      // valor cae en 'finProgramado', que es el default.
      const referenciaDesfase = source.referenciaDesfase === 'duracionReal' || source.referenciaDesfase === 'inicioReal'
        ? 'duracionReal'
        : 'finProgramado'
      // Default true: solo un false explícito (bool, 0 o 'false') lo desactiva.
      const esFalse = (x) => x === false || x === 0 || x === '0' || x === 'false'
      const ocultarRealesNoIniciadas = !esFalse(source.ocultarRealesNoIniciadas)
      const dejarSiguienteProgramada = !esFalse(source.dejarSiguienteProgramada)
      const mostrarLeyenda = !esFalse(source.mostrarLeyenda)
      const mostrarProgSinProducir = !esFalse(source.mostrarProgSinProducir)
      // Default false: solo un true explícito (bool, 1 o 'true') lo activa.
      const esTrue = (x) => x === true || x === 1 || x === '1' || x === 'true'
      const mostrarBotonTodo = esTrue(source.mostrarBotonTodo)
      const vistaPorConjunto = esTrue(source.vistaPorConjunto)
      const mostrarTituloGrupos = esTrue(source.mostrarTituloGrupos)
      // Solo un 'original' o 'porArea' explícito saca los conjuntos de arriba; cualquier otra cosa
      // es 'arriba', el default.
      const ordenConjuntos = source.ordenConjuntos === 'original' || source.ordenConjuntos === 'porArea'
        ? source.ordenConjuntos
        : 'arriba'
      // Default true: solo un false explícito oculta el nombre del área.
      const mostrarTituloArea = !esFalse(source.mostrarTituloArea)
      // Solo un 'hastaFragmento' explícito acorta la orden adelantada; lo demás es 'hastaFinalizar'.
      const modoAdelantada = source.modoAdelantada === 'hastaFragmento' ? 'hastaFragmento' : 'hastaFinalizar'
      // Solo un 'programadaSinArrancar' explícito cambia la segunda condición del rojo; el default
      // (y cualquier config guardada sin la llave) es 'activaSinOt'.
      const criterioRojoSinOrden = source.criterioRojoSinOrden === 'programadaSinArrancar'
        ? 'programadaSinArrancar'
        : 'activaSinOt'
      // 'general' o 'areas' acotan el alcance; cualquier otra cosa es 'ambas', el default.
      const alcanceConjuntos = source.alcanceConjuntos === 'general' || source.alcanceConjuntos === 'areas'
        ? source.alcanceConjuntos
        : 'ambas'
      // Solo un 'rango' explícito devuelve los pickers Inicio/Fin: el default es la semana.
      const modoFiltroFechas = source.modoFiltroFechas === 'rango' ? 'rango' : 'semana'
      // Igual con el nombre de la semana: solo un 'fechas' explícito cambia el default 'wk'.
      const formatoSemana = source.formatoSemana === 'fechas' ? 'fechas' : 'wk'
      // Solo un 'planAtrasado' explícito cambia el criterio; cualquier otra cosa es 'planEnCurso'.
      const modoSinOrdenArrancada = source.modoSinOrdenArrancada === 'planAtrasado' ? 'planAtrasado' : 'planEnCurso'
      // Color hex (#rrggbb o #rrggbbaa); cualquier otra cosa vuelve al rojo por defecto.
      const colorSinOrdenArrancada = esColorHex(source.colorSinOrdenArrancada)
        ? source.colorSinOrdenArrancada
        : def.colorSinOrdenArrancada
      return {
        porcentajeHolgura: eficiencia,
        bandasDesfase: this.normalizarBandas(source.bandasDesfase),
        bandasAvance: this.normalizarBandas(source.bandasAvance, getBandasAvanceDefault()),
        intervaloSeg: intervalo,
        modoPorcentaje,
        modoPantallaCompleta,
        filtroFecha,
        modoFinPeriodo,
        modoDesfase,
        referenciaDesfase,
        ocultarRealesNoIniciadas,
        dejarSiguienteProgramada,
        mostrarLeyenda,
        mostrarProgSinProducir,
        mostrarBotonTodo,
        vistaPorConjunto,
        mostrarTituloGrupos,
        ordenConjuntos,
        alcanceConjuntos,
        mostrarTituloArea,
        modoAdelantada,
        criterioRojoSinOrden,
        modoFiltroFechas,
        formatoSemana,
        modoSinOrdenArrancada,
        colorSinOrdenArrancada,
      }
    },

    // Normaliza una lista de bandas: cada banda { hasta (número|null), color (#hex), etiqueta }.
    // Descarta entradas sin color, ordena ascendente por "hasta" (null al final) y fuerza la última
    // banda como catch-all (hasta = null). Si no hay bandas válidas, usa el default recibido.
    normalizarBandas(bandas, porDefecto) {
      const lista = Array.isArray(bandas) ? bandas : []
      const limpias = lista
        .filter((b) => b && typeof b === 'object' && typeof b.color === 'string' && b.color.trim())
        .map((b) => {
          // OJO: Number(null) y Number('') devuelven 0 (no NaN), así que hay que tratar
          // null/undefined/'' como catch-all (hasta = null) ANTES de convertir a número.
          let hasta = null
          if (b.hasta !== null && b.hasta !== undefined && b.hasta !== '') {
            const n = Number(b.hasta)
            hasta = Number.isFinite(n) ? n : null
          }
          let color = String(b.color).trim()
          // El color-picker (modo hexa) puede devolver 8 dígitos (#rrggbbaa); echarts espera 6.
          if (/^#[0-9a-fA-F]{8}$/.test(color)) color = color.slice(0, 7)
          return {
            hasta,
            color,
            etiqueta: typeof b.etiqueta === 'string' ? b.etiqueta : '',
          }
        })
      if (!limpias.length) return porDefecto || getBandasDesfaseDefault()
      limpias.sort((a, b) => {
        if (a.hasta == null) return 1
        if (b.hasta == null) return -1
        return a.hasta - b.hasta
      })
      // La última banda siempre es el catch-all (sin límite superior).
      limpias[limpias.length - 1].hasta = null
      return limpias
    },

    // Desempaqueta la preferencia: puede venir como string JSON, como { configJson } (camel
    // o Pascal) o como el objeto de configuración directo.
    extraerConfig(pref = null) {
      if (!pref) return {}
      if (typeof pref === 'string') {
        try { return JSON.parse(pref) } catch (e) { return {} }
      }
      if (typeof pref !== 'object') return {}

      const rawConfigJson = pref.configJson ?? pref.ConfigJson
      if (rawConfigJson != null) {
        if (typeof rawConfigJson === 'string') {
          try { return JSON.parse(rawConfigJson) } catch (e) { return {} }
        }
        if (typeof rawConfigJson === 'object') return rawConfigJson
      }
      return pref
    },

    guardarConfigLocal(cfg) {
      const normalizada = this.normalizarConfig(this.extraerConfig(cfg))
      localStorage.setItem(this.codigoReporte, JSON.stringify(normalizada))
    },

    recuperarConfigLocal() {
      try {
        const data = localStorage.getItem(this.codigoReporte)
        if (!data) return this.normalizarConfig()
        return this.normalizarConfig(this.extraerConfig(JSON.parse(data)))
      } catch (e) {
        return this.normalizarConfig()
      }
    },

    // Carga las preferencias desde el backend. Si no existen (404), crea las default y las
    // persiste. Se invoca en cada recarga del reporte; mientras el diálogo está abierto no
    // sobrescribe el estado para no pisar lo que el usuario está editando.
    async cargarPreferencias() {
      if (this.dialogConfig) return
      try {
        const { data } = await axios.get(`ReportesConfig/Obtener/${this.codigoReporte}`)
        const pref = this.normalizarConfig(this.extraerConfig(data))
        this.configReporte = { ...pref }
        this.guardarConfigLocal(pref)
      } catch (e) {
        const def = this.normalizarConfig()
        this.configReporte = { ...def }
        this.guardarConfigLocal(def)
        // No existían preferencias: las creamos con el default.
        this.persistirPreferencias(def)
      }
    },

    // POST de la configuración al backend (ReportesConfig/Guardar).
    async persistirPreferencias(cfg) {
      const pref = this.normalizarConfig(this.extraerConfig(cfg))
      this.configGuardando = true
      try {
        await axios.post('ReportesConfig/Guardar/', {
          codigoReporte: this.codigoReporte,
          configJson: JSON.stringify(pref),
        })
        this.configReporte = { ...pref }
        this.guardarConfigLocal(pref)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error guardando configuración del reporte', e)
      } finally {
        this.configGuardando = false
      }
    },

    // Color de un % de avance de piezas según las bandas configurables (por defecto 0%–33% rojo,
    // 33%–66% amarillo, >66% verde). La última banda (hasta = null) es el catch-all.
    colorPorAvance(pct) {
      const bandas = this.bandasDe('bandasAvance')
      const lista = bandas.length ? bandas : getBandasAvanceDefault()
      for (let i = 0; i < lista.length; i++) {
        const h = lista[i].hasta
        if (h == null || Number(pct) <= Number(h)) return lista[i].color
      }
      return lista[lista.length - 1].color
    },

    // Texto de rango para la leyenda cuando la banda no tiene etiqueta propia.
    rangoBandaLeyenda(i) {
      const bandas = this.bandasLeyenda
      const desde = i === 0 ? 0 : (Number(bandas[i - 1].hasta) || 0)
      const hasta = bandas[i].hasta
      return hasta == null ? `>${desde}% desfase` : `${desde}%–${hasta}% desfase`
    },

    // Horas que le faltan a una orden en curso. Manda la CAVIDAD CON MENOS PIEZAS OK: cada entrada
    // de barra.operaciones es una cavidad con su meta (cantidad), sus piezas OK y su tc; se toma esa
    // cavidad y su tiempo restante es (programadas − ok) × tc. Solo cuentan las OK: las no-ok no
    // acercan el cierre. Empate en ok: gana la de más tiempo por delante. Es el mismo criterio del
    // reporte de Avance de Producción y del Excel. Null si la barra no trae operaciones.
    horasFaltantesBarra(barra) {
      const cavidades = prop(barra, 'operaciones') || []
      let minOk = null
      let segundos = null
      cavidades.forEach((op) => {
        const meta = Number(prop(op, 'cantidad') || 0)
        const okCav = Number(prop(op, 'piezasOk') || 0)
        const tc = Number(prop(op, 'tc') || 0)
        if (!(meta > 0) || !(tc > 0)) return
        const seg = Math.max(0, meta - okCav) * tc
        if (minOk == null || okCav < minOk || (okCav === minOk && seg > segundos)) {
          minOk = okCav
          segundos = seg
        }
      })
      return segundos == null ? null : segundos / 3600
    },

    // Lista de bandas de la config por su llave ('bandasDesfase' | 'bandasAvance').
    bandasDe(clave) {
      const lista = this.configReporte && this.configReporte[clave]
      return Array.isArray(lista) ? lista : []
    },

    // Texto legible del rango de la banda i para el encabezado de su tarjeta.
    bandaRangoTexto(grupo, i) {
      const bandas = this.bandasDe(grupo.clave)
      const desde = this.bandaDesde(grupo.clave, i)
      const b = bandas[i] || {}
      if (i === bandas.length - 1 || b.hasta == null || b.hasta === '') {
        return `${grupo.concepto} de ${desde}% en adelante`
      }
      return `${grupo.concepto} de ${desde}% a ${b.hasta}%`
    },

    // Límite inferior (en %) de la banda i: 0 para la primera, el "hasta" de la anterior para el resto.
    bandaDesde(clave, i) {
      if (i <= 0) return 0
      const prev = this.bandasDe(clave)[i - 1]
      const h = Number(prev && prev.hasta)
      return Number.isFinite(h) ? h : 0
    },

    // Agrega una banda nueva (catch-all) y convierte la anterior última en banda con límite.
    agregarBanda(clave) {
      const bandas = this.bandasDe(clave)
      const ult = bandas[bandas.length - 1]
      const prevHasta = bandas.length >= 2 ? Number(bandas[bandas.length - 2].hasta) : 0
      // La que era catch-all toma un límite (su 'desde' + 5) para dejar hueco a la nueva.
      if (ult) ult.hasta = (Number.isFinite(prevHasta) ? prevHasta : 0) + 5
      bandas.push({ hasta: null, color: (ult && ult.color) || '#d64545', etiqueta: '' })
    },

    quitarBanda(clave, i) {
      const bandas = this.bandasDe(clave)
      if (bandas.length <= 1) return
      bandas.splice(i, 1)
      // La última banda siempre es catch-all (sin límite superior).
      bandas[bandas.length - 1].hasta = null
    },

    // Abre el detalle de máquinas activas: con un área (columna de la tarjeta) solo muestra las
    // máquinas de esa área; con null (Planta o el título de la tarjeta), todas las del tablero.
    abrirActivas(area) {
      this.dialogActivasArea = area ? { id: area.id, nombre: area.nombre } : null
      // Si la config apagó la última pestaña y era la elegida, se vuelve a la primera.
      if (this.tabActivas >= this.tabsActivas.length) this.tabActivas = 0
      this.dialogActivas = true
    },

    abrirConfig() {
      this.configReporte = { ...this.recuperarConfigLocal() }
      this.dialogConfig = true
    },

    // Devuelve el color de "sin orden arrancada" al rojo por defecto (solo ese campo; el resto de
    // la config no se toca). Se aplica al guardar, como cualquier otro cambio del diálogo.
    restablecerColorSinOrden() {
      this.configReporte.colorSinOrdenArrancada = COLOR_SIN_ORDEN_DEFAULT
    },

    cerrarConfig() {
      this.configReporte = { ...this.recuperarConfigLocal() }
      this.dialogConfig = false
    },

    async guardarConfig() {
      const configPrevia = this.recuperarConfigLocal() || {}
      const filtroPrevio = configPrevia.filtroFecha
      const modoFinPrevio = configPrevia.modoFinPeriodo
      const modoDesfasePrevio = configPrevia.modoDesfase
      const filtroNuevo = (this.configReporte || {}).filtroFecha
      const modoFinNuevo = (this.configReporte || {}).modoFinPeriodo
      const modoDesfaseNuevo = (this.configReporte || {}).modoDesfase
      const refDesfasePrevia = configPrevia.referenciaDesfase
      const refDesfaseNueva = (this.configReporte || {}).referenciaDesfase
      await this.persistirPreferencias(this.configReporte)
      // El nuevo intervalo se aplica de inmediato reiniciando la cuenta regresiva.
      if (this.active && this.timerTick) this.reiniciarContador()
      if (!this.configGuardando) this.dialogConfig = false
      // La fecha del filtro la lee el backend al consultar: cambiarla cambia el conjunto de OT,
      // así que hay que volver a pedir el tablero. El modo de desfase también lo aplica el back
      // (color/banda de cada barra), así que cambiarlo obliga a recargar.
      const cambioFinPeriodo = modoFinNuevo !== modoFinPrevio
      const cambioModoDesfase = modoDesfaseNuevo !== modoDesfasePrevio || refDesfaseNueva !== refDesfasePrevia
      if (cambioFinPeriodo) await this.inicializarDesdeHorarios()
      // Al pasar a modo semana, el rango se ajusta a la semana que contiene el Inicio actual para
      // que el selector muestre una opción válida y el periodo sea el mismo que ofrecería.
      const pasoASemana = (this.configReporte || {}).modoFiltroFechas === 'semana'
        && configPrevia.modoFiltroFechas !== 'semana'
      if (pasoASemana && this.semanaSeleccionada) {
        await this.aplicarSemana(this.semanaSeleccionada)
      } else if (filtroNuevo !== filtroPrevio || cambioFinPeriodo || cambioModoDesfase) {
        await this.cargar()
      } else {
        // Ocultar pronósticos y pantalla completa son solo de vista: basta repintar con los mismos datos.
        this.render()
      }
    },

    async reestablecerConfig() {
      const configPrevia = this.recuperarConfigLocal() || {}
      const filtroPrevio = configPrevia.filtroFecha
      const modoFinPrevio = configPrevia.modoFinPeriodo
      const modoDesfasePrevio = configPrevia.modoDesfase
      const def = getDefaultConfigReporte()
      await this.persistirPreferencias(def)
      const cambioFinPeriodo = def.modoFinPeriodo !== modoFinPrevio
      const cambioModoDesfase = def.modoDesfase !== modoDesfasePrevio
        || def.referenciaDesfase !== configPrevia.referenciaDesfase
      if (cambioFinPeriodo) await this.inicializarDesdeHorarios()
      if (def.filtroFecha !== filtroPrevio || cambioFinPeriodo || cambioModoDesfase) await this.cargar()
      else this.render()
    },

    render() {
      if (!this.tablero) return
      const el = this.$refs.grafica
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      this.chart.setOption(this.crearOption(this.datos), true)
      this._carrilesRenderizados = this.claveCarriles(this.datos)
      this.chart.off('click')
      this.chart.on('click', this.onClickFragmento)
      // Sincroniza la barra de fechas fija con el zoom/paneo del gráfico principal.
      this.chart.off('dataZoom')
      this.chart.on('dataZoom', this.sincronizarEjeFecha)
      this.renderEjeFecha()
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
        this.medirStickyTop()
        this.sincronizarEjeFecha()
      })
    },

    // Barra de fechas: segundo gráfico con SOLO el eje de tiempo. Usa los mismos offsets de grid
    // (left/right) y vive en la misma caja que el gráfico, por lo que las fechas quedan alineadas
    // con las líneas verticales del tablero.
    renderEjeFecha() {
      const el = this.$refs.ejeFecha
      if (!el || this.maquinasFiltradas.length === 0) return
      if (!this.ejeChart) this.ejeChart = echarts.init(el)
      this.ejeExtent = { min: this.datos.min, max: this.datos.max }
      this.ejeChart.setOption(this.crearOptionEje(this.datos), true)
      this.$nextTick(() => this.ejeChart && this.ejeChart.resize())
    },

    // Copia al eje fijo la ventana de tiempo visible del gráfico principal, para que el zoom/paneo
    // se refleje en las fechas ancladas.
    sincronizarEjeFecha() {
      if (!this.ejeChart || !this.chart) return
      let ext = null
      try {
        ext = this.chart.getModel().getComponent('xAxis', 0).axis.scale.getExtent()
      } catch (e) {
        ext = null
      }
      if (ext && ext.length === 2 && isFinite(ext[0]) && isFinite(ext[1]) && ext[1] > ext[0]) {
        // Las celdas de fecha de la cabecera siguen la misma ventana que las horas.
        this.ejeExtent = { min: ext[0], max: ext[1] }
        this.ejeChart.setOption({
          xAxis: {
            min: ext[0],
            max: ext[1],
            axisLabel: this.crearEtiquetasEje(ext[0], ext[1]),
            axisTick: this.marcasTickEje(ext[0], ext[1]),
          },
        })
        // Las verticales del tablero siguen al mismo paso que las etiquetas (solo cambian los
        // cortes: no se toca el dataZoom, así que esto no vuelve a disparar el evento).
        this.chart.setOption({ xAxis: { axisTick: this.ticksTablero(ext[0], ext[1]) } })
      }
    },

    // Cortes del eje del TABLERO. OJO: splitLine no acepta customValues; echarts calcula sus
    // líneas con el modelo de axisTick (createAxisTicks → axis.getTickModel()), así que los
    // cortes se declaran aquí para que cada vertical caiga justo debajo de su etiqueta del
    // eje de fechas (fecha en el inicio de jornada, hora en las divisiones).
    ticksTablero(min, max) {
      const valores = this.valoresCorteEje(min, max)
      return valores ? { customValues: valores } : {}
    },

    // axisTick del eje de fechas: una marca por corte (los mismos que las etiquetas).
    marcasTickEje(min, max) {
      const tick = { show: true, lineStyle: { color: '#bdbdbd' } }
      const valores = this.valoresCorteEje(min, max)
      if (valores) tick.customValues = valores
      return tick
    },

    // Mide la altura real de la cabecera fija (leyenda + avance) para anclar la barra de fechas
    // justo debajo, sin importar si la leyenda se envuelve en varias líneas.
    medirStickyTop() {
      const el = this.$refs.stickyTop
      if (el && el.offsetHeight) this.stickyTopH = el.offsetHeight
    },

    // Etiquetas del eje: SOLO la hora (24 h) en cada corte, incluidos los extremos. Las fechas
    // viven en el renglón de arriba de la cabecera (marcasDia), sobre el corte de la mañana.
    crearEtiquetasEje(min, max) {
      const valores = this.valoresCorteEje(min, max)
      return {
        color: '#283139',
        fontWeight: 700,
        fontSize: 10,
        showMinLabel: true,
        showMaxLabel: true,
        // Sin horario de la empresa se dejan los cortes automáticos de echarts.
        ...(valores ? { customValues: valores } : {}),
        // Con rangos raros dos etiquetas pueden quedar encimadas: gana la de la izquierda.
        hideOverlap: true,
        formatter: (value) => {
          const d = new Date(Number(value))
          return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        },
      }
    },

    crearOptionEje(datos) {
      return {
        backgroundColor: 'transparent',
        animation: false,
        grid: { left: this.anchoColMaquina, right: 28, top: 18, bottom: 2 },
        xAxis: {
          type: 'time',
          position: 'top',
          min: datos.min,
          max: datos.max,
          axisLabel: this.crearEtiquetasEje(datos.min, datos.max),
          splitLine: { show: false },
          axisLine: { lineStyle: { color: '#bdbdbd' } },
          axisTick: this.marcasTickEje(datos.min, datos.max),
        },
        yAxis: { type: 'value', show: false, min: 0, max: 1 },
        series: [],
      }
    },

    crearOption(datos) {
      // Span total del eje de tiempo (sin zoom). renderPlan lo compara contra el
      // span visible para saber si hay zoom activo y decidir qué texto mostrar.
      this._fullSpanMs = Math.max(1, datos.max - datos.min)
      const ahora = Date.now()
      const mostrarAhora = ahora >= datos.min && ahora <= datos.max
      const mostrarFin = Number.isFinite(datos.finJornada)
      const locale = this.$i18n.locale
      const tNow = this.$t('Reports.ProductionPlan.chart.now')
      const tEndShift = this.$t('Reports.ProductionPlan.chart.endShift')
      // Etiquetas del tooltip traducidas (se recapturan en cada render; currentLocale() repinta).
      const L = {
        order: this.$t('Reports.ProductionPlan.detail.orderAbbr'),
        start: this.$t('Reports.ProductionPlan.detail.start'),
        end: this.$t('Reports.ProductionPlan.detail.end'),
        scheduledEnd: this.$t('Reports.ProductionPlan.detail.scheduledEnd'),
        finished: this.$t('Reports.ProductionPlan.detail.finishedAt'),
        scheduledTime: this.$t('Reports.ProductionPlan.tooltip.scheduledTime'),
        workedTime: this.$t('Reports.ProductionPlan.tooltip.workedTime'),
      }

      return {
        backgroundColor: '#fff',
        animation: false,

        tooltip: {
          trigger: 'item',
          borderColor: '#e6e9ed',
          formatter: (params) => {
            const v = params.value || []
            const esReal = params.seriesName === 'Real'
            // Fragmento teórico recortado porque la OT terminó antes de su fin programado: se
            // muestran el fin programado y la hora en que terminó.
            const finPlanTip = esReal ? NaN : toMs(prop((params.data && params.data.raw) || {}, 'fin'))
            const recortadaTip = Number.isFinite(finPlanTip) && finPlanTip > v[2]
            const lineas = recortadaTip
              ? [
                `${L.start}: ${fmtFechaHora(v[1], locale)}`,
                `${L.scheduledEnd}: ${fmtFechaHora(finPlanTip, locale)}`,
                `${L.finished}: ${fmtFechaHora(v[2], locale)}`,
              ]
              : [
                `${L.start}: ${fmtFechaHora(v[1], locale)}`,
                `${L.end}: ${fmtFechaHora(v[2], locale)}`,
              ]
            if (esReal) {
              const raw = (params.data && params.data.raw) || {}
              // Tiempo que la orden lleva en proceso. Solo tiene sentido en barras ARRANCADAS: en
              // los pronósticos la geometría es plan, no trabajo. El fin de la barra en curso es
              // una proyección al futuro (el backend le suma el tiempo perdido), así que el corte
              // es "ahora"; en una barra ya terminada el fin real queda en el pasado y manda él.
              if (prop(raw, 'iniciada') === true) {
                const corte = Math.min(Date.now(), v[2])
                const procesoSeg = Math.max(0, Math.round((corte - v[1]) / 1000))
                lineas.push(`${L.workedTime}: ${fmtDuracion(procesoSeg)}`)
              }
              // Tiempo teórico de la ODT tal como lo calcula el backend.
              const durTeoSeg = Number(prop(raw, 'duracionTeoricaSegundos') || 0)
              if (durTeoSeg > 0) lineas.push(`${L.scheduledTime}: ${fmtDuracion(durTeoSeg)}`)
            } else {
              // "Tiempo programado" = duración TOTAL con la eficiencia del plan ya aplicada
              // (base + holgura), no la base al 100%: del inicio al fin PROGRAMADO, que el backend
              // ya ajusta. Si la OT terminó antes, la barra dibujada es más corta pero el tiempo
              // programado no cambia.
              const finPlanSeg = Number.isFinite(finPlanTip) ? finPlanTip : v[2]
              const totalSeg = Math.max(0, Math.round((finPlanSeg - v[1]) / 1000))
              lineas.push(`${L.scheduledTime}: ${fmtDuracion(totalSeg)}`)
            }
            // La OT (clave) va hasta abajo.
            lineas.push(`${L.order}: ${v[3]}`)
            return lineas.join('<br/>')
          },
        },

        grid: { left: this.anchoColMaquina, right: 28, top: 24, bottom: 58 },

        dataZoom: [
          { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
          { type: 'slider', xAxisIndex: 0, height: 20, bottom: 20, filterMode: 'none' },
        ],

        xAxis: {
          type: 'time',
          position: 'top',
          min: datos.min,
          max: datos.max,
          // Etiquetas ocultas: las fechas las pinta la barra fija (pp-eje-fecha) para que queden
          // ancladas al hacer scroll. Aquí se conservan las líneas verticales de guía, en los
          // MISMOS cortes que esas etiquetas (inicio de jornada y sus divisiones), no en las 00:00.
          axisLabel: { show: false },
          axisTick: this.ticksTablero(datos.min, datos.max),
          splitLine: { show: true, lineStyle: { color: COLOR.guia } },
          axisLine: { lineStyle: { color: '#bdbdbd' } },
        },

        yAxis: {
          type: 'category',
          inverse: true,
          data: datos.etiquetas,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: {
            // Ancla a la izquierda: align 'left' + margin grande hace que todas las
            // etiquetas arranquen al mismo x (pegadas al borde izquierdo), sin importar su largo.
            align: 'left',
            margin: this.margenEtiquetaEje,
            // Nombre en rojo: instancia en paro no programado; en amarillo: en paro programado. En
            // ambos casos solo si el contador no la reporta activa (misma regla que las pestañas de
            // paros del reporte de Estado de Máquinas). Si no está en paro pero tiene una orden
            // programada sin arrancar (solo la de ahora, o también las atrasadas, según la config),
            // del color configurado (rojo por defecto).
            // A la derecha, solo si hay orden en curso, sus piezas y horas faltantes.
            formatter: this.formatterNombreEje(datos),
            rich: this.richNombreEje(),
          },
        },

        series: [
          {
            // Corchete de cada grupo de carriles, en el margen izquierdo (fuera del grid: las
            // series custom no se recortan). Sin tooltip ni clic.
            name: 'Grupos',
            type: 'custom',
            z: 0,
            silent: true,
            tooltip: { show: false },
            encode: { x: [1, 2], y: 0 },
            data: datos.corchetes || [],
            renderItem: this.renderCorchete,
          },
          {
            // Máquinas sin plan: una sola barra gris con el horario de la empresa. Sin tooltip ni
            // clic (no representa ninguna ODT), y por debajo de las demás series.
            name: 'Jornada',
            type: 'custom',
            z: 0,
            silent: true,
            tooltip: { show: false },
            encode: { x: [1, 2], y: 0 },
            data: datos.jornada || [],
            renderItem: this.renderJornada,
          },
          this.serie('Plan', datos.plan, 1, this.renderPlan),
          this.serie('Real', datos.real, 3, this.renderReal),
          {
            name: 'Ahora',
            type: 'line',
            data: [],
            silent: true,
            markLine: {
              symbol: 'none',
              label: {
                show: mostrarAhora,
                formatter: tNow,
                color: '#fff',
                backgroundColor: COLOR.ahora,
                padding: [3, 7],
                borderRadius: 3,
                fontWeight: 700,
              },
              lineStyle: { color: COLOR.ahora, type: 'dashed', width: 2 },
              data: mostrarAhora ? [{ xAxis: ahora }] : [],
            },
          },
          {
            name: 'Fin Jornada',
            type: 'line',
            data: [],
            silent: true,
            markLine: {
              symbol: 'none',
              label: {
                show: mostrarFin,
                formatter: tEndShift,
                color: '#fff',
                backgroundColor: COLOR.finJornada,
                padding: [3, 7],
                borderRadius: 3,
                fontWeight: 700,
              },
              lineStyle: { color: COLOR.finJornada, type: 'solid', width: 3 },
              data: mostrarFin ? [{ xAxis: datos.finJornada }] : [],
            },
          },
        ],
      }
    },

    serie(nombre, data, z, renderItem) {
      return {
        name: nombre,
        type: 'custom',
        z,
        encode: { x: [1, 2], y: 0 },
        data,
        renderItem,
      }
    },

    // Geometría compartida: teórico de DOBLE altura (arriba), real de altura simple
    // pegada a la parte inferior. El teórico pinta su dato en la mitad superior, que
    // la barra real no cubre.
    geometriaFila(p0, rowH) {
      const gap = rowH * 0.1
      const realH = rowH * 0.3
      const planH = realH * 2
      const bottomY = p0[1] + rowH / 2 - gap
      return { realH, planH, bottomY }
    },

    // Identidad de los carriles y sus marcos con los que se pintó el tablero (ver cargar/render).
    // Con los conjuntos arriba, las activas cambian el orden del eje; con los conjuntos en su
    // lugar solo cambian las marcas por máquina, que también son una serie y obligan a repintar.
    claveCarriles(datos) {
      const etiquetas = ((datos && datos.etiquetas) || []).join('|')
      const grupos = ((datos && datos.grupos) || []).map((g) => `${g.clave}:${g.idxTitulo}-${g.idxFin}`).join(',')
      return `${etiquetas}#${grupos}`
    },

    // "Activa" según el contador (indiceActivas, "el contador manda"): la máquina del tablero se
    // cruza por idInstancia/idMaquina y, si no hay id, por nombre normalizado. Lo comparten la
    // vista por conjunto (agruparCarriles) y el diálogo de máquinas activas (detalleActivas).
    esMaquinaActiva(m) {
      const { porId, porNombre } = this.indiceActivas
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      return (Number.isFinite(id) && id > 0 && porId.has(id)) || (!!nombre && porNombre.has(nombre))
    },

    // Instancia dentro de un índice { ids, nombres } (instanciasParoNoProg / instanciasParoProg):
    // por idInstancia/idMaquina si lo hay y, si no, por nombre normalizado.
    estaEnIndiceParo(m, indice) {
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      return Number.isFinite(id) && id > 0 ? indice.ids.has(id) : !!nombre && indice.nombres.has(nombre)
    },

    // Paro NO programado que el contador no reporta activo (instanciasParoNoProg). Pinta el nombre
    // en rojo (estilo np) y es una de las dos condiciones del grupo rojo (ver agruparCarriles).
    esParoNoProgramado(m) {
      return this.estaEnIndiceParo(m, this.instanciasParoNoProg)
    },

    // Paro programado que el contador no reporta activo (instanciasParoProg): nombre en amarillo.
    esParoProgramado(m) {
      return this.estaEnIndiceParo(m, this.instanciasParoProg)
    },

    // Instancia con operación abierta según el estado en tiempo real (instanciasConOt,
    // TieneOperacionAbierta): por idInstancia/idMaquina o por nombre normalizado.
    tieneOtAbierta(m) {
      const conOt = this.instanciasConOt
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      return (Number.isFinite(id) && conOt.ids.has(id)) || (!!nombre && conOt.nombres.has(nombre))
    },

    // Molde de la OT abierta de una máquina: el del estado en tiempo real
    // (moldesAbiertosPorInstancia) o, si no lo trae, el de su barra real iniciada que cruza
    // "ahora". '—' si no se conoce.
    moldeAbiertoDeMaquina(m, ahoraMs) {
      const { porId, porNombre } = this.moldesAbiertosPorInstancia
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      const delEstado = (Number.isFinite(id) && id > 0 && porId.get(id)) || (!!nombre && porNombre.get(nombre))
      if (delEstado) return delEstado
      const barra = (prop(m, 'barrasReales') || []).find((b) => {
        if (prop(b, 'iniciada') !== true || prop(b, 'finalizada') === true) return false
        const ini = toMs(prop(b, 'inicio'))
        const fin = toMs(prop(b, 'fin'))
        return Number.isFinite(ini) && Number.isFinite(fin) && ini <= ahoraMs && ahoraMs <= fin
      })
      return (barra && (prop(barra, 'molde') || prop(barra, 'clave'))) || '—'
    },

    // Órdenes (idTrabajoOrden) que la máquina está corriendo: las de sus operaciones abiertas en
    // el estado en tiempo real (otsAbiertasPorInstancia). Si el estado no las trae para la
    // instancia, se cae al tablero: con OT abierta (tieneOtAbierta), sus barras reales iniciadas,
    // no finalizadas y que cruzan "ahora". Set vacío si no corre ninguna.
    otsAbiertasDeMaquina(m, ahoraMs) {
      const { porId, porNombre } = this.otsAbiertasPorInstancia
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      const delEstado = (Number.isFinite(id) && id > 0 && porId.get(id)) || (!!nombre && porNombre.get(nombre))
      if (delEstado) return delEstado
      const ots = new Set()
      if (!this.tieneOtAbierta(m)) return ots
        ; (prop(m, 'barrasReales') || []).forEach((b) => {
          if (prop(b, 'iniciada') !== true || prop(b, 'finalizada') === true) return
          const ini = toMs(prop(b, 'inicio'))
          const fin = toMs(prop(b, 'fin'))
          if (!Number.isFinite(ini) || !Number.isFinite(fin) || ini > ahoraMs || ahoraMs > fin) return
          const idOt = Number(prop(b, 'idTrabajoOrden'))
          if (Number.isFinite(idOt) && idOt > 0) ots.add(idOt)
        })
      return ots
    },

    // Activa según el contador pero SIN operación abierta en el sistema: la máquina produce y
    // nadie arrancó una OT. Segunda condición alternativa del conjunto rojo (criterioRojoSinOrden
    // 'activaSinOt'). No mira el plan ni cambia el color del nombre.
    esActivaSinOt(m) {
      return this.esMaquinaActiva(m) && !this.tieneOtAbierta(m)
    },

    // Orden programada SIN ARRANCAR según el criterio configurado (modoSinOrdenArrancada).
    // Condición previa para los dos criterios: la máquina NO tiene ninguna otra orden abierta
    // (instanciasConOt, TieneOperacionAbierta del estado en tiempo real); si está trabajando
    // cualquier OT, no cuenta. "Sin arrancar" es por OT: la barra teórica no está iniciada (la ODT
    // no ha tenido operaciones reales en el periodo) ni finalizada.
    //  - 'planEnCurso': SOLO la orden programada ahora: la línea de "ahora" cae sobre un fragmento
    //    teórico de la máquina (inicio <= ahora < fin, mismo criterio que resumenPorArea) y esa
    //    OT no se ha arrancado.
    //  - 'planAtrasado': lo anterior MÁS lo atrasado: cualquier fragmento teórico que ya debió
    //    correr (inicio <= ahora, en curso o ya vencido) cuya OT no se ha arrancado.
    // Se lee del payload (barrasTeoricas), no de las barras dibujadas, para que aplique igual en
    // la vista teórica. Pinta el nombre con el color de la config (rojo por defecto; estilo so) y
    // es la otra condición del grupo rojo (ver agruparCarriles).
    tieneOrdenSinArrancar(m, ahoraMs) {
      if (this.tieneOtAbierta(m)) return false
      const incluirAtrasadas = this.modoSinOrdenArrancada === 'planAtrasado'
      return (prop(m, 'barrasTeoricas') || []).some((b) => {
        if (prop(b, 'iniciada') === true || prop(b, 'finalizada') === true) return false
        const ini = toMs(prop(b, 'inicio'))
        if (!Number.isFinite(ini) || ini > ahoraMs) return false
        if (incluirAtrasadas) return true
        const fin = toMs(finTeorica(b))
        return Number.isFinite(fin) && ahoraMs < fin
      })
    },

    // Orden ADELANTADA: la máquina arrancó una orden antes del inicio de su fragmento programado en
    // esa misma máquina (p. ej. el molde de mañana ya está produciendo) y esa orden sigue en
    // curso. Se lee del payload: una barra real iniciada, no finalizada y que cruza "ahora" (el
    // backend estira hasta el corte el fin de la orden en curso; al terminarla, el fin se queda
    // en el real y deja de cruzar), cuyo inicio real es anterior al inicio teórico más temprano
    // de la misma ODT en la máquina. Sin barra teórica de esa ODT no hay plan contra el cual
    // adelantarse. Con modoAdelantada 'hastaFragmento' deja de contar en cuanto "ahora" alcanza
    // ese inicio teórico; con 'hastaFinalizar' cuenta mientras la orden siga en curso. Una orden
    // pausada (sin operación en curso) deja de cruzar "ahora" y sale del conjunto hasta que
    // retome. Es la condición del conjunto naranja (ver agruparCarriles).
    tieneOrdenAdelantada(m, ahoraMs) {
      const teoricas = prop(m, 'barrasTeoricas') || []
      const soloHastaFragmento = this.modoAdelantada === 'hastaFragmento'
      return (prop(m, 'barrasReales') || []).some((r) => {
        if (prop(r, 'iniciada') !== true || prop(r, 'finalizada') === true) return false
        const iniReal = toMs(prop(r, 'inicio'))
        const finReal = toMs(prop(r, 'fin'))
        if (!Number.isFinite(iniReal) || !Number.isFinite(finReal) || iniReal > ahoraMs || ahoraMs > finReal) return false
        const idOt = prop(r, 'idTrabajoOrden')
        const inicios = teoricas
          .filter((t) => prop(t, 'idTrabajoOrden') === idOt)
          .map((t) => toMs(prop(t, 'inicio')))
          .filter((x) => Number.isFinite(x))
        if (!inicios.length) return false
        const iniTeo = Math.min(...inicios)
        if (iniReal >= iniTeo) return false
        return !(soloHastaFragmento && ahoraMs >= iniTeo)
      })
    },

    // Área de una máquina del tablero, en este orden: el campo `area` del tablero V4 (nombre del
    // área de la instancia, que el backend manda desde TableroMaquinaV4ViewModel.Area); si no
    // viene, la de su instancia en el estado en tiempo real (por id o por nombre; ver
    // areasPorInstancia); y si tampoco, la de su primera barra teórica o real (el área de la ODT).
    // Vacío si no se conoce: va a la sección "Sin área" de la posición por área.
    areaDeMaquina(m) {
      const directa = String(prop(m, 'area') || '').trim()
      if (directa) return directa
      const { porId, porNombre } = this.areasPorInstancia
      const id = Number(prop(m, 'idInstancia') ?? prop(m, 'idMaquina'))
      const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
      const delEstado = (Number.isFinite(id) && id > 0 && porId.get(id)) || (!!nombre && porNombre.get(nombre))
      if (delEstado) return delEstado
      const barra = (prop(m, 'barrasTeoricas') || [])[0] || (prop(m, 'barrasReales') || [])[0]
      return String((barra && prop(barra, 'area')) || '').trim()
    },

    // Reparte las máquinas en carriles del eje Y según los dos conjuntos de excepción:
    //  1. Rojo, "Sin arrancar / paro no programado": paro no programado que el contador no
    //     reporta activo (esParoNoProgramado) más una segunda condición que elige la config
    //     (criterioRojoSinOrden): activa según el contador sin OT abierta (esActivaSinOt, el
    //     default, que no cambia el color del nombre) u orden programada sin arrancar
    //     (tieneOrdenSinArrancar, la misma que pinta el nombre en rojo; el color configurado no
    //     influye).
    //  2. Naranja, "Orden adelantada": una orden cuya producción real arrancó antes del inicio de
    //     su fragmento programado en esa máquina y que sigue en curso (tieneOrdenAdelantada);
    //     según modoAdelantada cuenta hasta que la orden se finalice o solo hasta que la hora
    //     actual alcance su fragmento programado. No depende del contador.
    // El rojo tiene prioridad si una máquina cumple los dos.
    // Con ordenConjuntos 'arriba', cada conjunto sube al inicio como un renglón de título más sus
    // máquinas y el resto sigue en el orden del backend; se devuelve dónde empieza y termina cada
    // uno (índices de carril) para el marco. Con 'porArea' el tablero se parte en secciones por
    // área (renglón de título con el nombre; en orden natural, las sin área al final) y dentro de
    // cada una se aplica lo de 'arriba' con las máquinas en orden natural de nombre. Con
    // 'original' nadie se mueve: cada tramo de máquinas consecutivas del mismo conjunto es un
    // grupo (porMaquina) que renderCorchete pinta como punto y línea propios, sin renglón de
    // título. Solo con "Vista por conjunto" encendida, dentro del alcance configurado
    // (conjuntosAplicanAqui) y ya con una lectura del contador; si no, el orden del backend.
    agruparCarriles(maquinas) {
      const sinGrupos = () => ({ carriles: maquinas.map((m) => ({ maquina: m })), grupos: [] })
      if (!this.vistaPorConjunto || !this.maquinasActivasListas || !this.conjuntosAplicanAqui) return sinGrupos()

      const ahora = toMs(prop(this.tablero, 'fechaConsulta')) || Date.now()

      // Segunda condición del rojo según la config (criterioRojoSinOrden): la orden programada sin
      // arrancar (la que pinta el nombre en rojo) o la máquina activa sin OT abierta.
      const segundaRoja = this.criterioRojoSinOrden === 'activaSinOt'
        ? (m) => this.esActivaSinOt(m)
        : (m) => this.tieneOrdenSinArrancar(m, ahora)
      // Conjunto de cada máquina (null = ninguno); el rojo va primero.
      const claveDe = (m) => {
        if (this.esParoNoProgramado(m) || segundaRoja(m)) return 'notStartedOrUnplannedStop'
        return this.tieneOrdenAdelantada(m, ahora) ? 'startedEarly' : null
      }

      if (this.ordenConjuntos === 'original') {
        // Máquinas consecutivas del mismo conjunto comparten una sola marca: el tramo va de la
        // primera a la última (idxTitulo..idxFin); un cambio de conjunto o una máquina sin
        // conjunto lo cierra.
        const grupos = []
        let tramo = null
        maquinas.forEach((m, idx) => {
          const clave = claveDe(m)
          if (tramo && tramo.clave === clave) {
            tramo.idxFin = idx
            return
          }
          tramo = clave
            ? { clave, idxTitulo: idx, idxFin: idx, porMaquina: true, color: GRUPOS_CARRIL[clave].color }
            : null
          if (tramo) grupos.push(tramo)
        })
        return { carriles: maquinas.map((m) => ({ maquina: m })), grupos }
      }

      const carriles = []
      const grupos = []
      // Conjuntos arriba: por cada conjunto no vacío, un renglón de título más sus máquinas y, al
      // final, las máquinas sin conjunto en el orden en que vienen. Anexa a carriles/grupos.
      const anexarConjuntosArriba = (lista) => {
        const bloques = [
          { clave: 'notStartedOrUnplannedStop', maquinas: [] },
          { clave: 'startedEarly', maquinas: [] },
          { clave: null, maquinas: [] },
        ]
        lista.forEach((m) => bloques.find((b) => b.clave === claveDe(m)).maquinas.push(m))
        bloques.forEach((b) => {
          if (!b.maquinas.length) return
          if (!b.clave) {
            b.maquinas.forEach((m) => carriles.push({ maquina: m }))
            return
          }
          const idxTitulo = carriles.length
          carriles.push({ titulo: b.clave })
          b.maquinas.forEach((m) => carriles.push({ maquina: m }))
          grupos.push({ clave: b.clave, idxTitulo, idxFin: carriles.length - 1, color: GRUPOS_CARRIL[b.clave].color })
        })
      }

      if (this.ordenConjuntos === 'porArea') {
        // Una sección por área, en orden natural de nombre (NAVE 1, NAVE 2…) con las máquinas sin
        // área al final; cada una abre con su renglón de título (si mostrarTituloArea) y dentro
        // van los conjuntos arriba y el resto, todas en orden natural de nombre de máquina (101,
        // 102, 103A…).
        const nombreDe = (m) => prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || ''
        const porArea = new Map()
        maquinas.forEach((m) => {
          const area = this.areaDeMaquina(m)
          if (!porArea.has(area)) porArea.set(area, [])
          porArea.get(area).push(m)
        })
        const areas = [...porArea.keys()].sort((a, b) => (a === '') - (b === '') || cmpNatural(a, b))
        areas.forEach((area) => {
          if (this.mostrarTituloArea) carriles.push({ tituloArea: area || this.$t('Reports.ProductionPlan.groups.noArea') })
          anexarConjuntosArriba(porArea.get(area).slice().sort((a, b) => cmpNatural(nombreDe(a), nombreDe(b))))
        })
        return { carriles, grupos }
      }

      anexarConjuntosArriba(maquinas)
      return { carriles, grupos }
    },

    // Marco de grupo: una sola línea vertical de 1.5 px a la izquierda de los nombres. Arranca en
    // la esquina redondeada (8 px) a la altura del título y baja recta hasta el pie del último
    // carril del grupo. Sin línea arriba, abajo ni a la derecha, sin sombreado ni conteo. Va en el
    // hueco ANCHO_CORCHETE; el centro de cada renglón sale del eje de categorías.
    renderCorchete(params, api) {
      const yIni = api.coord([api.value(1), api.value(0)])[1]
      const yUlt = api.coord([api.value(1), api.value(3)])[1]
      if (!Number.isFinite(yIni) || !Number.isFinite(yUlt)) return null
      const rowH = api.size([0, 1])[1]
      const x = COL_INSET + 4
      const color = api.value(4) || COLOR.ahora
      // value[5] = 1 → marca en su lugar (conjuntos sin reordenar): punto y línea sobre el tramo de
      // carriles consecutivos del mismo conjunto (un solo carril si la máquina está aislada).
      if (api.value(5) === 1) return this.renderMarcaMaquina(x, yIni, yUlt, rowH, color)
      if (yUlt <= yIni) return null
      // Pie del último carril, con un respiro para no tocar el título del grupo siguiente.
      const yFin = yUlt + rowH / 2 - 4
      const r = Math.min(8, (yFin - yIni) / 2)
      return {
        type: 'path',
        silent: true,
        shape: {
          pathData: `M${x + r},${yIni} A${r},${r} 0 0 0 ${x},${yIni + r} L${x},${yFin}`,
        },
        style: { fill: 'none', stroke: color, lineWidth: 1.5, lineCap: 'round' },
      }
    },

    // Marca de conjunto en su lugar (ordenConjuntos 'original'): el punto del conjunto en el techo
    // del primer carril del tramo y una línea de 1.5 px hasta el pie del último, ambos de su color
    // y en el hueco ANCHO_CORCHETE. yIni/yUlt son los centros del primer y último carril (el mismo
    // si la máquina está aislada). En filas muy bajas (pantalla completa compacta) el punto se
    // encoge y la línea se omite si ya no cabe.
    renderMarcaMaquina(x, yIni, yUlt, rowH, color) {
      const yTecho = yIni - rowH / 2 + 2
      const yPie = yUlt + rowH / 2 - 2
      const r = Math.max(1.5, Math.min(3, (yPie - yTecho) / 4))
      const y1 = yTecho + r * 2 + 2
      const children = [
        { type: 'circle', silent: true, shape: { cx: x, cy: yTecho + r, r }, style: { fill: color } },
      ]
      if (y1 < yPie) {
        children.push({
          type: 'line',
          silent: true,
          shape: { x1: x, y1, x2: x, y2: yPie },
          style: { stroke: color, lineWidth: 1.5, lineCap: 'round' },
        })
      }
      return { type: 'group', silent: true, children }
    },

    // Barra de jornada (máquinas sin plan): en gris y de MEDIA altura respecto a una teórica, para
    // que un carril vacío no pese lo mismo que uno con plan. Comparte la base con las demás barras.
    renderJornada(params, api) {
      const cat = api.value(0)
      const p0 = api.coord([api.value(1), cat])
      const p1 = api.coord([api.value(2), cat])
      const rowH = api.size([0, 1])[1]
      const { planH, bottomY } = this.geometriaFila(p0, rowH)
      const alto = planH / 2

      const rect = echarts.graphic.clipRectByRect(
        { x: p0[0], y: bottomY - alto, width: p1[0] - p0[0], height: alto },
        { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height }
      )
      if (!rect) return null

      return {
        type: 'rect',
        shape: { x: rect.x, y: rect.y, width: rect.width, height: rect.height, r: 6 },
        style: { fill: COLOR.jornadaFill, stroke: COLOR.jornadaBorde, lineWidth: 1 },
      }
    },

    renderPlan(params, api) {
      const cat = api.value(0)
      const p0 = api.coord([api.value(1), cat])
      const p1 = api.coord([api.value(2), cat])
      const rowH = api.size([0, 1])[1]
      const ancho = p1[0] - p0[0]
      const { planH, realH, bottomY } = this.geometriaFila(p0, rowH)
      const y = bottomY - planH

      const rect = echarts.graphic.clipRectByRect(
        { x: p0[0], y, width: ancho, height: planH },
        { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height }
      )
      if (!rect) return null

      const children = [
        {
          type: 'rect',
          shape: { x: rect.x, y: rect.y, width: rect.width, height: rect.height, r: 6 },
          style: { fill: COLOR.planFill, stroke: COLOR.planBorde, lineWidth: 1 },
        },
      ]

      if (ancho > 64) {
        const molde = api.value(5) || api.value(3)
        // Tiempo programado del fragmento = duración del PLAN (fin programado - inicio, índice 6).
        // Si la OT terminó antes, la barra dibujada es más corta pero el texto sigue diciendo lo
        // que se programó para esas piezas.
        const durPlanMs = Number(api.value(6))
        const durSeg = Math.max(0, Math.round((Number.isFinite(durPlanMs) ? durPlanMs : api.value(2) - api.value(1)) / 1000))
        // ¿Hay zoom activo? Comparamos el span de tiempo visible (deducido de la
        // escala píxeles/ms de esta barra) contra el span total del eje. Sin zoom
        // solo mostramos el molde; el detalle de piezas y duración aparece al hacer zoom.
        const durMs = Math.max(1, api.value(2) - api.value(1))
        const spanVisibleMs = (params.coordSys.width * durMs) / Math.max(1, ancho)
        const hayZoom = spanVisibleMs < (this._fullSpanMs || Infinity) * 0.98
        const pzas = this.$t('Reports.ProductionPlan.summary.pieces')
        const texto = hayZoom
          ? `${molde}      ${fmtNum(api.value(4))} ${pzas}      ${fmtDuracion(durSeg)}`
          : `${molde}`
        // Dato en la mitad SUPERIOR (no la tapa la barra real). Fuente escalada al alto de fila
        // para que en pantalla completa comprimida no se encime con el badge de la barra real.
        const fsPlan = Math.max(8, Math.min(11, Math.round(rowH * 0.26)))
        children.push({
          type: 'text',
          style: {
            x: rect.x + 7,
            y: rect.y + realH / 2,
            text: texto,
            fill: COLOR.planTexto,
            fontSize: fsPlan,
            fontWeight: 700,
            textVerticalAlign: 'middle',
            width: rect.width - 12,
            overflow: 'truncate',
          },
        })
      }

      return { type: 'group', children }
    },

    renderReal(params, api) {
      const cat = api.value(0)
      const p0 = api.coord([api.value(1), cat])
      const p1 = api.coord([api.value(2), cat])
      const rowH = api.size([0, 1])[1]
      const ancho = p1[0] - p0[0]
      const { realH, bottomY } = this.geometriaFila(p0, rowH)
      const y = bottomY - realH // pegada a la parte inferior

      const rect = echarts.graphic.clipRectByRect(
        { x: p0[0], y, width: ancho, height: realH },
        { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height }
      )
      if (!rect) return null

      const color = api.value(6)
      const enCurso = api.value(7) === 1
      const finalizada = api.value(9) === 1
      const children = []

      // Marco EXTERIOR de la barra (fuera del relleno, para no taparlo y verse siempre):
      // verde y más grueso si la ODT está finalizada (tiene prioridad sobre el azul),
      // azul si solo está en curso. El relleno de desfase se conserva intacto.
      const marcoColor = finalizada ? COLOR.finalizada : (enCurso ? COLOR.azul : null)
      if (marcoColor) {
        // El trazo se dibuja centrado sobre el borde, así que al adelgazar el marco de finalizada
        // se acerca también el offset: el hueco con el relleno se mantiene en 2px como el azul.
        const grosor = finalizada ? 1.25 : 2.5
        const off = 2 + grosor / 2
        children.push({
          type: 'rect',
          shape: { x: rect.x - off, y: rect.y - off, width: rect.width + off * 2, height: rect.height + off * 2, r: 8 },
          style: { fill: 'transparent', stroke: marcoColor, lineWidth: grosor },
        })
      }

      children.push({
        type: 'rect',
        shape: { x: rect.x, y: rect.y, width: rect.width, height: rect.height, r: 5 },
        style: { fill: color },
      })

      if (ancho > 58) {
        const badgeW = 52
        const molde = api.value(8) || api.value(3)
        // ODT crítica: el fondo del badge del molde va en rojo (mismo rojo del marco que lleva
        // su barra teórica) en vez del negro translúcido, para ubicarla de un vistazo.
        const critica = api.value(10) === 1
        // Badge del molde: legible pero contenido en la fila. Se centra en la barra real y puede
        // crecer hasta la mitad del alto de fila (el margen que no invade la fila de abajo), así
        // el molde se lee aunque las filas estén comprimidas en pantalla completa.
        const cy = rect.y + rect.height / 2
        const badgeH = Math.min(16, Math.max(rect.height, rowH * 0.5))
        const fsMolde = Math.max(8, Math.min(10, Math.round(badgeH * 0.7)))
        // Solo las críticas llevan fondo, y va en rojo (el mismo de la banda) para ubicarlas de un
        // vistazo. En las demás el molde se escribe directo sobre el relleno de la barra.
        if (critica) {
          children.push({
            type: 'rect',
            shape: { x: rect.x + 4, y: cy - badgeH / 2, width: badgeW, height: badgeH, r: badgeH / 2 },
            style: { fill: BANDA_COLOR.rojo, stroke: '#ffffff', lineWidth: 1 },
          })
        }
        children.push({
          type: 'text',
          style: {
            x: rect.x + 4 + badgeW / 2,
            y: cy,
            text: molde,
            fill: '#ffffff',
            fontSize: fsMolde,
            fontWeight: 800,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            width: badgeW - 6,
            overflow: 'truncate',
          },
        })
        // Sin zoom solo dejamos el molde; las piezas (ok / objetivo) aparecen al hacer zoom.
        const durMs = Math.max(1, api.value(2) - api.value(1))
        const spanVisibleMs = (params.coordSys.width * durMs) / Math.max(1, ancho)
        const hayZoom = spanVisibleMs < (this._fullSpanMs || Infinity) * 0.98
        if (hayZoom && ancho > badgeW + 120) {
          children.push({
            type: 'text',
            style: {
              x: rect.x + 4 + badgeW + 16,
              y: rect.y + rect.height / 2,
              text: `${fmtNum(api.value(4))} / ${fmtNum(api.value(5))} ${this.$t('Reports.ProductionPlan.summary.pieces')}`,
              fill: '#fff',
              fontSize: Math.max(7, fsMolde + 1),
              fontWeight: 700,
              textVerticalAlign: 'middle',
              width: rect.width - badgeW - 34,
              overflow: 'truncate',
            },
          })
        }
      }

      return { type: 'group', children }
    },

    onClickFragmento(p) {
      if (p.seriesName !== 'Plan' && p.seriesName !== 'Real') return
      const v = p.value || []
      const raw = (p.data && p.data.raw) || {}
      const esReal = p.seriesName === 'Real'

      const objetivo = esReal ? Number(v[5] || 0) : Number(v[4] || 0)
      const ok = esReal ? Number(v[4] || 0) : 0
      const noOk = Number(prop(raw, 'piezasNoOk') || 0)
      const faltantes = Math.max(0, objetivo - ok)
      const cumplimiento = objetivo > 0 ? Math.round((ok / objetivo) * 100) : 0
      const durSeg = Math.max(0, Math.round((v[2] - v[1]) / 1000))
      // Fragmento teórico: el fin y la duración que se muestran son los PROGRAMADOS. Si la OT
      // terminó antes de su fin programado, la barra se dibujó hasta la hora en que terminó y esa
      // hora se muestra aparte ("Terminó").
      const finPlanMs = esReal ? NaN : toMs(prop(raw, 'fin'))
      const finMostradoMs = Number.isFinite(finPlanMs) ? finPlanMs : v[2]
      const durPlanSeg = Math.max(0, Math.round((finMostradoMs - v[1]) / 1000))
      const termino = !esReal && Number.isFinite(finPlanMs) && finPlanMs > v[2]
        ? fmtFechaHora(v[2], this.$i18n.locale)
        : null
      const tc = Number(prop(raw, 'tc') || 0)
      const tcReal = esReal && ok > 0 && durSeg > 0 ? Math.round(durSeg / ok) : 0
      const colorHex = esReal ? colorBanda(ok, objetivo, prop(raw, 'color')) : COLOR.planFill

      // ODT "no iniciada": marca autoritativa del backend (la ODT no tiene producción real).
      // No se infiere de barrasReales porque el backend agrega un pronóstico real a TODA teórica.
      const idTrabajoOrden = Number(prop(raw, 'idTrabajoOrden') || 0)
      const iniciada = prop(raw, 'iniciada') === true

      // Piezas que el plan esperaba producidas HASTA AHORA para esta OT: la calcula el backend y la
      // expone en la barra real (null si no hay barra teórica correspondiente).
      const progAhoraRaw = esReal ? prop(raw, 'piezasProgramadasHastaAhora') : null
      const programadasAhora = progAhoraRaw == null ? null : Math.round(Number(progAhoraRaw))

      this.fragmento = {
        esReal,
        idTrabajoOrden,
        numeroVersion: Number(prop(raw, 'numeroVersion') || 1),
        esVigente: prop(raw, 'esVigente') !== false,
        idMolde: Number(prop(raw, 'idMolde') || 0),
        // La proyeccion real se dibuja encima del plan, por lo que normalmente es la barra que
        // recibe el clic. La accion debe estar disponible desde cualquiera de las dos mientras
        // la version vigente no tenga produccion real.
        noIniciada: idTrabajoOrden > 0 && prop(raw, 'esVigente') !== false && !iniciada,
        maquina: this.datos.etiquetas[v[0]],
        area: prop(raw, 'area') || '—',
        molde: prop(raw, 'molde') || (esReal ? v[8] : v[5]) || '—',
        clave: prop(raw, 'clave') || v[3] || '—',
        articulo: prop(raw, 'articulo') || '—',
        inicio: fmtFechaHora(v[1], this.$i18n.locale),
        fin: fmtFechaHora(esReal ? v[2] : finMostradoMs, this.$i18n.locale),
        termino,
        duracion: fmtDuracion(esReal ? durSeg : durPlanSeg),
        // Holgura (solo fragmentos teóricos): colchón de tiempo extra que el backend inyecta al
        // plan. duracionBase = duración al 100%; holgura = segundos extra por eficiencia; la
        // "duracion" de arriba ya es el total ajustado según la geometría de la barra.
        holguraSeg: !esReal ? Number(prop(raw, 'holguraSegundos') || 0) : 0,
        holgura: !esReal ? fmtDuracion(Number(prop(raw, 'holguraSegundos') || 0)) : '',
        duracionBase: !esReal ? fmtDuracion(Number(prop(raw, 'duracionTeoricaSegundos') || 0)) : '',
        // Tiempo teórico planificado de la ODT CON HOLGURA (duración base + colchón): es la
        // referencia contra la que el backend mide el desfase. En las barras reales el backend
        // ya lo expone ajustado; en las teóricas es la duración programada del fragmento.
        duracionTeorica: esReal
          ? (Number(prop(raw, 'duracionTeoricaSegundos') || 0) > 0
            ? fmtDuracion(Number(prop(raw, 'duracionTeoricaSegundos')))
            : null)
          : fmtDuracion(durPlanSeg),
        objetivo,
        programadasAhora,
        ok,
        noOk,
        faltantes,
        cumplimiento,
        cumplimientoBar: Math.min(100, cumplimiento),
        tc: tc || 0,
        tcReal,
        // Desfase de TIEMPO (fin real vs fin programado, sobre la duración programada CON
        // HOLGURA) que envía el backend.
        desfaseSeg: esReal ? Number(prop(raw, 'desfaseSegundos') || 0) : 0,
        desfasePct: esReal ? Number(prop(raw, 'desfasePorcentaje') || 0) : 0,
        // Desfase por PIEZAS (ok vs programadas hasta ahora) que envía el backend.
        desfasePiezas: esReal ? Number(prop(raw, 'desfasePiezas') || 0) : 0,
        desfasePiezasPct: esReal ? Number(prop(raw, 'desfasePiezasPorcentaje') || 0) : 0,
        enCurso: esReal ? v[7] === 1 : false,
        // OT finalizada (algún TrabajoOrdenDetalle activo en EstadoProduccion 5): marca del backend.
        finalizada: prop(raw, 'finalizada') === true || (esReal && v[9] === 1),
        colorHex,
        bandaKey: esReal ? bandaKey(ok, objetivo, prop(raw, 'color')) : '',
        // Etiqueta de banda configurable que manda el backend (tiene prioridad en bandaLabel).
        bandaEtiqueta: esReal ? (prop(raw, 'bandaEtiqueta') || '') : '',
        // Detalle por operación (cavidad). Plan: solo esperadas; Real: + hechas/ok/noOk.
        operaciones: (prop(raw, 'operaciones') || []).map((o) => ({
          idOperacion: prop(o, 'idOperacion'),
          codigo: prop(o, 'codigo'),
          tc: Number(prop(o, 'tc') || 0),
          esperadas: Number(prop(o, 'cantidad') || 0),
          ok: Number(prop(o, 'piezasOk') || 0),
          noOk: Number(prop(o, 'piezasNoOk') || 0),
          totales: Number(prop(o, 'piezasTotales') || 0),
        })),
      }
      this.dialogFragmento = true
    },
  },
}
</script>

<style scoped>
.board-pp {
  --ink: #283139;
  --muted: #6b7682;
  --line: #e6e9ed;
  --blue: #2563d6;
  background: #fff;
  padding: 16px 18px 22px;
}

/* Pantalla completa: la tarjeta llena la pantalla y permite scroll de todo el contenido */
.board-pp:fullscreen,
.board-pp:-webkit-full-screen {
  background: #fff;
  overflow: auto;
  width: 100vw;
  height: 100vh;
  border-radius: 0 !important;
  box-shadow: none !important;
  position: relative;
}

/* En pantalla completa el botón "Salir" no debe robar una fila: flota arriba a la derecha.
   La fila de filtros/toggle está oculta, así que la toolbar queda vacía y colapsa. */
.board-pp:fullscreen .toolbar,
.board-pp:-webkit-full-screen .toolbar {
  min-height: 0;
  overflow: visible;
}

.board-pp:fullscreen .tb-actions,
.board-pp:-webkit-full-screen .tb-actions {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 10;
}

.board-pp:fullscreen .pp-toprow,
.board-pp:-webkit-full-screen .pp-toprow {
  margin-top: 0;
  /* reserva el ancho del botón flotante para que no tape la card */
  padding-right: 100px;
}

.board-pp:fullscreen .legend,
.board-pp:-webkit-full-screen .legend {
  margin: 6px 2px 2px;
}

.board-pp:fullscreen .grafica-box,
.board-pp:-webkit-full-screen .grafica-box {
  margin-top: 6px;
  padding-top: 4px;
}

/* El fondo detrás del elemento en pantalla completa (por defecto es negro) */
.board-pp:fullscreen::backdrop {
  background: #fff;
}

.board-pp:-webkit-full-screen::backdrop {
  background: #fff;
}

.pp-fs-btn {
  height: 40px !important;
  border-radius: 10px !important;
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: nowrap;
  overflow-x: auto;
}

/* Timer + botones (config + pantalla completa) a la derecha de la fila. */
.tb-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* Sentinela del scroll infinito del selector de semanas: invisible, pero con alto para que el
   observador de intersección lo detecte al llegar al final de la lista. */
.pp-semanas-mas {
  height: 8px;
}

/* Selector de semana: un poco más ancho que el de vista, para que quepa el rango de fechas. */
.f-sel--semana {
  min-width: 240px;
}

/* Check "Mostrar todo": compacto, para que no crezca la altura de la toolbar. */
.pp-todo-check {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.pp-todo-check >>> label {
  font-size: 13px;
  color: var(--ink);
  white-space: nowrap;
}

/* Filtros dentro de la toolbar: sin margen superior, alineados en la misma fila. */
.toolbar .filtros {
  margin: 0;
}

.seg-toggle {
  display: inline-flex;
  background: #eef1f4;
  border-radius: 999px;
  padding: 3px;
}

.seg-toggle button {
  border: 0;
  background: transparent;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}

.seg-toggle button.act {
  background: var(--blue);
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .15);
}

.orden-note {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}

.auto-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
  background: #f1f4f8;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 14px;
  white-space: nowrap;
}

.auto-refresh .v-icon {
  color: var(--blue);
}

.auto-refresh.is-loading {
  color: var(--muted);
  background: #f1f3f6;
  border-color: var(--line);
}

.auto-refresh .spin {
  animation: pp-spin 1s linear infinite;
}

@keyframes pp-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ===== Dos tarjetas resumen en el MISMO renglón:
   (Planta + Máquinas activas) | (Avance de piezas + Programadas al momento). ===== */
.pp-toprow {
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: nowrap;
  margin-top: 4px;
}

/* Cada tarjeta: fondo blanco, borde gris claro, radio ~10px y sombra muy ligera. */
.pp-topcard {
  display: flex;
  align-items: center;
  gap: 8px 18px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(20, 40, 80, .06);
  padding: 8px 16px;
  min-width: 0;
}

/* La tarjeta de avance es la más grande de la fila (40% / 60%): son los números que se leen
   primero y la barra necesita el ancho. */
.pp-topcard--maq {
  flex: 4 1 0;
}

.pp-topcard--avance {
  flex: 6 1 0;
  /* La barra de avance se estira hasta aquí: este hueco es el que la separa de
     "Programadas al momento". */
  column-gap: 28px;
}

.pp-sec {
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Sin min-width:0: así una sección no se encoge por debajo de su contenido; cuando no
     cabe, la fila baja a otra línea (flex-wrap) en vez de que el texto se encime. */
}

.pp-sec--planta {
  flex: 0 0 auto;
  cursor: pointer;
}

.pp-sec--maq {
  flex: 1 1 auto;
  cursor: pointer;
}

/* Avance: toma parte del espacio sobrante para estirar la barra, pero menos que antes
   para dejarle más aire a "Máquinas activas". Sin min-width fijo: si no cabe, baja de fila. */
.pp-sec--avance {
  flex: 1 1 200px;
}

.pp-sec--prog {
  flex: 0 0 auto;
}

/* Título de cada sección (Planta / Máquinas activas / Avance pzas / Programadas). */
.pp-sec-title {
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: .03em;
  white-space: nowrap;
}

.pp-sec-title--center {
  text-align: center;
}

/* "Programadas al momento": etiqueta en peso normal (el número va en negritas). */
.pp-sec-title--normal {
  font-weight: 400;
}

.pp-sec-loader {
  align-self: flex-start;
  min-height: 28px;
}

/* Avance: piezas · barra · porcentaje en una misma línea. */
.pp-avance-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

/* Responsive: al angostarse, la barra roja desaparece; siempre quedan las piezas y el
   porcentaje (lo esencial). Las secciones que no caben bajan a otra fila sin encimarse. */
@media (max-width: 1100px) {
  .rs-bar {
    display: none;
  }
}

.filtros {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  flex-wrap: nowrap;
}

/* Inputs de filtro: se reparten el ancho disponible (crecen por igual) en lugar de un
   ancho fijo, y bajan a otra fila en pantallas chicas. El texto va como label dentro. */
.filtros .f-input {
  flex: 1 1 130px;
  width: auto;
  min-width: 120px;
}

.filtros .f-input--areas {
  flex: 1.3 1 150px;
  min-width: 130px;
}

/* Todos los controles del filtro comparten el mismo lenguaje: alto 40px, radio 10px
   y borde neutro que vira a azul al enfocar. */
.filtros .f-input >>> .v-input__slot {
  border-radius: 10px !important;
}

.filtros .f-input >>> fieldset {
  border-radius: 10px;
  border-color: var(--line);
}

.filtros .f-input.v-input--is-focused >>> fieldset {
  border-color: var(--blue);
}

/* Datepicker CTK (Inicio / Fin): mismo alto, radio y borde que los selects. */
.filtros .f-input >>> .field-input {
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 10px !important;
  border: 1px solid var(--line) !important;
  box-shadow: none !important;
  font-size: 13px !important;
  color: var(--ink) !important;
}

.filtros .f-input >>> .field.is-focused .field-input {
  border-color: var(--blue) !important;
}

/* "Vista": etiqueta y valor EN LÍNEA (Vista · Tiempo Real), no la etiqueta arriba.
   El recuadro (borde/alto/radio) lo dibuja .f-sel; el v-select interno va sin borde propio. */
.filtros .f-sel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  height: 40px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}

.filtros .f-sel:focus-within {
  border-color: var(--blue);
}

/* Etiqueta "Vista" y valor "Tiempo Real": mismo color, tamaño y peso, en la misma línea. */
.filtros .f-sel-lbl {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}

.filtros .f-sel-input {
  flex: 1 1 auto;
  min-width: 0;
}

/* El v-select interno pierde su recuadro/alto propio: el marco lo pone .f-sel. */
.filtros .f-sel >>> .v-input__slot {
  min-height: 0 !important;
  padding: 0 !important;
}

.filtros .f-sel >>> fieldset {
  border: none !important;
}

.filtros .f-sel >>> .v-select__selections {
  padding: 0 !important;
  min-height: 0 !important;
  justify-content: flex-end;
}

/* El valor seleccionado igualado a la etiqueta (mismo color/tamaño/peso). */
.filtros .f-sel >>> .v-select__selection,
.filtros .f-sel >>> .v-select__selections input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  color: var(--muted) !important;
}

.filtros .f-sel >>> .v-input__append-inner {
  margin-top: 0 !important;
  padding-left: 2px !important;
  align-self: center;
}

.f-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.f-field.f-grow {
  flex: 1;
  min-width: 140px;
}

.f-field label {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .03em;
}

.f-field.f-grow {
  min-width: 200px;
}

/* Filtro de áreas: más corto, no crece a lo ancho como los demás. */
.f-field.f-areas {
  flex: 0 1 260px;
  min-width: 170px;
  max-width: 300px;
}

/* Datepicker: el activador (v-text-field) ocupa el mismo ancho que los selects */
.f-field>>>.v-text-field {
  min-width: 150px;
}

/* Overrides del v-date-picker: el _tables.scss global rompe la tabla del
   calendario (filas/colores). Estas reglas la neutralizan, igual que en
   el componente MenuDatepicker. */
.pp-date-picker>>>.v-btn--rounded {
  border-radius: 28px !important;
}

.pp-date-picker>>>.v-btn.v-btn--disabled {
  color: rgba(0, 0, 0, .26) !important;
}

.pp-date-picker>>>.v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
  font-family: Roboto, sans-serif;
  font-weight: 500 !important;
  padding: 0;
}

.pp-date-picker>>>.v-date-picker-table .v-btn.v-btn--active {
  color: #fff !important;
}

.pp-date-picker>>>.accent {
  background-color: #005caf !important;
  border-color: #005caf !important;
}

.pp-date-picker>>>.accent--text {
  color: #005caf !important;
  caret-color: #005caf !important;
}

.pp-date-picker>>>.v-btn__content {
  font-family: Roboto, sans-serif;
  font-weight: 500 !important;
  flex-grow: 1;
}

.pp-date-picker>>>tbody tr:nth-of-type(odd) {
  background-color: transparent;
}

.pp-date-picker>>>.v-btn--active:before,
.pp-date-picker>>>.v-btn:focus:before,
.pp-date-picker>>>.v-btn:hover:before {
  background-color: rgb(85, 85, 85);
}

.pp-date-picker>>>.v-date-picker-header__value button {
  font-size: 1rem;
}

.f-field>>>.v-input {
  margin: 0;
  padding: 0;
  font-size: 13px;
}

.f-field>>>.v-input__control {
  min-height: 34px;
}

.f-field>>>.v-select__selections {
  min-height: 32px;
}

.f-aplicar {
  align-self: center;
  height: 40px !important;
  border-radius: 10px !important;
  flex: 0 0 auto;
}

.tb-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-grp {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fg-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
}

.chip {
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.chip.act {
  border-color: var(--blue);
  color: var(--blue);
  background: #f0f5ff;
  font-weight: 600;
}

.pop {
  background: #fff;
  padding: 8px 12px;
  display: grid;
  gap: 2px;
  max-height: 320px;
  overflow: auto;
}

.pop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
  cursor: pointer;
  color: var(--ink);
}

.pop-item.pop-all {
  border-bottom: 1px solid var(--line);
  padding-bottom: 6px;
  margin-bottom: 4px;
  font-weight: 600;
}

/* KPI Máquinas activas (réplica de ReporteCumplimientoProduccion) */
.pp-kpi-grid {
  margin: 0 0 14px;
}

.cp-kpi-grid {
  display: grid;
  align-items: stretch;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.cp-kpi-card {
  background: linear-gradient(180deg, #f8fbff 0%, #f2f6fc 100%);
  border: 1px solid #c4d5ea;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  min-height: 130px;
  padding: 14px 16px 12px;
}

.cp-kpi-card--blue {
  border-color: #8fc1f3;
}

.cp-kpi-top {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.cp-kpi-top-main {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
}

.cp-kpi-eye-btn {
  height: 26px !important;
  margin-left: 8px;
  width: 26px !important;
}

.cp-kpi-eye-icon {
  color: #9aa6b8 !important;
  font-size: 1.1rem !important;
}

.cp-kpi-icon-pack {
  align-items: center;
  border-radius: 16px;
  display: inline-flex;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.cp-kpi-icon-pack--blue {
  background: linear-gradient(145deg, #d8e9fb, #c6def7);
}

.cp-kpi-icon-main {
  color: #2e8aed !important;
  font-size: 1.45rem !important;
}

.cp-kpi-title {
  color: #1c2639;
  font-size: clamp(1.05rem, 1.1vw, 1.3rem);
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
  text-align: left;
}

.cp-kpi-value-row {
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 8px 0 4px;
  text-align: center;
  width: 100%;
}

.cp-kpi-value-main {
  font-size: 52px !important;
  font-weight: 800;
  line-height: 0.9;
  margin: 0;
}

.cp-kpi-value-main--blue {
  color: #2d84da;
}

/* Spinner que reemplaza al número mientras cargan las máquinas activas. La fila usa
   align-items: baseline; lo centramos para que no quede desalineado con el "/ total". */
.cp-kpi-value-loader {
  align-self: center;
}

.cp-kpi-value-secondary {
  color: #667791;
  font-size: 27px !important;
  font-weight: 700;
  line-height: 1;
  margin: 0;
}

.cp-kpi-caption {
  color: #657993;
  font-size: clamp(0.9rem, 0.95vw, 1rem);
  font-weight: 500;
  line-height: 1.1;
  margin: auto 0 0;
}

/* ---- Variante horizontal del KPI: sin ícono, con los elementos repartidos a lo
   ancho (título + ojo · valor · caption). Prioriza el uso del ancho sobre la altura. ---- */
.cp-kpi-card.cp-kpi-card--row {
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 0;
  padding: 10px 18px;
  cursor: pointer;
}

.cp-kpi-head {
  align-items: center;
  display: flex;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
}

.cp-kpi-card--row .cp-kpi-title {
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cp-kpi-card--row .cp-kpi-eye-btn {
  margin-left: 0;
}

.cp-kpi-card--row .cp-kpi-value-row {
  align-items: baseline;
  gap: 6px;
  justify-content: center;
  margin: 0;
  width: auto;
  flex: 0 0 auto;
  white-space: nowrap;
}

.cp-kpi-card--row .cp-kpi-value-main {
  font-size: 30px !important;
  line-height: 1;
}

.cp-kpi-card--row .cp-kpi-value-secondary {
  font-size: 20px !important;
}

.cp-kpi-card--row .cp-kpi-caption {
  font-size: 0.85rem;
  margin: 0;
  white-space: nowrap;
}

/* ---- Sección "Máquinas activas": naves en fila, compactas y sin divisores. El número
   (activas/esperadas) va en azul y el nombre de la nave debajo. ---- */
/* Naves distribuidas de forma simétrica dentro de la sección, para que el título
   (centrado) siempre quede en medio, ya sean 2, 3 o 4 áreas. */
.pp-ma-cols {
  display: flex;
  align-items: flex-start;
  justify-content: space-evenly;
  gap: 4px 18px;
  flex-wrap: wrap;
  width: 100%;
}

/* Columna de área de la tarjeta: clic abre el detalle de activas solo de esa área. La zona
   clicable (y el resaltado al pasar) la pone el ::before, que sobresale unos px sin ocupar
   espacio: el layout y el tamaño de los textos no cambian. Sobresale menos que el gap entre
   columnas (18 px) para no encimarse con la vecina. */
.pp-ma-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 38px;
  cursor: pointer;
  position: relative;
}

.pp-ma-col::before {
  content: '';
  position: absolute;
  top: -6px;
  right: -8px;
  bottom: -6px;
  left: -8px;
  border-radius: 8px;
}

.pp-ma-col:hover::before {
  background: rgba(45, 132, 218, 0.08);
}

.pp-ma-val {
  color: #2d84da;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

/* El contador de Planta resalta más que el de las naves. */
.pp-sec--planta .pp-ma-val {
  font-size: 21px;
}

.pp-ma-area {
  color: #657993;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  margin-top: 2px;
  text-align: center;
  white-space: nowrap;
}

/* Nombre de la planta: alineado a la izquierda y algo más presente que las naves. */
.pp-ma-area--planta {
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  text-align: left;
  margin-top: 2px;
  text-transform: uppercase;
}

/* Leyenda */
/* Leyenda + avance total: se mantienen fijos en la parte superior al hacer scroll */
.pp-sticky-top {
  position: sticky;
  top: 0;
  z-index: 6;
  background: #fff;
  /* compensa el padding lateral de .board-pp para que el fondo cubra de borde a borde */
  margin: 0 -18px;
  padding: 4px 18px 2px;
}

.legend {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: center;
  margin: 14px 2px 4px;
}

.lg {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: var(--muted);
}

.lg i {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
}

.lg i.i-plan {
  background: #dcf0d6;
  border: 1.5px solid #7cbd74;
}

/* Indicador de ODT finalizada: cuadro transparente con borde grueso verde. */
.lg i.i-finalizada {
  background: transparent;
  border: 3px solid #22a84e;
}

/* Nombre de instancia en rojo (paro no programado), amarillo (paro programado) o del color
   configurado (sin orden arrancada con plan en curso; va inline): muestra de texto, no cuadro.
   Los colores deben coincidir con los estilos np / pp / so de richNombreEje. */
.lg i.i-paro-np,
.lg i.i-paro-p,
.lg i.i-sin-orden {
  width: auto;
  height: auto;
  border-radius: 0;
  font-style: normal;
  font-weight: 800;
  font-size: 13px;
  line-height: 1;
  color: #E83535;
}
.lg i.i-paro-p {
  color: #D99A00;
}

/* Avance de piezas: el dato principal (producidas / objetivo), resaltado. */
.rs-piezas {
  font-size: 21px;
  font-weight: 800;
  color: var(--ink);
  line-height: 1;
  letter-spacing: -.02em;
  white-space: nowrap;
}

/* Barra de progreso: horizontal, con bordes redondeados. Se estira con el espacio sobrante
   de su sección; el aire con "Programadas al momento" lo pone el column-gap de la tarjeta. */
.rs-bar {
  flex: 1 1 110px;
  min-width: 40px;
  height: 10px;
  border-radius: 999px;
  background: #eef1f5;
  overflow: hidden;
}

.rs-fill {
  height: 100%;
  border-radius: 999px;
  transition: width .3s ease;
}

/* Porcentaje: a la derecha de la barra, prominente (color según el desfase). */
.rs-pct {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -.02em;
  white-space: nowrap;
}

/* "Programadas al momento": fracción y % en la misma línea, bajo su título. */
.rs-now-row {
  display: flex;
  align-items: baseline;
  gap: 18px;
}

/* % producido contra lo programado al momento: mismo color que la barra de avance. */
.rs-now-pct {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -.02em;
  white-space: nowrap;
}

/* "Programadas al momento": fracción alineada bajo su título. Un punto por debajo del % que
   lleva al lado, que es el dato que manda en esa sección. */
.rs-now-val {
  font-size: 15px;
  font-weight: 540;
  color: var(--ink);
  line-height: 1;
  white-space: nowrap;
}


.cp-empty {
  padding: 40px 4px;
  color: var(--muted);
  font-size: 14px;
  text-align: center;
}

/* Caja contenedora de la gráfica */
.grafica-box {
  border: 1px solid var(--line);
  border-top: none;
  border-radius: 0 0 12px 12px;
  background: #fff;
  padding: 8px 14px;
  margin-top: 8px;
}

/* Barra de fechas (eje de tiempo) fija dentro de la caja, anclada bajo la cabecera. Comparte
   el ancho y el offset de grid del gráfico principal, por lo que las fechas quedan alineadas
   con las líneas verticales. `top` se calcula con la altura real de la cabecera fija. */
/* Cabecera de tiempo: fechas arriba, horas abajo. Es la que queda fija al hacer scroll. */
.pp-eje-wrap {
  position: sticky;
  z-index: 4;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid var(--line);
}

/* Renglón de fechas: el padding izquierdo reserva la columna de instancias (mismo offset que el
   grid del gráfico) y el derecho, el margen del eje; la pista interior mide justo el área de
   dibujo, así que los porcentajes caen sobre el corte exacto de cada día. */
.pp-eje-dias {
  height: 22px;
  padding-right: 28px;
  box-sizing: border-box;
}

.pp-dias-track {
  position: relative;
  height: 100%;
}

/* Cada fecha va centrada sobre su corte de la mañana, alineada con la hora que tiene debajo.
   El tamaño es el mismo que el de las horas (axisLabel del eje: fontSize 10). */
.pp-dia {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  line-height: 22px;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 800;
  color: #283139;
}

.pp-eje-fecha {
  height: 30px;
  width: 100%;
  background: #fff;
}

.grafica-tablero {
  width: 100%;
  min-height: 360px;
}

.frag-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  display: flex;
  align-items: center;
}

/* OT finalizada: marco y sombra verdes en el encabezado (Real · área) para que resalte. */
.frag-title.frag-title--fin {
  border: 2px solid #22a84e;
  border-radius: 8px;
  background: rgba(34, 168, 78, 0.10);
  box-shadow: 0 0 0 3px rgba(34, 168, 78, 0.18);
  padding: 8px 12px;
  margin: 4px 4px 0;
}

/* Los botones de acciones pueden saltar de línea para no desbordar la carta
   ni generar scroll horizontal cuando hay varias acciones visibles. */
.frag-actions {
  flex-wrap: wrap;
  row-gap: 4px;
}

.frag-actions .v-btn {
  flex: 0 1 auto;
  min-width: 0;
}

.cambio-lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  margin: 12px 0 4px;
}

/* Encabezado de una sección del diálogo de configuración: agrupa varios ajustes afines
   (p. ej. "Agrupadores"). Los ajustes de la sección siguen con su .cambio-lbl normal. */
.cfg-seccion {
  font-size: 13px;
  font-weight: 800;
  color: var(--ink);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Dato informativo (no editable) de la configuración: se ve como valor, no como campo. */
.jornada-val {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  text-transform: capitalize;
}

.cambio-error {
  background: #fdecec;
  color: #b42318;
  border: 1px solid #f3c6c2;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  margin-bottom: 8px;
}

/* Editor de bandas de color por desfase (tarjetas) */
.bandas-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.banda-card {
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  background: #fafbfc;
  padding: 10px 12px;
}

.banda-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.banda-swatch {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: block;
}

/* Config de un solo color (sin orden arrancada): swatch, muestra "Aa" del color, hex y botón
   para volver al rojo por defecto, en un renglón. */
.color-cfg-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}
.color-cfg-muestra {
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
}

.banda-rango-txt {
  font-weight: 700;
  font-size: 13.5px;
  color: var(--ink, #283139);
}

.banda-card-body {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.banda-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.banda-field--grow {
  flex: 1 1 auto;
}

.banda-field label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7682;
}

.banda-hasta-input {
  max-width: 120px;
}

.banda-ultima {
  flex: 0 0 auto;
  font-size: 12px;
  font-style: italic;
  color: #6b7682;
  padding-bottom: 8px;
}

.frag-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f6;
  font-size: 13.5px;
}

.frag-row:last-child {
  border-bottom: 0;
}

.frag-row span {
  color: var(--muted);
}

.frag-row b {
  color: var(--ink);
  font-weight: 700;
  text-align: right;
}

/* Fila de máquina activa: nombre con punto de estado + badge Con/Sin OT + detalle */
/* Nombre de la máquina en las filas del diálogo de activas: un punto más grande que el molde
   (que se queda en el 13.5px de .frag-row). */
.act-name {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  color: var(--ink);
  font-size: 15px;
  font-weight: 700;
}

/* Área elegida en la tarjeta: caption arriba del resumen del diálogo de activas. */
.act-area {
  color: var(--muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 12px 16px 0;
  text-transform: uppercase;
}

/* Resumen del diálogo de activas: un conteo por pestaña, arriba de las pestañas. Cada uno es un
   botón que abre su pestaña; el de la pestaña visible va resaltado. */
.act-resumen {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  padding: 14px 16px 12px;
}

.act-stat {
  align-items: center;
  background: #f6f8fa;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font: inherit;
  gap: 2px;
  padding: 8px 4px;
}

.act-stat--sel {
  background: #eef3fd;
  border-color: #2563d6;
}

.act-stat-val {
  color: var(--ink);
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
}

.act-stat-lbl {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

/* Pestaña "Prog sin arrancar": su cuadro del resumen y sus nombres de máquina van en ámbar, el
   mismo del nombre en paro programado del eje (estilo pp de richNombreEje). */
.act-stat--ambar .act-stat-val {
  color: #d99a00;
}

.act-stat--ambar.act-stat--sel {
  background: #fff7e6;
  border-color: #d99a00;
}

.act-name--ambar {
  color: #d99a00;
}

/* Chip entre el nombre y el molde en las filas del diálogo de activas (tabsActivas): en "Act c.
   prog. desfasado" el caso del desfase (atrasada en el rojo del conjunto sin arrancar, adelantada
   en el naranja del conjunto adelantada; ver GRUPOS_CARRIL) y en "Prog sin arrancar" la vencida,
   en el ámbar de esa pestaña. */
.act-name .act-chip {
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1;
  padding: 3px 7px;
  text-transform: uppercase;
  white-space: nowrap;
}

.act-name .act-chip--atrasada {
  background: #fdecec;
  color: #E0524D;
}

.act-name .act-chip--adelantada {
  background: #fff4dd;
  color: #F2A31B;
}

.act-name .act-chip--vencida {
  background: #fff7e6;
  color: #d99a00;
}

/* Estados de "Prog sin producir": paro no programado y programado en los colores del nombre en
   el eje (estilos np / pp de richNombreEje), sin actividad en azul grisáceo, pausada en gris y
   finalizada en el verde del marco de OT finalizada. */
.act-name .act-chip--paroNoProg {
  background: #fdecec;
  color: #E83535;
}

.act-name .act-chip--paroProg {
  background: #fff7e6;
  color: #D99A00;
}

.act-name .act-chip--sinActividad {
  background: #e8eef7;
  color: #3b5b8a;
}

.act-name .act-chip--pausada {
  background: #eceff3;
  color: #5c6875;
}

.act-name .act-chip--finalizada {
  background: #e6f6ec;
  color: #22a84e;
}

.act-right {
  align-items: center;
  display: inline-flex;
  gap: 10px;
}

.frag-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  margin-right: 8px;
}

/* OT finalizada: marco verde grueso alrededor del indicador (respeta su color de relleno),
   igual que el borde de las barras finalizadas de la gráfica. */
.frag-dot.frag-dot--fin {
  box-shadow: 0 0 0 2px #22a84e;
}

.frag-banda {
  font-size: 12px;
  font-weight: 700;
}

.frag-rango {
  display: inline-flex;
  align-items: center;
}

.frag-sec {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #97a0aa;
  margin: 12px 0 2px;
}

.frag-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 18px;
}

.frag-row--wide {
  grid-column: 1 / -1;
}

.frag-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.frag-progress-bar {
  flex: 1;
  height: 8px;
  border-radius: 5px;
  background: #e9edf2;
  overflow: hidden;
}

/* OT finalizada: marco verde alrededor de la barra de progreso (respeta el color del relleno). */
.frag-progress-bar.frag-progress-bar--fin {
  box-shadow: 0 0 0 2px #22a84e;
}

.frag-progress-fill {
  height: 100%;
  border-radius: 5px;
  transition: width .3s ease;
}

.frag-progress-txt {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
}

.frag-ops {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
  font-size: 12px;
}

.frag-ops th {
  text-align: left;
  font-weight: 700;
  color: #97a0aa;
  border-bottom: 1px solid var(--line);
  padding: 4px 8px;
  white-space: nowrap;
}

.frag-ops td {
  padding: 5px 8px;
  border-bottom: 1px solid #f1f3f6;
  color: var(--ink);
  font-weight: 600;
}

.frag-ops .num {
  text-align: right;
}

.frag-ops-id {
  color: #b3bcc6;
  font-weight: 600;
  font-size: 10px;
}

/* Selector de operador del diálogo de finalización: rejilla de tarjetas clicables. */
.fin-op-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.fin-op-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.fin-op-card:hover:not(:disabled) {
  border-color: #bdc4cc;
  background: #fafbfc;
}

.fin-op-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fin-op-card--sel {
  border-color: #d4374c;
  background: rgba(212, 55, 76, 0.06);
}

.fin-op-avatar {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef1f4;
  color: #566472;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fin-op-card--sel .fin-op-avatar {
  background: #d4374c;
  color: #fff;
}

.fin-op-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.fin-op-nombre {
  font-size: 13px;
  font-weight: 600;
  color: #15212e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fin-op-meta {
  font-size: 11px;
  color: #8593a0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<!-- Popover y diálogo de activas (montados fuera del scope) -->
<style>
/* Diálogo de máquinas activas: ancho según su contenido. Vuetify pone width: 100% al diálogo y a
   su hijo (hasta el max-width); aquí se libera, con un mínimo para que las listas vacías no lo
   dejen diminuto y un tope para moldes muy largos. */
.v-dialog.pp-act-dialog {
  width: auto;
  min-width: 340px;
  max-width: min(720px, calc(100vw - 48px));
}

/* Pestañas del diálogo de activas: con cuatro o cinco títulos largos, v-tabs (v-slide-group)
   pasaba a carrusel con flechas porque cada pestaña mide su título en una línea y no se encoge.
   Aquí las pestañas se reparten el ancho a partes iguales y el título baja de renglón si no cabe. */
.v-dialog.pp-act-dialog .v-slide-group__content {
  flex: 1 1 auto;
  min-width: 0;
}

.v-dialog.pp-act-dialog .v-tabs-bar .v-tab {
  flex: 1 1 0;
  font-size: 12px;
  letter-spacing: 0.03em;
  min-width: 0;
  padding: 0 8px;
}

.pp-pop-menu {
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  box-shadow: 0 8px 26px rgba(20, 30, 40, 0.14);
  background: #fff;
}

.pp-pop-menu .pop {
  background: #fff;
  padding: 8px 12px;
  display: grid;
  gap: 2px;
  max-height: 320px;
  overflow: auto;
  min-width: 180px;
}

.pp-pop-menu .pop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
  cursor: pointer;
  color: #283139;
}

.pp-pop-menu .pop-item.pop-all {
  border-bottom: 1px solid #e6e9ed;
  padding-bottom: 6px;
  margin-bottom: 4px;
  font-weight: 600;
}

.pp-pop-menu .pop-item input {
  cursor: pointer;
}
</style>
